import React, { useEffect, useState, useCallback, useRef, act, ChangeEvent } from "react";
import { useNavigate, useLocation } from 'react-router-dom';
import SideNavbar from '../../../components/SideNavbar';
import Navbar from '../../../components/Navbar';
import * as XLSX from "xlsx";
import { useAuth } from '../../../AuthContext';
import TableSkeleton from '../../../components/TableSkeleton';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

// Redux
import {
    setLeadProperty,
    setSearchString,
    clearLeadManagementFilterProps,
    clearLeadManagementPropsSearchString,
    clearLeadManagementAssigneeOptions
} from '../../../redux/leadManagementPropsSlice';
import { setDashboardProps, reset } from '../../../redux/leadManagementDashboardSclice';
import { useAppDispatch, useAppSelector } from '../../../redux/store';

// firebase
import { app, db, functions } from "../../../firebaseConfig";
import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, collection, query, where, getDocs, Timestamp, limit, addDoc, updateDoc, writeBatch, getCountFromServer, DocumentData, documentId, orderBy, onSnapshot } from 'firebase/firestore';
import { getAuth } from "firebase/auth";

// Functions
import { fetchLeads, LeadData, addNewLead, updateLead, exportLeadsToExcel } from '../Admin/Function/LeadFunctions'
import Toast from "../../../components/Toast";
import { set } from "react-datepicker/dist/date_utils";
import ActionModals from "../../../components/modals/ActionModals";
import getCurrentScreen from "../../../Helper/GetScreen";
import GradientSubmitButton from "../../../components/GradientSubmitButton";
import ExportModal from "../../../components/modals/ExportModal";
import CollaborateModal from "../../../components/Actions/modal/CollaborateModal";
import TransferModal from "../../../components/Actions/modal/TransferModal";
// import FollowUpModal from "../../../components/Actions/modal/FollowUpModal";
import FollowUpModal from "../../../components/modals/FollowUpModal";

import AddUpdateLeadModal from "../../../components/Actions/modal/AddUpdateLeadModal";
import { update } from "firebase/database";

// Water Effect Button Component
interface WaterEffectButtonProps {
    children: React.ReactNode;
    onClick: () => void;
    isActive: boolean;
    color: 'blue' | 'green' | 'red';
    disabled?: boolean;
}

interface Ripple {
    id: number;
    x: number;
    y: number;
}

const WaterEffectButton: React.FC<WaterEffectButtonProps> = ({ children, onClick, isActive, color, disabled = false }) => {
    const [ripples, setRipples] = useState<Ripple[]>([]);

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        if (disabled) return;

        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const newRipple: Ripple = {
            id: Date.now(),
            x,
            y,
        };

        setRipples(prev => [...prev, newRipple]);

        // Remove ripple after animation
        setTimeout(() => {
            setRipples(prev => prev.filter(ripple => ripple.id !== newRipple.id));
        }, 600);

        onClick();
    };

    const getButtonStyles = () => {
        let baseStyles = "relative overflow-hidden px-2 py-1 text-xs rounded font-medium transition-all duration-200 ";

        if (isActive) {
            switch (color) {
                case 'blue':
                    return baseStyles + "bg-blue-500 text-white shadow-lg transform scale-105";
                case 'green':
                    return baseStyles + "bg-green-500 text-white shadow-lg transform scale-105";
                case 'red':
                    return baseStyles + "bg-red-500 text-white shadow-lg transform scale-105";
                default:
                    return baseStyles + "bg-gray-500 text-white shadow-lg transform scale-105";
            }
        } else {
            return baseStyles + "bg-gray-100 text-gray-600 hover:bg-gray-200 hover:shadow-md " +
                (disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:scale-102");
        }
    };

    const getRippleColor = () => {
        if (isActive) {
            switch (color) {
                case 'blue':
                    return 'rgba(59, 130, 246, 0.5)';
                case 'green':
                    return 'rgba(34, 197, 94, 0.5)';
                case 'red':
                    return 'rgba(239, 68, 68, 0.5)';
                default:
                    return 'rgba(156, 163, 175, 0.5)';
            }
        }
        return 'rgba(156, 163, 175, 0.3)';
    };

    return (
        <>
            <style>{`
        @keyframes waterRipple {
          0% {
            transform: scale(0);
            opacity: 1;
          }
          50% {
            opacity: 0.8;
          }
          100% {
            transform: scale(4);
            opacity: 0;
          }
        }
      `}</style>
            <button
                onClick={handleClick}
                className={getButtonStyles()}
                disabled={disabled}
            >
                {children}

                {/* Water ripple effects */}
                {ripples.map(ripple => (
                    <span
                        key={ripple.id}
                        className="absolute pointer-events-none"
                        style={{
                            left: ripple.x - 10,
                            top: ripple.y - 10,
                            width: 20,
                            height: 20,
                            borderRadius: '50%',
                            backgroundColor: getRippleColor(),
                            animation: 'waterRipple 0.6s ease-out forwards',
                        }}
                    />
                ))}
            </button>
        </>
    );
};

// Extend User type to include pipelineIds
interface User {
    id: string;
    name: string;
    // ...other properties...
    pipelineIds?: string[]; // Add this line
}

export interface Lead {
    id: string;
    name: string;
    email: string;
    createdAt?: Date;
    followUpDate?: Date;
    number: string;
    alternateNumber1?: string;
    alternateNumber2?: string;
    assignedTo: string;
    adminNotes?: string;
    sourceId?: string;
    stageId?: string;
    stateId?: string;
    statusId?: string;
    distribution: number;
    isActive: boolean;
    isNewEntry?: boolean;
    followUpTime?: string;
    updatedAt?: Date;
    campaign?: string;
    followUp?: string | null;
    hasActiveFollowUp?: boolean;
    activeFollowUpId?: string | null;
    lastRemark?: string | null;
}

interface Student {
    id: string;
    name: string;
    createdAt: Timestamp;
    source: { source: string };
    stage: { color: string; priority: number; stage: string };
    priority: { color: string; priority: number; status: string };
    admissionStatus?: { admissionStatus: string };
}

interface User {
    id: string;
    name: string;
    lastName: string;
    email: string;
    distribution?: number;
}

interface ExcelRow {
    FirstName: string;
    LastName: string;
    Email?: string;
    PhoneNumber: string;
    AlternateNumber1?: string;
    AlternateNumber2?: string;
}

interface LeadUpdateData extends DocumentData {
    name: string;
    number: string;
    alternateNumber1?: string;
    alternateNumber2?: string;
    email: string;
    phoneNumber: string;
    assignedTo: string;
    adminNotes: string;
    sourceId: string;
    stageId: string;
    stateId: string;
    statusId: string;
    updatedAt: Timestamp;
    updatedBy: string;
    createdAt?: Timestamp;
    followUpDate?: Timestamp;
    followUpTime?: string;
}

interface ToastProps {
    message: string;
    isError?: boolean; // Make sure this is optional
    onClose: () => void;
    show: boolean;
}

interface FollowUp {
    id: string;
    followUp: string;
    followUpTime: Timestamp;
    isDone: boolean;
    isEdited: boolean;
    leadId: string;
    studentName: string;
    studentNumber: string;
    userId: string;
    assignedUserName: string;
}

interface LeadAdmissionStatus {
    priority: any;
    level: any;
    admissionStatus: string;
}

interface LeadSource {
    source: string;
}

interface LeadStage {
    stage: string;
    priority: number;
    color: string;
}

interface LeadState {
    state: string;
    makeInactive: boolean;
}

interface LeadStatus {
    status: string;
    priority: number;
    color: string;
}

interface LeadWelcomeCallByAdmissionTeamStatus {
    welcomeCallByAdmissionTeamStatus: string;
}

interface LeadVisaFileProcessingStatus {
    visaFileProcessingStatus: string;
}

interface LeadApplicationMovedStatus {
    applicationMovedStatus: string;
}

interface LeadOfferLetterStatus {
    offerLetterStatus: string;
}

interface LeadFeePaymentStatus {
    feePaymentStatus: string;
}

interface LeadFileSubmissionStatus {
    fileSubmissionStatus: string;
}

interface LeadFollowUpStatus {
    followUpStatus: string;
}

interface LeadCampaign {
    campaign: string;
}

interface PreferredStream {
    stream: any;
    preferredStream: string;
}

interface SuperAgents {
    superAgent: string;
}

interface AdmissionSubStatus {
    subAdmissionStatus: string;
}

interface LeadSubSources {
    subSource: string;
}

interface LeadSubStages {
    subStage: string;
}

interface LeadSubStatuses {
    subStatus: string;
}

interface Destination {
    id: string
    name: string;
}

interface Department {
    department: string;
}

interface leadDateFilter {
    startDate: string;
    endDate: string;
}
interface leadUpdatedFilter {
    startDate: string;
    endDate: string;
}

interface leadFollowUpFilter {
    startDate: string;
    endDate: string;
}

interface SubAgent {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
}

interface DocumentWithId {
    id: string;
    data: any;
}

interface FilterButton {
    sourceIds: string[];
    stageIds: string[];
    stateIds: string[];
    statusIds: string[];
    admissionStatusIds: string[];
    welcomeCallStatusIds: string[];
    visaProcessingStatusIds: string[];
    applicationStatusIds: string[];
    conditionalOfferLetterStatusIds: string[];
    unconditionalOfferLetterStatusIds: string[];
    feePaymentStatusIds: string[];
    fileSubmissionStatusIds: string[];
    preferredStreamIds: string[];
    assigneeIds: string[];
    subAgentIds: string[];
    superAgentIds: string[];
    followUpIds: string[];
    campaignIds: string[];
    destinationIds: string[];
    followUpStatus: string[];
}

interface filterButtons {
    id: string;
    name: string;
    description: string;
    color: string;
    filterButton: FilterButton;
}

interface FilterConfig {
    id: string;
    title: string;
    type: 'text' | 'date' | 'intake-month-year' | 'transferred-date';
    reduxKey1: string;
    reduxKey2?: string | null;
    customColor?: string | null;
    getDisplayTextPath: string; // Path to the display text property (e.g., 'source', 'stage', 'name')
    getColorPath?: string; // Path to the color property (e.g., 'color')
    disabled?: boolean;
    isActive: boolean;
    hasSubFilter?: boolean; // Indicates if this filter has child filters
    parentFilterId?: string; // For sub-filters, reference to parent filter
    subFilterKey?: string; // Redux key for the sub-filter
    order: number; // Display order
    gridSpan?: number; // Grid column span (default: 1)
    specialConfig?: {
        hasAllOption?: boolean; // For assignee options
        hasNoFilterOption?: boolean; // For filters with "No Filter" option
        searchConfig?: {
            minSearchLength?: number;
            searchField?: string;
            firebaseCollection?: string;
        };
        dateConfig?: {
            maxDate?: string; // 'today' or specific date
            quickFilters?: Array<{
                label: string;
                value: string;
                color?: string;
            }>;
        };
    };
}

const LeadManagement = () => {

    const { userData, currentUser, isAdmin, isManager, isStaff, departmentData } = useAuth();


    const currentScreen = getCurrentScreen(useAppSelector(state => state.screen.currentScreen), isAdmin, isManager, isStaff);

    const dispatch = useAppDispatch();
    const location = useLocation();
    const isCollapsed = useAppSelector(state => state.sidebar.isCollapsed);

    // Navigation state from studentDetails return
    const navigationState = location.state;


    const selectedLeadValue = useAppSelector(state => state.leadManagementProps.leadStates);
    const selectedSourceValue = useAppSelector(state => state.leadManagementProps.leadSources);
    const selectedStageValue = useAppSelector(state => state.leadManagementProps.leadStages);
    const selectedStatusValue = useAppSelector(state => state.leadManagementProps.leadStatuses);
    const selectedAdmissionStatusValue = useAppSelector(state => state.leadManagementProps.leadAdmissionStatuses);
    const selectedWelcomeCallByAdmissionTeamStatusValue = useAppSelector(state => state.leadManagementProps.leadWelcomeCallByAdmissionTeamStatuses);
    const selectedVisaProcessingStatusValue = useAppSelector(state => state.leadManagementProps.leadVisaProcessingStatuses);
    const selectedApplicationMovedStatusValue = useAppSelector(state => state.leadManagementProps.leadApplicationMovedStatuses);
    const selectedConditionalOfferLetterStatusValue = useAppSelector(state => state.leadManagementProps.leadConditionalOfferLetterStatuses);
    const selectedUnConditionalOfferLetterStatusValue = useAppSelector(state => state.leadManagementProps.leadUnConditionalOfferLetterStatuses);
    const selectedFeePaymentStatusValue = useAppSelector(state => state.leadManagementProps.leadFeePaymentStatuses);
    const selectedFileSubmissionStatusValue = useAppSelector(state => state.leadManagementProps.leadFileSubmissionStatuses);
    const selectedFollowUpStatusValue = useAppSelector(state => state.leadManagementProps.leadFollowUpStatuses);
    const selectedAssigneeOptionsValue = useAppSelector(state => state.leadManagementProps.leadAssigneeOptions);
    const selectedSubAgentOptionsValue = useAppSelector(state => state.leadManagementProps.leadSubAgentOptions);
    const selectedCampaignOptionsValue = useAppSelector(state => state.leadManagementProps.leadCampaignOptions);
    const selectedPrefferedStreamsValue = useAppSelector(state => state.leadManagementProps.leadPrefferedStreams);

    const selectedSuperAgentsValue = useAppSelector(state => state.leadManagementProps.leadSuperAgents);
    const selectedAdmissionSubStatusValue = useAppSelector(state => state.leadManagementProps.leadAdmissionSubStatus);
    const selectedLeadSubSourcesvalue = useAppSelector(state => state.leadManagementProps.leadSubSources);
    const selectedLeadSubStagesvalue = useAppSelector(state => state.leadManagementProps.leadSubStages);
    const selectedLeadSubStatusesValue = useAppSelector(state => state.leadManagementProps.leadSubStatuses);

    const selectedDestinationsValue = useAppSelector(state => state.leadManagementProps.leadDestinations);
    const selectedDepartmentsValue = useAppSelector(state => state.leadManagementProps.departments);
    const nameSearchString = useAppSelector(state => state.leadManagementProps.nameSearchString);
    const phoneSearchString = useAppSelector(state => state.leadManagementProps.phoneSearchString);
    const createdAtStartDateValue = useAppSelector(state => state.leadManagementProps.createdAtStartDate);
    const createdAtEndDateValue = useAppSelector(state => state.leadManagementProps.createdAtEndDate);
    const transferredDatesValue = useAppSelector(state => state.leadManagementProps.transferredAt);
    const transferredEndDatesValue = useAppSelector(state => state.leadManagementProps.transferredEndAt);
    const updatedAtStartDateValue = useAppSelector(state => state.leadManagementProps.updatedAtStartDate);
    const updatedAtEndDateValue = useAppSelector(state => state.leadManagementProps.updatedAtEndDate);
    const followUpAtStartDateValue = useAppSelector(state => state.leadManagementProps.followUpAtStartDate);
    const followUpAtEndDateValue = useAppSelector(state => state.leadManagementProps.followUpAtEndDate);
    const selectedIntakeYearValue = useAppSelector(state => state.leadManagementProps.leadIntakeYear);
    const selectedIntakeMonthValue = useAppSelector(state => state.leadManagementProps.leadIntakeMonth);
    const leadManagementDashboardProps = useAppSelector(state => state.leadManagementDashboard);

    const customTableProps = {
        "Name": "name",
        "Phone": "phone",
        "Created At": "createdAt",
        "Updated At": "updatedAt",
        "Source": "source",
        "Campaign": "campaign",
        "Assigned To": "assignedTo",
        "Sub Agent": "subAgent",
        "Admission Status": "admissionStatus",
        "Stage": "stage",
        "Status": "priority",
        "Follow Up": "followUp",
        "Last Remark": "lastRemark",
        "Actions": "actions",
        "Destination": "destinations",
        "Intake Year": "intakeYear",
        "Intake Month": "intakeMonth",
    };

    const navigate = useNavigate();

    // Pagination persistence helpers
    const savePaginationState = (page: number, cursorId?: string | null) => {
        sessionStorage.setItem('leadManagementCurrentPage', page.toString());
        if (cursorId) {
            sessionStorage.setItem('leadManagementCursorId', cursorId);
        }
    };

    const clearPaginationState = () => {
        sessionStorage.removeItem('leadManagementCurrentPage');
        sessionStorage.removeItem('leadManagementCursorId');
        // Keep the old 'currentPage' and 'cursorId' for backward compatibility, but clear them too
        sessionStorage.removeItem('currentPage');
        sessionStorage.removeItem('cursorId');
    };

    const restorePaginationStateAndFetch = async (targetPage: number) => {
        if (targetPage <= 1) {
            // If target page is 1 or invalid, just do normal fetch
            fetchPageData(1);
            return;
        }

        try {
            setLoading(true);
            
            // Optimized approach: Build cursor chain incrementally
            // This is more efficient than fetching one by one for high page numbers
            const batchSize = Math.min(10, targetPage); // Process in batches
            let currentCursor: string | undefined = undefined;
            
            // If target page is very high (>10), we can optimize by fetching larger chunks
            if (targetPage > 10) {
                // First, fetch a large batch to get closer to target
                const largePageSize = pageSize * Math.floor(targetPage / 2);
                const assignedTo = await getAssignedToValue();
                const auth = getAuth();

                if (!auth.currentUser) {
                    console.error('User not logged in');
                    return;
                }

                const result = await fetchLeads(db, auth.currentUser, {
                    orderByField: orderByField,
                    sortDirection: sortDirection,
                    pageSize: largePageSize,
                    direction: 'first',
                    cursorId: undefined,
                    isActive: isStaff && currentScreen === "Staff" ? true : undefined,
                    sourceIds: selectedLeadSources,
                    leadSubSources: selectedLeadSubSources,
                    stateIds: selectedLeadStates,
                    stageIds: selectedLeadStages,
                    statusIds: selectedLeadStatuses,
                    admissionStatusIds: selectedAdmissionStatuses,
                    welcomeCallStatusIds: selectedWelcomeCallByAdmissionTeamStatuses,
                    visaProcessStatusIds: selectedVisaProcessingStatuses,
                    applicationStatusIds: selectedApplicationMovedStatuses,
                    conditionalOfferLetterStatusIds: selectedConditionalOfferLetterStatuses,
                    unconditionalOfferLetterStatusIds: selectedUnConditionalOfferLetterStatuses,
                    feePaymentStatusIds: selectedFeePaymentStatuses,
                    fileSubmissionStatusIds: selectedFileSubmissionStatuses,
                    activeFollowUpIds: selectedFollowUpStatuses,
                    assignedTo: assignedTo,
                    subAgent: selectedSubAgentOptions,
                    campaignIds: selectedCampaignOptions,
                    preferredStreamIds: selectedPrefferedStreams,
                    destinationIds: selectedDestinations,
                    searchValue: searchQuery.length > 2 ? searchQuery : '',
                    phoneValue: phoneSearchQuery.length > 2 ? phoneSearchQuery : '',
                    startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
                    endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
                    tranffertAtStartDate: selectedTransferredDates.length > 0 ? convertToFirebaseTimestamp(selectedTransferredDates[0], true) : null,
                    tranffertAtEndDate: selectedTransferredEndDates.length > 0 ? convertToFirebaseTimestamp(selectedTransferredEndDates[0], false) : null,
                    updatedAtStartDate: selectedUpdatedAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtStartDate[0], true) : null,
                    updatedAtEndDate: selectedUpdatedAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtEndDate[0], false) : null,
                    followUpAtStartDate: selectedFollowUpAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtStartDate[0], true) : null,
                    followUpAtEndDate: selectedFollowUpAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtEndDate[0], false) : null,
                    intakeYear: selectedIntakeYear,
                    intakeMonth: selectedIntakeMonth
                });

                const response = result.data;
                if (response?.leads && response.leads.length > 0) {
                    // Calculate approximate position for target page
                    const targetIndex = (targetPage - 1) * pageSize;
                    const leads = response.leads;
                    
                    if (targetIndex < leads.length) {
                        // We can extract the target page directly from this large result
                        const startIdx = targetIndex;
                        const endIdx = Math.min(startIdx + pageSize, leads.length);
                        const targetPageData = leads.slice(startIdx, endIdx);
                        
                        setLeadsData(targetPageData);
                        setHasMore(endIdx < leads.length || response?.hasMore || false);
                        // Map LeadData to the expected lastDocument format
                        const lastLead = targetPageData[targetPageData.length - 1];
                        if (lastLead) {
                            setLastDocument({
                                id: lastLead.id,
                                leadDate: lastLead.createdAt || Timestamp.now() // Use createdAt as leadDate, fallback to current timestamp
                            });
                        } else {
                            setLastDocument(null);
                        }
                        setTotalLeadsCount(response?.count || 0);
                        setCurrentPage(targetPage);
                        
                        // Save the pagination state
                        savePaginationState(targetPage, lastLead?.id || null);
                        return;
                    } else {
                        // Set cursor to continue from where this batch ended
                        currentCursor = response?.lastDocument?.id;
                    }
                }
            }
            
            // If the optimized approach didn't work or target page is small, fall back to sequential approach
            const startPage = currentCursor ? Math.floor(targetPage / 2) + 1 : 1;
            
            for (let page = startPage; page <= targetPage; page++) {
                const assignedTo = await getAssignedToValue();
                const auth = getAuth();

                if (!auth.currentUser) {
                    console.error('User not logged in');
                    return;
                }

                const result = await fetchLeads(db, auth.currentUser, {
                    orderByField: orderByField,
                    sortDirection: sortDirection,
                    pageSize: pageSize,
                    direction: page === 1 ? 'first' : 'next',
                    cursorId: currentCursor,
                    isActive: isStaff && currentScreen === "Staff" ? true : undefined,
                    sourceIds: selectedLeadSources,
                    leadSubSources: selectedLeadSubSources,
                    stateIds: selectedLeadStates,
                    stageIds: selectedLeadStages,
                    statusIds: selectedLeadStatuses,
                    admissionStatusIds: selectedAdmissionStatuses,
                    welcomeCallStatusIds: selectedWelcomeCallByAdmissionTeamStatuses,
                    visaProcessStatusIds: selectedVisaProcessingStatuses,
                    applicationStatusIds: selectedApplicationMovedStatuses,
                    conditionalOfferLetterStatusIds: selectedConditionalOfferLetterStatuses,
                    unconditionalOfferLetterStatusIds: selectedUnConditionalOfferLetterStatuses,
                    feePaymentStatusIds: selectedFeePaymentStatuses,
                    fileSubmissionStatusIds: selectedFileSubmissionStatuses,
                    activeFollowUpIds: selectedFollowUpStatuses,
                    assignedTo: assignedTo,
                    subAgent: selectedSubAgentOptions,
                    campaignIds: selectedCampaignOptions,
                    preferredStreamIds: selectedPrefferedStreams,
                    destinationIds: selectedDestinations,
                    searchValue: searchQuery.length > 2 ? searchQuery : '',
                    phoneValue: phoneSearchQuery.length > 2 ? phoneSearchQuery : '',
                    startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
                    endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
                    tranffertAtStartDate: selectedTransferredDates.length > 0 ? convertToFirebaseTimestamp(selectedTransferredDates[0], true) : null,
                    tranffertAtEndDate: selectedTransferredEndDates.length > 0 ? convertToFirebaseTimestamp(selectedTransferredEndDates[0], false) : null,
                    updatedAtStartDate: selectedUpdatedAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtStartDate[0], true) : null,
                    updatedAtEndDate: selectedUpdatedAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtEndDate[0], false) : null,
                    followUpAtStartDate: selectedFollowUpAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtStartDate[0], true) : null,
                    followUpAtEndDate: selectedFollowUpAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtEndDate[0], false) : null,
                    intakeYear: selectedIntakeYear,
                    intakeMonth: selectedIntakeMonth
                });

                const response = result.data;

                if (page === targetPage) {
                    // This is our target page, set the data
                    setLeadsData(response?.leads || []);
                    setHasMore(response?.hasMore || false);
                    setLastDocument(response?.lastDocument || null);
                    setTotalLeadsCount(response?.count || 0);
                    setCurrentPage(targetPage);

                    // Save the pagination state
                    savePaginationState(targetPage, response?.lastDocument?.id || null);
                    break;
                } else {
                    // This is an intermediate page, just get the cursor for the next page
                    currentCursor = response?.lastDocument?.id;
                    if (!currentCursor) {
                        // If we can't get a cursor, we've reached the end of data
                        // Fall back to normal fetch for page 1
                        fetchPageData(1);
                        return;
                    }
                }
            }
        } catch (error) {
            console.error('Error restoring pagination state:', error);
            // Fall back to normal fetch for page 1 on error
            fetchPageData(1);
        } finally {
            setLoading(false);
        }
    };

    // collapse state for filter section
    const [isCollapsedFilter, setIsCollapsedFilter] = useState(false);

    // useEffect execution order control
    const [firstEffectDone, setFirstEffectDone] = useState(false);

    const [isPopupOpen, setPopupOpen] = useState(false);
    // Using pageSize for all pagination calculations
    const openPopup = () => {
        setEditingId(null);
        // Clear phone validation state
        setPhoneValidationMessage('');
        setIsPhoneValidating(false);
        setPhoneValidationType('');
        // For staff users (non-admin) in Staff screen, pre-fill assignedTo with current user ID
        if (isStaff && !isAdmin && currentScreen === "Staff" && currentUser) {
            setNewLead({ assignedTo: currentUser.uid });
        } else {
            setNewLead({});
        }
        setPopupOpen(true);
    };

    const closePopup = () => {
        // Clear phone validation state when closing
        setPhoneValidationMessage('');
        setIsPhoneValidating(false);
        setPhoneValidationType('');
        setPopupOpen(false);
    };

    const [customizeOpen, setCustomizeOpen] = useState(false);

    const [newLead, setNewLead] = useState<Partial<LeadData>>({});
    // inputRef moved to AddUpdateLeadModal

    // Convert function moved to AddUpdateLeadModal

    const [editingId, setEditingId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [companyId, setCompanyId] = useState<string | null>(null);
    const [stages, setStages] = useState<{ id: string; stage: string }[]>([]);
    const [states, setStates] = useState<{ id: string; state: string }[]>([]);
    const [statuses, setStatuses] = useState<{ id: string; status: string }[]>([]);
    const [assignedToOptions, setAssignedToOptions] = useState<User[]>([]);
    const [pageSize, setPageSize] = useState<number>(() => {
        const storedPageSize = sessionStorage.getItem('pageSize');
        return storedPageSize ? parseInt(storedPageSize, 10) : 10;
    });
    const leadsPerPage = pageSize;

    // Custom styles for webkit scrollbars when pageSize > 10
    React.useEffect(() => {
        const styleId = 'lead-table-scrollbar-styles';
        let style = document.getElementById(styleId);
        
        if (pageSize > 10) {
            if (!style) {
                style = document.createElement('style');
                style.id = styleId;
                document.head.appendChild(style);
            }
            style.textContent = `
                .lead-table-container {
                    height: 600px !important;
                    min-height: 600px !important;
                }
                .lead-table-container::-webkit-scrollbar {
                    width: 8px;
                    height: 8px;
                }
                .lead-table-container::-webkit-scrollbar-track {
                    background: #f1f5f9;
                    border-radius: 4px;
                }
                .lead-table-container::-webkit-scrollbar-thumb {
                    background: #3483BE;
                    border-radius: 4px;
                }
                .lead-table-container::-webkit-scrollbar-thumb:hover {
                    background: #2563eb;
                }
                .lead-table-container::-webkit-scrollbar-corner {
                    background: #f1f5f9;
                }
            `;
        } else if (style) {
            document.head.removeChild(style);
        }
        
        return () => {
            const cleanupStyle = document.getElementById(styleId);
            if (cleanupStyle) {
                document.head.removeChild(cleanupStyle);
            }
        };
    }, [pageSize]);
    const auth = getAuth(app);
    const [totalLeadsCount, setTotalLeadsCount] = useState<number>(0);
    const [newLeadsCount, setNewLeadsCount] = useState<number>(0);
    const [isLoadingNewCount, setIsLoadingNewCount] = useState<boolean>(false);
    const totalPages = Math.ceil(totalLeadsCount / leadsPerPage);
    const [sources, setSources] = useState<{ id: string; source: string }[]>([]);
    const [subSources, setSubSources] = useState<{ id: string; subSource: string, sourceId: string }[]>([]);
    const [campaigns, setCampaigns] = useState<{ id: string; campaign: string }[]>([]);
    const [pipelines, setPipelines] = useState<{ id: string; pipelineName: any }[]>([]);
    const [currentUserPipelines, setCurrentUserPipelines] = useState<string[]>([]);
    const [password, setPassword] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState("");
    const [isError, setIsError] = useState(false);
    const [callingLeads, setCallingLeads] = useState<{ [key: string]: boolean }>({});

    // Filter states
    const [leadStates, setLeadStates] = useState<DocumentWithId[]>([]);
    const [leadStatuses, setLeadStatuses] = useState<DocumentWithId[]>([]);
    const [leadStages, setLeadStages] = useState<DocumentWithId[]>([]);
    const [leadSources, setLeadSources] = useState<DocumentWithId[]>([]);
    const [admissionStatuses, setAdmissionStatuses] = useState<DocumentWithId[]>([]);
    const [welcomeCallByAdmissionTeamStatuses, setWelcomeCallByAdmissionTeamStatuses] = useState<DocumentWithId[]>([]);
    const [visaProcessingStatuses, setVisaProcessingStatuses] = useState<DocumentWithId[]>([]);
    const [applicationMovedStatuses, setApplicationMovedStatuses] = useState<DocumentWithId[]>([]);
    const [conditionalofferLetterStatuses, setConditionalOfferLetterStatuses] = useState<DocumentWithId[]>([]);
    const [unConditionalofferLetterStatuses, setUnConditionalOfferLetterStatuses] = useState<DocumentWithId[]>([]);
    const [feePaymentStatuses, setFeePaymentStatuses] = useState<DocumentWithId[]>([]);
    const [fileSubmissionStatuses, setFileSubmissionStatuses] = useState<DocumentWithId[]>([]);
    const followUpStatuses: DocumentWithId[] = [
        { id: 'false', data: { followUpStatus: 'False' } },
        { id: 'true', data: { followUpStatus: 'True' } }
    ];
    const [assigneeOptions, setAssigneeOptions] = useState<DocumentWithId[]>([]);
    const [subAgentOptions, setSubAgentOptions] = useState<DocumentWithId[]>([]);
    const [campaignOptions, setCampaignOptions] = useState<DocumentWithId[]>([]);
    const [managerTeamOptions, setManagerTeamOptions] = useState<string[]>([]);
    const [prefferedStreams, setPrefferedStreams] = useState<DocumentWithId[]>([]);
    const [destinations, setDestinations] = useState<DocumentWithId[]>([]);
    const [departments, setDepartments] = useState<DocumentWithId[]>([]);
    const [superAgents, setSuperAgents] = useState<DocumentWithId[]>([]);
    const [admissionSubStatus, setAdmissionSubStatus] = useState<DocumentWithId[]>([])
    const [leadSubSources, setLeadSubSources] = useState<DocumentWithId[]>([])
    const [leadSubStages, setLeadSubStages] = useState<DocumentWithId[]>([])
    const [leadSubStatuses, setLeadSubStatuses] = useState<DocumentWithId[]>([])

    // states for actions
    const [selectedStudent, setSelectedStudent] = useState<LeadData | null>(null);
    const [selectedStudentFollowUp, setSelectedStudentFollowUp] = useState<{ id: string; name: string; phone: string }>({ id: '', name: '', phone: '' });
    const [selectedStudentPhone, setSelectedStudentPhone] = useState<string>('');
    const [selectedFollowUp, setSelectedFollowUp] = useState<FollowUp | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState<{ id: string, name: string }[]>([]);
    const [currentAssignedUser, setCurrentAssignedUser] = useState<{ id: string, name: string } | null>(null);
    const [students, setStudents] = useState<Student[]>([]);
    const [companyCloudTelephony, setCompanyCloudTelephony] = useState<boolean>(false);

    // loading states
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);

    // modals
    const [showCollaborateModal, setShowCollaborateModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);
    const [showFollowUpModal, setShowFollowUpModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    // Deactivate confirmation modal states
    const [showDeactivateModal, setShowDeactivateModal] = useState(false);
    const [leadToDeactivate, setLeadToDeactivate] = useState<{ id: string, isActive: boolean, name: string } | null>(null);
    const [isDeactivating, setIsDeactivating] = useState(false);

    // selected filters
    const [selectedLeadStates, setSelectedLeadStates] = useState<string[]>([]);
    const [selectedLeadStatuses, setSelectedLeadStatuses] = useState<string[]>([]);
    const [selectedLeadStages, setSelectedLeadStages] = useState<string[]>([]);
    const [selectedLeadSources, setSelectedLeadSources] = useState<string[]>([]);
    const [selectedAdmissionStatuses, setSelectedAdmissionStatuses] = useState<string[]>([]);
    const [selectedWelcomeCallByAdmissionTeamStatuses, setSelectedWelcomeCallByAdmissionTeamStatuses] = useState<string[]>([]);
    const [selectedVisaProcessingStatuses, setSelectedVisaProcessingStatuses] = useState<string[]>([]);
    const [selectedApplicationMovedStatuses, setSelectedApplicationMovedStatuses] = useState<string[]>([]);
    const [selectedConditionalOfferLetterStatuses, setSelectedConditionalOfferLetterStatuses] = useState<string[]>([]);
    const [selectedUnConditionalOfferLetterStatuses, setSelectedUnConditionalOfferLetterStatuses] = useState<string[]>([]);
    const [selectedFeePaymentStatuses, setSelectedFeePaymentStatuses] = useState<string[]>([]);
    const [selectedFileSubmissionStatuses, setSelectedFileSubmissionStatuses] = useState<string[]>([]);
    const [selectedFollowUpStatuses, setSelectedFollowUpStatuses] = useState<string[]>([]);
    const [selectedAssigneeOptions, setSelectedAssigneeOptions] = useState<string[]>([]);
    const [selectedSubAgentOptions, setSelectedSubAgentOptions] = useState<string[]>([]);
    const [selectedCampaignOptions, setSelectedCampaignOptions] = useState<string[]>([]);
    const [selectedPrefferedStreams, setSelectedPrefferedStreams] = useState<string[]>([]);
    const [selectedSuperAgents, setSelectedSuperAgents] = useState<string[]>([])
    const [selectedAdmissionSubStatus, setSelectedAdmissionSubStatus] = useState<string[]>([])
    const [selectedLeadSubSources, setSelectedLeadSubSources] = useState<string[]>([])
    const [selectedLeadSubStages, setSelectedLeadSubStages] = useState<string[]>([])
    const [selectedLeadSubStatuses, setSelectedLeadSubStatuses] = useState<string[]>([])
    const [selectedDestinations, setSelectedDestinations] = useState<string[]>([]);
    const [selectedDepartments, setSelectedDepartments] = useState<string[]>([]);
    const [selectedStartDate, setSelectedStartDate] = useState<string[]>([]);
    const [selectedEndDate, setSelectedEndDate] = useState<string[]>([]);

    const [selectedTransferredDates, setSelectedTransferredDates] = useState<string[]>([]);
    const [selectedTransferredEndDates, setSelectedTransferredEndDates] = useState<string[]>([]);

    const [selectedUpdatedAtStartDate, setSelectedUpdatedAtStartDate] = useState<string[]>([]);
    const [selectedUpdatedAtEndDate, setSelectedUpdatedAtEndDate] = useState<string[]>([]);

    const [selectedFollowUpAtStartDate, setSelectedFollowUpAtStartDate] = useState<string[]>([]);
    const [selectedFollowUpAtEndDate, setSelectedFollowUpAtEndDate] = useState<string[]>([]);

    const [searchQuery, setSearchQuery] = useState<string>('');
    // const [phoneSearchQuery, setPhoneSearchQuery] = useState<string>('');

    const [selectedIntakeYear, setSelectedIntakeYear] = useState<string[]>([])
    const [selectedIntakeMonth, setSelectedIntakeMonth] = useState<string[]>([])


    // filter buttons
    const [filterButtons, setFilterButtons] = useState<filterButtons[]>([]);

    // Filter combination warning
    const [showFilterWarning, setShowFilterWarning] = useState(false);
    const [filterCombinationCount, setFilterCombinationCount] = useState(0);

    // states for actions and operations
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [selectedLeadsForAction, setSelectedLeadsForAction] = useState<string[]>([]);
    const [currentViewingFollowUpId, setCurrentViewingFollowUpId] = useState<string | null>(null);
    const [followUpDetails, setFollowUpDetails] = useState<{ [key: string]: any }>({});
    const [selectCurrentPage, setSelectCurrentPage] = useState(false);
    const MAX_SELECTABLE_LEADS = 2000;
    const [selectedAction, setSelectedAction] = useState<number | null>(null);
    const [actionDropdownOpen, setActionDropdownOpen] = useState(false);
    const [actionSearchQuery, setActionSearchQuery] = useState('');
    const [highlightedActionIndex, setHighlightedActionIndex] = useState(-1);

    const [isSubmitting, setIsSubmitting] = useState(false);

    // submitLoading moved to AddUpdateLeadModal

    // error

    const [phoneSearchQuery, setPhoneSearchQuery] = useState("+91");

    // export to the excel data
    const [downloadLoading, setDownloadLoading] = useState(false);

    // Real-time phone number validation states
    const [phoneValidationMessage, setPhoneValidationMessage] = useState<string>("");
    const [isPhoneValidating, setIsPhoneValidating] = useState<boolean>(false);
    const [phoneValidationType, setPhoneValidationType] = useState<'error' | 'warning' | 'success' | ''>("");


    // filter state
    const [filterConfigs, setFilterConfigs] = useState<{
        find: any;
        filter(arg0: (config: { isActive: any; }) => any): unknown;
        FilterConfig: any;
    }>({
        find: null,
        filter: () => [],
        FilterConfig: []
    });
    const [selectedFilters, setSelectedFilters] = useState<FilterConfig[]>([])

    // orderBy
    const [orderByField, setOrderByField] = useState<'createdAt' | 'updatedAt' | 'transferredAt'>(() => {
        const storedOrderBy = sessionStorage.getItem('orderByField');
        return storedOrderBy as 'createdAt' | 'updatedAt' | 'transferredAt' || 'createdAt';
    });

    // Sort direction for the Updated At column
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>(() => {
        const storedSortDirection = sessionStorage.getItem('sortDirection');
        return storedSortDirection as 'asc' | 'desc' || 'desc';
    });

    // FollowUp Date Quick Filter buttons state
    const [selectedFollowUpQuickFilter, setSelectedFollowUpQuickFilter] = useState<'upcoming' | 'today' | 'overdue' | null>(null);

    // State for lead name color scheme with localStorage persistence
    const [leadColorScheme, setLeadColorScheme] = useState<'stage' | 'status' | 'state' | null>(() => {
        const stored = localStorage.getItem('leadColorScheme');
        return stored && ['stage', 'status', 'state'].includes(stored) ? stored as 'stage' | 'status' | 'state' : null;
    });

    // set all modals close and there respective states
    const setAllModalsClose = () => {
        setShowCollaborateModal(false);
        setShowTransferModal(false);
        setShowFollowUpModal(false);
        setShowDeactivateModal(false);
        setSelectedStudent(null);
        setSelectedFollowUp(null);
        setSelectedAction(null);
        setActionDropdownOpen(false);
        setActionSearchQuery('');
        setSelectedStudentPhone('');
        setSelectedLeadsForAction([]);
        setSearchTerm('');
        setSearchResults([]);
        setLeadToDeactivate(null);
    }

    // Effect to close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setActionDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Effect to handle keyboard navigation
    useEffect(() => {
        if (!actionDropdownOpen) return;

        const handleKeyDown = (event: KeyboardEvent) => {
            // Using a local function to filter actions to avoid referencing Actions before declaration
            const filteredOptions = Actions.filter(item =>
                item.name.toLowerCase().includes(actionSearchQuery.toLowerCase()) && item.active
            );

            if (filteredOptions.length === 0) return;

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    event.stopPropagation();
                    setHighlightedActionIndex(prev => {
                        const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
                        // Scroll the highlighted item into view if needed
                        setTimeout(() => {
                            const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                            highlightedElement?.scrollIntoView({ block: 'nearest' });
                        }, 0);
                        return nextIndex;
                    });
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    event.stopPropagation();
                    setHighlightedActionIndex(prev => {
                        const nextIndex = prev > 0 ? prev - 1 : 0;
                        // Scroll the highlighted item into view if needed
                        setTimeout(() => {
                            const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                            highlightedElement?.scrollIntoView({ block: 'nearest' });
                        }, 0);
                        return nextIndex;
                    });
                    break;
                case 'Enter':
                    event.preventDefault();
                    event.stopPropagation();
                    if (highlightedActionIndex >= 0 && highlightedActionIndex < filteredOptions.length) {
                        handleAction(filteredOptions[highlightedActionIndex].id);
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    event.stopPropagation();
                    setActionDropdownOpen(false);
                    break;
                default:
                    break;
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [actionDropdownOpen, actionSearchQuery, highlightedActionIndex]);

    // Temporary states for actions
    const [tempAssignee, setTempAssignee] = useState<string[]>([]);

    const handleClear = (e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedAction(null);
        setActionSearchQuery('');
    };

    // Reset highlighted index when search query changes
    useEffect(() => {
        setHighlightedActionIndex(actionSearchQuery ? 0 : -1);
    }, [actionSearchQuery]);

    useEffect(() => {
        setCurrentPage(1);
        sessionStorage.setItem('currentPage', '1');
    }, [orderByField, sortDirection]);

    // Reset search query when dropdown closes
    useEffect(() => {
        if (!actionDropdownOpen) {
            setActionSearchQuery('');
            setHighlightedActionIndex(-1);
        }
    }, [actionDropdownOpen]);

    // Save leadColorScheme to localStorage when it changes
    useEffect(() => {
        if (leadColorScheme) {
            localStorage.setItem('leadColorScheme', leadColorScheme);
        } else {
            localStorage.removeItem('leadColorScheme');
        }
    }, [leadColorScheme]);

    // Function to fetch follow-up details when user clicks 'Yes'
    const handleFetchFollowUp = async (leadId: string, followUpId: string | null | undefined) => {
        // If we're already viewing the follow-up details for this lead, hide them
        if (currentViewingFollowUpId === leadId) {
            setCurrentViewingFollowUpId(null);
            return;
        }

        // If there's no follow-up ID, nothing to fetch
        if (!followUpId) {
            return;
        }

        try {
            // If we've already fetched this follow-up, just show it
            if (followUpDetails[leadId]) {
                setCurrentViewingFollowUpId(leadId);
                return;
            }

            const followUpsQuery = query(
                collection(db, `companies/${companyId}/followUps`),
                where(documentId(), '==', followUpId)
            );

            const followUpsSnapshot = await getDocs(followUpsQuery);
            const followUp = followUpsSnapshot.docs[0]?.data();

            if (followUp) {
                setFollowUpDetails(prev => ({
                    ...prev,
                    [leadId]: followUp
                }));
                setCurrentViewingFollowUpId(leadId);
            }
        } catch (error) {
            console.error(`Error fetching follow-up details for lead ${leadId}:`, error);
            // Show error toast
            setToastMessage(`Error fetching follow-up details: ${(error as Error).message}`);
            setIsError(true);
            setShowToast(true);
            setTimeout(() => {
                setShowToast(false);
                setIsError(false);
            }, 3000);
        }
    };

    const handleActionSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setActionSearchQuery(e.target.value);
        setHighlightedActionIndex(e.target.value ? 0 : -1);
    };

    const handleActionDropdownKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !actionDropdownOpen) {
            e.preventDefault();
            setActionDropdownOpen(true);
        }
    };

    const handleActionSearchInputKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();

        // Handle arrow key navigation directly in the search input
        const filteredOptions = getFilteredActions().filter(item => item.active);

        if (filteredOptions.length === 0) return;

        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setHighlightedActionIndex(prev => {
                const nextIndex = prev < filteredOptions.length - 1 ? prev + 1 : prev;
                // Scroll the highlighted item into view if needed
                setTimeout(() => {
                    const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                    highlightedElement?.scrollIntoView({ block: 'nearest' });
                }, 0);
                return nextIndex;
            });
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setHighlightedActionIndex(prev => {
                const nextIndex = prev > 0 ? prev - 1 : 0;
                // Scroll the highlighted item into view if needed
                setTimeout(() => {
                    const highlightedElement = document.getElementById(`action-option-${nextIndex}`);
                    highlightedElement?.scrollIntoView({ block: 'nearest' });
                }, 0);
                return nextIndex;
            });
        } else if (e.key === 'Enter') {
            e.preventDefault();
            if (highlightedActionIndex >= 0 && highlightedActionIndex < filteredOptions.length) {
                handleAction(filteredOptions[highlightedActionIndex].id);
            }
        } else if (e.key === 'Escape') {
            e.preventDefault();
            setActionDropdownOpen(false);
        }
    };

    const clearAllFilters = (e: React.MouseEvent) => {
        e.stopPropagation();
        dispatch(clearLeadManagementFilterProps());
        
        // For staff users, immediately set their own ID back as assignee filter
        // since they should only see their own leads
        if (isStaff && currentScreen === 'Staff' && currentUser?.uid) {
            setTimeout(() => {
                setSelectedAssigneeOptions([currentUser.uid]);
                dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: [currentUser.uid] }));
            }, 0);
        }
        
        setFilterButtonActive(false);
        setFilterButtonKey('');
        setIsCollapsedFilter(false);
        clearPaginationState(); // Reset pagination state in session storage
        savePaginationState(1, null); // Set page to 1
    };

    const clearSearch = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (tempAssignee?.length > 0) {
            dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: tempAssignee }));
        }
        dispatch(clearLeadManagementPropsSearchString());
        dispatch(clearLeadManagementAssigneeOptions())
    };

    // FollowUp Date Quick Filter handlers
    const handleFollowUpQuickFilter = (filterType: 'upcoming' | 'today' | 'overdue') => {
        const today = new Date();
        const todayStr = today.toISOString().split('T')[0]; // Format as YYYY-MM-DD

        let startDate: string;
        let endDate: string;

        switch (filterType) {
            case 'today':
                startDate = todayStr;
                endDate = todayStr;
                break;
            case 'upcoming':
                // Tomorrow onwards
                const tomorrow = new Date(today);
                tomorrow.setDate(tomorrow.getDate() + 1);
                startDate = tomorrow.toISOString().split('T')[0];
                // Set end date to 5 years from now as a reasonable upper limit
                const nextFiveYears = new Date(today);
                nextFiveYears.setFullYear(nextFiveYears.getFullYear() + 5);
                endDate = nextFiveYears.toISOString().split('T')[0];
                break;
            case 'overdue':
                // From a past date up to yesterday
                const pastFiveYears = new Date(today);
                pastFiveYears.setFullYear(pastFiveYears.getFullYear() - 5);
                startDate = pastFiveYears.toISOString().split('T')[0];
                const yesterday = new Date(today);
                yesterday.setDate(yesterday.getDate() - 1);
                endDate = yesterday.toISOString().split('T')[0];
                break;
            default:
                return;
        }

        // Set the selected quick filter
        setSelectedFollowUpQuickFilter(filterType);

        // Dispatch the date range to Redux
        dispatch(setLeadProperty({ key: 'followUpAtStartDate', value: [startDate] }));
        dispatch(setLeadProperty({ key: 'followUpAtEndDate', value: [endDate] }));
    };

    const clearFollowUpQuickFilter = () => {
        setSelectedFollowUpQuickFilter(null);
        dispatch(setLeadProperty({ key: 'followUpAtStartDate', value: [] }));
        dispatch(setLeadProperty({ key: 'followUpAtEndDate', value: [] }));
    };

    // Handler for Updated At column sorting
    const handleUpdatedAtSort = () => {
        const newDirection = orderByField === 'updatedAt' && sortDirection === 'desc' ? 'asc' : 'desc';
        setSortDirection(newDirection);
        setOrderByField('updatedAt');
        setCurrentPage(1);

        // Store in session storage
        sessionStorage.setItem('orderByField', 'updatedAt');
        sessionStorage.setItem('sortDirection', newDirection);
        sessionStorage.setItem('currentPage', '1');
    };

    // Function to render sort icons for Updated At column
    const renderSortIcon = () => {
        if (orderByField !== 'updatedAt') {
            return null; // Don't show any icon when orderBy is not updatedAt
        }

        return (
            <span className="inline-flex ml-2">
                {sortDirection === 'asc' ? (
                    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                ) : (
                    <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                )}
            </span>
        );
    };

    useEffect(() => {
        if (searchQuery === '' && phoneSearchQuery === '') {
            handleSearch();
        } else {
            setIsCollapsedFilter(false);
        }
    }, [searchQuery, phoneSearchQuery]);

    // Function to get lead name color based on selected color scheme
    const getLeadNameColor = (lead: any) => {
        if (!leadColorScheme) return null;

        switch (leadColorScheme) {
            case 'stage':
                if (lead.stageId) {
                    const stage = leadStages.find(s => s.id === lead.stageId);
                    return stage?.data?.color;
                }
                break;
            case 'status':
                if (lead.statusId) {
                    const status = leadStatuses.find(s => s.id === lead.statusId);
                    return status?.data?.color;
                }
                break;
            case 'state':
                if (lead.stateId) {
                    const state = leadStates.find(s => s.id === lead.stateId);
                    return state?.data?.color;
                }
                break;
            default:
                return null;
        }
        return null;
    };

    const handlePageSizeChange = (newPageSize: number) => {
        setPageSize(newPageSize);
        sessionStorage.setItem('pageSize', newPageSize.toString());

        // Clear existing data to prevent confusion when showing a new page size
        setLeadsData([]);
        // Reset pagination state
        setLastDocument(null);
        setHasMore(true);
        setCurrentPage(1);

        // Clear pagination state when page size changes
        clearPaginationState();
        savePaginationState(1, null);
    };

    // useEffect to set the redux state for the selected items
    useEffect(() => {
        setSelectedLeadSources(selectedSourceValue);
        setSelectedLeadStages(selectedStageValue);
        setSelectedLeadStates(selectedLeadValue);
        setSelectedLeadStatuses(selectedStatusValue);
        setSelectedAdmissionStatuses(selectedAdmissionStatusValue);
        setSelectedWelcomeCallByAdmissionTeamStatuses(selectedWelcomeCallByAdmissionTeamStatusValue);
        setSelectedVisaProcessingStatuses(selectedVisaProcessingStatusValue);
        setSelectedApplicationMovedStatuses(selectedApplicationMovedStatusValue);
        setSelectedConditionalOfferLetterStatuses(selectedConditionalOfferLetterStatusValue);
        setSelectedUnConditionalOfferLetterStatuses(selectedUnConditionalOfferLetterStatusValue);
        setSelectedFeePaymentStatuses(selectedFeePaymentStatusValue);
        setSelectedFileSubmissionStatuses(selectedFileSubmissionStatusValue);
        setSelectedFollowUpStatuses(selectedFollowUpStatusValue);
        setSelectedAssigneeOptions(selectedAssigneeOptionsValue);
        setSelectedSubAgentOptions(selectedSubAgentOptionsValue);
        setSelectedCampaignOptions(selectedCampaignOptionsValue);
        setSelectedPrefferedStreams(selectedPrefferedStreamsValue);
        setSelectedSuperAgents(selectedSuperAgentsValue);
        setSelectedAdmissionSubStatus(selectedAdmissionSubStatusValue)
        setSelectedLeadSubSources(selectedLeadSubSourcesvalue)
        setSelectedLeadSubStages(selectedLeadSubStagesvalue)
        setSelectedLeadSubStatuses(selectedLeadSubStatusesValue)
        setSelectedDestinations(selectedDestinationsValue);
        setSelectedDepartments(selectedDepartmentsValue);
        setSelectedStartDate(createdAtStartDateValue);
        setSelectedEndDate(createdAtEndDateValue);
        setSelectedTransferredDates(transferredDatesValue);
        setSelectedTransferredEndDates(transferredEndDatesValue);
        setSelectedUpdatedAtStartDate(updatedAtStartDateValue);
        setSelectedUpdatedAtEndDate(updatedAtEndDateValue);
        setSelectedFollowUpAtStartDate(followUpAtStartDateValue);
        setSelectedFollowUpAtEndDate(followUpAtEndDateValue);
        setSelectedIntakeYear(selectedIntakeYearValue);
        setSelectedIntakeMonth(selectedIntakeMonthValue);
        console.log(">>>>>>>>>>>>", transferredDatesValue, transferredEndDatesValue);


    }, [firstEffectDone, selectedSourceValue, selectedStageValue, selectedLeadValue,
        selectedStatusValue, selectedAdmissionStatusValue, selectedWelcomeCallByAdmissionTeamStatusValue,
        selectedVisaProcessingStatusValue, selectedApplicationMovedStatusValue, selectedConditionalOfferLetterStatusValue,
        selectedUnConditionalOfferLetterStatusValue, selectedFeePaymentStatusValue, selectedFileSubmissionStatusValue,
        selectedFollowUpStatusValue, selectedAssigneeOptionsValue, selectedSubAgentOptionsValue, selectedCampaignOptionsValue,
        selectedPrefferedStreamsValue, selectedSuperAgentsValue, selectedAdmissionSubStatusValue, selectedLeadSubSourcesvalue,
        selectedLeadSubStagesvalue, selectedLeadSubStatusesValue, selectedDestinationsValue, createdAtStartDateValue, createdAtEndDateValue,
        transferredDatesValue, transferredEndDatesValue, updatedAtStartDateValue, updatedAtEndDateValue, followUpAtStartDateValue, followUpAtEndDateValue,
        selectedIntakeMonthValue, selectedIntakeYearValue, selectedDepartmentsValue]);

    useEffect(() => {
        setSearchQuery(nameSearchString[0] || '');
        setPhoneSearchQuery(phoneSearchString[0] || '');
    }, [nameSearchString, phoneSearchString]);

    // Reset quick filter state when follow-up dates are cleared manually
    useEffect(() => {
        if (selectedFollowUpAtStartDate.length === 0 && selectedFollowUpAtEndDate.length === 0) {
            setSelectedFollowUpQuickFilter(null);
        }
    }, [selectedFollowUpAtStartDate, selectedFollowUpAtEndDate]);

    // Restore state when returning from studentDetails
    useEffect(() => {
        if (navigationState?.returnedFromStudentDetails && navigationState?.shouldRestoreScroll) {
            const state = navigationState;

            // Restore page size
            if (state.pageSize) {
                setPageSize(state.pageSize);
            }

            // Restore all filter states if they exist
            if (state.filters) {
                const { filters } = state;

                if (filters.selectedLeadStates) setSelectedLeadStates(filters.selectedLeadStates);
                if (filters.selectedLeadSources) setSelectedLeadSources(filters.selectedLeadSources);
                if (filters.selectedLeadStages) setSelectedLeadStages(filters.selectedLeadStages);
                if (filters.selectedLeadStatuses) setSelectedLeadStatuses(filters.selectedLeadStatuses);
                if (filters.selectedAdmissionStatuses) setSelectedAdmissionStatuses(filters.selectedAdmissionStatuses);
                if (filters.selectedWelcomeCallByAdmissionTeamStatuses) setSelectedWelcomeCallByAdmissionTeamStatuses(filters.selectedWelcomeCallByAdmissionTeamStatuses);
                if (filters.selectedVisaProcessingStatuses) setSelectedVisaProcessingStatuses(filters.selectedVisaProcessingStatuses);
                if (filters.selectedApplicationMovedStatuses) setSelectedApplicationMovedStatuses(filters.selectedApplicationMovedStatuses);
                if (filters.selectedConditionalOfferLetterStatuses) setSelectedConditionalOfferLetterStatuses(filters.selectedConditionalOfferLetterStatuses);
                if (filters.selectedUnConditionalOfferLetterStatuses) setSelectedUnConditionalOfferLetterStatuses(filters.selectedUnConditionalOfferLetterStatuses);
                if (filters.selectedFeePaymentStatuses) setSelectedFeePaymentStatuses(filters.selectedFeePaymentStatuses);
                if (filters.selectedFileSubmissionStatuses) setSelectedFileSubmissionStatuses(filters.selectedFileSubmissionStatuses);
                if (filters.selectedFollowUpStatuses) setSelectedFollowUpStatuses(filters.selectedFollowUpStatuses);
                if (filters.selectedAssigneeOptions) setSelectedAssigneeOptions(filters.selectedAssigneeOptions);
                if (filters.selectedSubAgentOptions) setSelectedSubAgentOptions(filters.selectedSubAgentOptions);
                if (filters.selectedCampaignOptions) setSelectedCampaignOptions(filters.selectedCampaignOptions);
                if (filters.selectedPrefferedStreams) setSelectedPrefferedStreams(filters.selectedPrefferedStreams);
                if (filters.selectedSuperAgents) setSelectedSuperAgents(filters.selectedSuperAgents);
                if (filters.selectedAdmissionSubStatus) setSelectedAdmissionSubStatus(filters.selectedAdmissionSubStatus);
                if (filters.selectedLeadSubSources) setSelectedLeadSubSources(filters.selectedLeadSubSources);
                if (filters.selectedLeadSubStages) setSelectedLeadSubStages(filters.selectedLeadSubStages);
                if (filters.selectedLeadSubStatuses) setSelectedLeadSubStatuses(filters.selectedLeadSubStatuses);
                if (filters.selectedDestinations) setSelectedDestinations(filters.selectedDestinations);
                if (filters.selectedDepartments) setSelectedDepartments(filters.selectedDepartments);
                if (filters.selectedStartDate) setSelectedStartDate(filters.selectedStartDate);
                if (filters.selectedEndDate) setSelectedEndDate(filters.selectedEndDate);
                if (filters.selectedTransferredDates) setSelectedTransferredDates(filters.selectedTransferredDates);
                if (filters.selectedTransferredEndDates) setSelectedTransferredEndDates(filters.selectedTransferredEndDates);
                if (filters.selectedUpdatedAtStartDate) setSelectedUpdatedAtStartDate(filters.selectedUpdatedAtStartDate);
                if (filters.selectedUpdatedAtEndDate) setSelectedUpdatedAtEndDate(filters.selectedUpdatedAtEndDate);
                if (filters.selectedFollowUpAtStartDate) setSelectedFollowUpAtStartDate(filters.selectedFollowUpAtStartDate);
                if (filters.selectedFollowUpAtEndDate) setSelectedFollowUpAtEndDate(filters.selectedFollowUpAtEndDate);
                if (filters.selectedIntakeYear) setSelectedIntakeYear(filters.selectedIntakeYear);
                if (filters.selectedIntakeMonth) setSelectedIntakeMonth(filters.selectedIntakeMonth);
            }

            // Restore search queries
            if (state.searchQuery) setSearchQuery(state.searchQuery);
            if (state.phoneSearchQuery) setPhoneSearchQuery(state.phoneSearchQuery);
            if (state.orderByField) setOrderByField(state.orderByField);
            if (state.selectedFollowUpQuickFilter) setSelectedFollowUpQuickFilter(state.selectedFollowUpQuickFilter);

            // Restore scroll position after a short delay to ensure content is loaded
            setTimeout(() => {
                if (state.scrollPosition !== undefined) {
                    window.scrollTo(0, state.scrollPosition);
                }
            }, 200);

            // Clear the navigation state to prevent repeated restoration
            navigate(location.pathname, { replace: true, state: null });
        }
    }, [navigationState, location.pathname, navigate]);

    // Monitor filter combinations and show warning
    useEffect(() => {
        const combinations = calculateFilterCombinations();
        setFilterCombinationCount(combinations);
        // Always show warning when combinations exceed 30, even if user previously dismissed it
        if (combinations > 30) {
            setShowFilterWarning(true);
        } else {
            setShowFilterWarning(false);
        }
    }, [
        selectedLeadSources,
        selectedLeadStages,
        selectedLeadStates,
        selectedLeadStatuses,
        selectedAdmissionStatuses,
        selectedWelcomeCallByAdmissionTeamStatuses,
        selectedVisaProcessingStatuses,
        selectedApplicationMovedStatuses,
        selectedConditionalOfferLetterStatuses,
        selectedUnConditionalOfferLetterStatuses,
        selectedFeePaymentStatuses,
        selectedFileSubmissionStatuses,
        selectedFollowUpStatuses,
        selectedAssigneeOptions,
        selectedSubAgentOptions,
        selectedCampaignOptions,
        selectedPrefferedStreams,
        selectedSuperAgents,
        selectedAdmissionSubStatus,
        selectedLeadSubSources,
        selectedLeadSubStages,
        selectedLeadSubStatuses,
        selectedDestinations,
        selectedDepartments,
        selectedIntakeYear,
        selectedIntakeMonth,
        selectedStartDate,
        selectedUpdatedAtStartDate,
        selectedFollowUpAtStartDate,
        selectedTransferredDates
    ]);

    // Function to determine if assignee filter should show red indicators
    const shouldShowAssigneeRedIndicators = () => {
        return selectedDepartments.length > 0 && selectedAssigneeOptions.length === 0;
    };

    // Helper functions for date filter mutual exclusion
    const isCreatedDateSelected = () => {
        return selectedStartDate.length > 0 || selectedEndDate.length > 0;
    };

    const isUpdatedDateSelected = () => {
        return selectedUpdatedAtStartDate.length > 0 || selectedUpdatedAtEndDate.length > 0;
    };

    const isFollowUpDateSelected = () => {
        return selectedFollowUpAtStartDate.length > 0 || selectedFollowUpAtEndDate.length > 0;
    };

    const isDateFilterDisabled = (filterType: 'created' | 'updated' | 'followUp') => {
        switch (filterType) {
            case 'created':
                return isFollowUpDateSelected();
            case 'updated':
                return isFollowUpDateSelected();
            case 'followUp':
                return isCreatedDateSelected() || isUpdatedDateSelected();
            default:
                return false;
        }
    };

    // Function to calculate filter combinations
    const calculateFilterCombinations = () => {
        // For staff users, exclude assignee filter from count since it's automatically applied
        const assigneeFilterForCount = (isStaff && currentScreen === 'Staff') ? [] : selectedAssigneeOptions;
        
        const filterArrays = [
            selectedLeadSources,
            selectedLeadStages,
            selectedLeadStates,
            selectedLeadStatuses,
            selectedAdmissionStatuses,
            selectedWelcomeCallByAdmissionTeamStatuses,
            selectedVisaProcessingStatuses,
            selectedApplicationMovedStatuses,
            selectedConditionalOfferLetterStatuses,
            selectedUnConditionalOfferLetterStatuses,
            selectedFeePaymentStatuses,
            selectedFileSubmissionStatuses,
            selectedFollowUpStatuses,
            assigneeFilterForCount, // Use filtered assignee options
            selectedSubAgentOptions,
            selectedCampaignOptions,
            selectedPrefferedStreams,
            selectedSuperAgents,
            selectedAdmissionSubStatus,
            selectedLeadSubSources,
            selectedLeadSubStages,
            selectedLeadSubStatuses,
            selectedDestinations,
            selectedDepartments,
            selectedIntakeYear,
            selectedIntakeMonth,
            // Add date filters as single selections since they represent ranges
            selectedStartDate.length > 0 ? ['date-range'] : [],
            selectedUpdatedAtStartDate.length > 0 ? ['updated-date-range'] : [],
            selectedTransferredDates.length > 0 ? ['transferred-date-range'] : [],
            selectedFollowUpAtStartDate.length > 0 ? ['follow-up-date-range'] : [],
            selectedFollowUpAtEndDate.length > 0 ? ['follow-up-date-range'] : []
        ].filter(arr => arr.length > 0); // Only include arrays with selections

        if (filterArrays.length === 0) {
            return 0;
        }

        // Calculate total combinations by multiplying the length of each filter array
        const totalCombinations = filterArrays.reduce((total, filterArray) => {
            return total * filterArray.length;
        }, 1);

        return totalCombinations;
    };

    // Fix: Reset dateRangeStart/dateRangeEnd when filter changes, and ensure correct state update on repeated filter attempts
    const RenderSettingsBox = (
        title: string,
        type: string,
        items: DocumentWithId[],
        selectedItems: string[],
        selectedItems2: string[] | null,
        reduxKey1: string,
        reduxKey2: string | null,
        customColor: string | null,
        getDisplayText: (item: any) => string,
        getColor?: (item: any) => string | undefined,
        disabled?: boolean,
    ) => {
        const dispatch = useAppDispatch();
        const dropdownRef = useRef<HTMLDivElement>(null);
        const customDatePopupRef = useRef<HTMLDivElement>(null);
        const [localDropdownOpen, setLocalDropdownOpen] = useState<boolean>(false);
        const [localSearchQuery, setLocalSearchQuery] = useState('');
        const [inputValue, setInputValue] = useState('');
        const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
        const [filteredItems, setFilteredItems] = useState<DocumentWithId[]>([]);
        const [isSearching, setIsSearching] = useState<boolean>(false);
        const [tempDateValue, setTempDateValue] = useState<string>('');
        const [dateRangeStart, setDateRangeStart] = useState<Date | null>(null);
        const [dateRangeEnd, setDateRangeEnd] = useState<Date | null>(null);
        const [openDatePicker, setOpenDatePicker] = useState<'created' | 'updated' | 'transferred' | 'followUp' | null>(null);
        const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
        const [selectedYear, setSelectedYear] = useState<string | null>(null);

        // console.log("color", customColor);


        const months = [
            { value: 1, name: 'January' },
            { value: 2, name: 'February' },
            { value: 3, name: 'March' },
            { value: 4, name: 'April' },
            { value: 5, name: 'May' },
            { value: 6, name: 'June' },
            { value: 7, name: 'July' },
            { value: 8, name: 'August' },
            { value: 9, name: 'September' },
            { value: 10, name: 'October' },
            { value: 11, name: 'November' },
            { value: 12, name: 'December' }
        ];

        const currentYear = new Date().getFullYear();
        const years = Array.from({ length: 11 }, (_, i) => currentYear - 5 + i + '');

        const isNoFilterOption = (filterValue: string) => {
            return filterValue === 'no-filter' || filterValue === 'null' || filterValue === 'empty';
        };

        useEffect(() => {
            const handleClickOutside = (event: MouseEvent) => {
                if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                    setLocalDropdownOpen(false);
                    setOpenDatePicker(null);
                }
            };
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }, []);

        // Fix: Reset dateRangeStart/dateRangeEnd when filter changes or popup opens
        useEffect(() => {
            if (type === 'date' && selectedItems && selectedItems.length > 0 && selectedItems2 && selectedItems2.length > 0) {
                const startDate = new Date(selectedItems[0]);
                const endDate = new Date(selectedItems2[0]);
                setDateRangeStart(startDate);
                setDateRangeEnd(endDate);
            } else if (type === 'date' && (!selectedItems || selectedItems.length === 0 || !selectedItems2 || selectedItems2.length === 0)) {
                setDateRangeStart(null);
                setDateRangeEnd(null);
            }
        }, [selectedItems, selectedItems2, type, localDropdownOpen, openDatePicker]);

        const handleIntakeMonthYearSelection = () => {
            if (selectedMonth || selectedYear) {
                if (reduxKey1 && selectedMonth !== null) {
                    dispatch(setLeadProperty({
                        key: reduxKey1,
                        value: [selectedMonth]
                    }));
                }

                if (reduxKey2 && selectedYear !== null) {
                    dispatch(setLeadProperty({
                        key: reduxKey2,
                        value: [selectedYear]
                    }));
                }

                setLocalDropdownOpen(false);
            }
        };

        const clearIntakeMonthYearSelection = () => {
            setSelectedMonth(null);
            setSelectedYear(null);
            dispatch(setLeadProperty({ key: reduxKey1, value: [] }));
            if (reduxKey2) {
                dispatch(setLeadProperty({ key: reduxKey2, value: [] }));
            }
        };

        const filterItems = (items: DocumentWithId[], getDisplayText: (item: any) => string) => {
            if (title === 'Destinations' && reduxKey1 === 'leadDestinations') {
                return filteredItems;
            }

            if (!localSearchQuery) return items;
            return items.filter(item =>
                getDisplayText(item.data).toLowerCase().includes(localSearchQuery.toLowerCase())
            );
        };

        const handleLocalSearch = async (e: React.ChangeEvent<HTMLInputElement>) => {
            e.stopPropagation();
            const value = e.target.value;
            setInputValue(value);
            setLocalSearchQuery(value);
            setLocalDropdownOpen(true);

            if (title === 'Destinations' && reduxKey1 === 'leadDestinations' && value.length >= 2) {
                setIsSearching(true);
                try {
                    const countriesRef = collection(db, 'countries');
                    const searchValue = value.toUpperCase();
                    const q = query(
                        countriesRef,
                        where('name', '>=', searchValue),
                        where('name', '<=', searchValue + '\uf8ff'),
                        limit(20)
                    );

                    const querySnapshot = await getDocs(q);
                    const countriesData = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        data: { name: doc.data().name } as Destination
                    }));

                    setFilteredItems(countriesData);
                } catch (error) {
                    console.error('Error searching countries:', error);
                    setFilteredItems([]);
                } finally {
                    setIsSearching(false);
                }
            } else if (title === 'Destinations' && value.length < 2) {
                setFilteredItems([]);
            }
        };

        const handleSelection = (id: string) => {
            if (type === 'date') {
                setTempDateValue(id);
            } else {
                if (isNoFilterOption(id)) {
                    // Handle "No Filter" selection
                    dispatch(setLeadProperty({
                        key: reduxKey1,
                        value: [id]
                    }));

                    // Clear child filters when parent is set to "no filter"
                    if (reduxKey1 === 'leadSources') {
                        dispatch(setLeadProperty({ key: 'leadSubSources', value: [] }));
                    } else if (reduxKey1 === 'leadStages') {
                        dispatch(setLeadProperty({ key: 'leadSubStages', value: [] }));
                    } else if (reduxKey1 === 'leadAdmissionStatuses') {
                        dispatch(setLeadProperty({ key: 'leadAdmissionSubStatus', value: [] }));
                    } else if (reduxKey1 === 'leadStatuses') {
                        dispatch(setLeadProperty({ key: 'leadSubStatuses', value: [] }));
                    }

                    sessionStorage.setItem('currentPage', JSON.stringify(1));
                } else if (id === 'all' && title === 'Assignee Options') {
                    // Handle "All" selection for assignee options
                    dispatch(setLeadProperty({
                        key: reduxKey1,
                        value: ['all']
                    }));
                    sessionStorage.setItem('currentPage', JSON.stringify(1));
                } else if (title === 'Assignee Options' && reduxKey1 === 'leadAssigneeOptions') {
                    // Handle multiple selection for Assignee Options
                    const currentSelected = selectedItems || [];
                    let newSelectedItems: string[];

                    if (currentSelected.includes(id)) {
                        // Remove if already selected
                        newSelectedItems = currentSelected.filter(itemId => itemId !== id);
                        // If removing 'all', keep other selected items
                        if (id === 'all') {
                            newSelectedItems = currentSelected.filter(itemId => itemId !== 'all');
                        }
                    } else {
                        // Add to selection
                        if (id === 'all') {
                            // If selecting 'all', clear other selections and only keep 'all'
                            newSelectedItems = ['all'];
                        } else {
                            // If selecting individual item, remove 'all' if it exists and add new item
                            newSelectedItems = [...currentSelected.filter(itemId => itemId !== 'all'), id];
                        }
                    }

                    dispatch(setLeadProperty({
                        key: reduxKey1,
                        value: newSelectedItems
                    }));
                    sessionStorage.setItem('currentPage', JSON.stringify(1));

                    // Don't close dropdown for multi-select - keep it open for further selections
                    // setLocalDropdownOpen(false);
                    setLocalSearchQuery('');
                    setInputValue('');
                } else {
                    // Handle normal single selection for other filters
                    // Clear child filters whenever parent is changed
                    if (reduxKey1 === 'leadSources') {
                        dispatch(setLeadProperty({ key: 'leadSubSources', value: [] }));
                    } else if (reduxKey1 === 'leadStages') {
                        dispatch(setLeadProperty({ key: 'leadSubStages', value: [] }));
                    } else if (reduxKey1 === 'leadAdmissionStatuses') {
                        dispatch(setLeadProperty({ key: 'leadAdmissionSubStatus', value: [] }));
                    } else if (reduxKey1 === 'leadStatuses') {
                        dispatch(setLeadProperty({ key: 'leadSubStatuses', value: [] }));
                    }

                    dispatch(setLeadProperty({
                        key: reduxKey1,
                        value: [id]
                    }));
                    sessionStorage.setItem('currentPage', JSON.stringify(1));

                    setLocalDropdownOpen(false);
                    setLocalSearchQuery('');
                    const selectedItem = items.find(item => item?.id === id);
                    setInputValue(selectedItem ? getDisplayText(selectedItem.data) : '');
                }
            }
        };


        const handleDateSelection = (from: string | Date, to: string | Date) => {
            // Always store as ISO string for Redux
            const fromStr = from instanceof Date ? from.toISOString() : from;
            const toStr = to instanceof Date ? to.toISOString() : to;
            dispatch(setLeadProperty({ key: reduxKey1, value: [fromStr] }));
            if (reduxKey2) {
                dispatch(setLeadProperty({ key: reduxKey2, value: [toStr] }));
            }

            const storageKey = title === 'Created Date'
                ? 'createdDateRange'
                : title === 'Updated Date'
                    ? 'updatedDateRange'
                    : title === 'FollowUp Date'
                        ? 'followUpDateRange'
                        : 'transferredDateRange';
            sessionStorage.setItem(storageKey, JSON.stringify({ start: fromStr, end: toStr }));

            setTempDateValue(''); // Clear temporary date value after selection
            setDateRangeStart(null);
            setDateRangeEnd(null);
            setLocalDropdownOpen(false);
            setLocalSearchQuery('');
            // Reset to first page when date is selected
            clearPaginationState();
            savePaginationState(1, null);
        };

        const clearDateRangeSelection = (item: 'created' | 'updated' | 'transferred' | 'followUp' | null = null) => {
            setDateRangeStart(null);
            setDateRangeEnd(null);
            dispatch(setLeadProperty({ key: reduxKey1, value: [] }));
            if (reduxKey2) {
                dispatch(setLeadProperty({ key: reduxKey2, value: [] }));
            }

            const storageKey = title === 'Created Date'
                ? 'createdDateRange'
                : title === 'Updated Date'
                    ? 'updatedDateRange'
                    : title === 'FollowUp Date'
                        ? 'followUpDateRange'
                        : 'transferredDateRange';
            sessionStorage.removeItem(storageKey);
            clearPaginationState(); // Reset current page in session storage
            savePaginationState(1, null);
            setOpenDatePicker(item);
        };

        const handleApplyDateSelection = () => {
            if (dateRangeStart && dateRangeEnd) {
                handleDateSelection(dateRangeStart.toISOString(), dateRangeEnd.toISOString());
                setOpenDatePicker(null);
                setLocalDropdownOpen(false);
                setLocalSearchQuery('');
                sessionStorage.setItem('currentPage', JSON.stringify(1)); // Reset current page in session storage
            }
        };

        const clearDataSelection = () => {
            dispatch(setLeadProperty({ key: reduxKey1, value: [] }));
            // Clear child filters when parent is cleared
            if (reduxKey1 === 'leadSources') {
                dispatch(setLeadProperty({ key: 'leadSubSources', value: [] }));
            } else if (reduxKey1 === 'leadStages') {
                dispatch(setLeadProperty({ key: 'leadSubStages', value: [] }));
            } else if (reduxKey1 === 'leadAdmissionStatuses') {
                dispatch(setLeadProperty({ key: 'leadAdmissionSubStatus', value: [] }));
            } else if (reduxKey1 === 'leadStatuses') {
                dispatch(setLeadProperty({ key: 'leadSubStatuses', value: [] }));
            }
            setInputValue('');
            setLocalSearchQuery('');
            setTempDateValue('');
            setLocalDropdownOpen(false);
        };

        const cachedSelectedItemName = useRef<string>('');

        const getSelectedItemText = () => {
            if (selectedItems && selectedItems.length > 0) {
                if (isNoFilterOption(selectedItems[0])) {
                    return `No ${title}`;
                }

                // Handle 'All' cases for assignee options
                if (title === 'Assignee Options') {
                    if (selectedItems.includes('all') || selectedItems[0] === 'all') {
                        return 'All';
                    }
                    
                    // Handle multiple assignee selection
                    if (selectedItems.length > 1) {
                        const selectedNames = selectedItems
                            .map(id => {
                                const item = items?.find(item => item?.id === id);
                                return item ? getDisplayText(item.data) : '';
                            })
                            .filter(name => name !== '');
                        
                        if (selectedNames.length > 2) {
                            return `${selectedNames[0]}, ${selectedNames[1]} +${selectedNames.length - 2} more`;
                        } else if (selectedNames.length === 2) {
                            return `${selectedNames[0]}, ${selectedNames[1]}`;
                        } else if (selectedNames.length === 1) {
                            return selectedNames[0];
                        }
                    } else if (selectedItems.length === 1) {
                        // Single assignee selected
                        const selectedItem = items?.find(item => item?.id === selectedItems[0]);
                        return selectedItem ? getDisplayText(selectedItem.data) : '';
                    }
                    
                    // Check if all assignees are selected (excluding the 'all' option)
                    if (items && items.length > 0) {
                        const actualAssignees = items.filter(item => item.id !== 'all');
                        const selectedAssignees = selectedItems.filter(id => id !== 'all');
                        if (actualAssignees.length === selectedAssignees.length && actualAssignees.every(item => selectedItems.includes(item.id))) {
                            return 'All';
                        }
                    }
                }

                if (title === 'Destinations' && reduxKey1 === 'leadDestinations') {
                    const selectedItem = filteredItems?.find(item => item?.id === selectedItems[0]);
                    if (selectedItem) {
                        return getDisplayText(selectedItem.data);
                    }

                    if (cachedSelectedItemName.current) {
                        return cachedSelectedItemName.current;
                    }

                    return 'Loading...';
                } else {
                    const selectedItem = items?.find(item => item?.id === selectedItems[0]);
                    return selectedItem ? getDisplayText(selectedItem.data) : '';
                }
            }
            return inputValue;
        };

        useEffect(() => {
            if (title === 'Destinations' && reduxKey1 === 'leadDestinations' &&
                selectedItems && selectedItems.length > 0 &&
                !isNoFilterOption(selectedItems[0]) &&
                !cachedSelectedItemName.current) {

                const selectedInFiltered = filteredItems.find(item => item.id === selectedItems[0]);
                if (selectedInFiltered) {
                    cachedSelectedItemName.current = getDisplayText(selectedInFiltered.data);
                    return;
                }

                const fetchSelectedCountry = async () => {
                    try {
                        const countryDoc = await getDoc(doc(db, 'countries', selectedItems[0]));
                        if (countryDoc.exists()) {
                            cachedSelectedItemName.current = countryDoc.data().name;
                            setInputValue(cachedSelectedItemName.current);
                        }
                    } catch (error) {
                        console.error('Error fetching selected country:', error);
                    }
                };

                fetchSelectedCountry();
            }
        }, [selectedItems, title, reduxKey1, filteredItems]);

        const handleInputClick = async () => {
            // Prevent opening if disabled
            if (disabled) return;

            setLocalDropdownOpen(true);

            if (!inputValue && type !== 'date') {
                const selectedItem = items.find(item => item?.id === selectedItems[0]);
                if (selectedItem) {
                    setInputValue('');
                    setLocalSearchQuery('');
                }
            }

            if (title === 'Destinations' && reduxKey1 === 'leadDestinations') {
                setFilteredItems([]);
            }
            // Fix: Reset date range when opening date picker
            if (type === 'date') {
                if (selectedItems && selectedItems.length > 0 && selectedItems2 && selectedItems2.length > 0) {
                    setDateRangeStart(new Date(selectedItems[0]));
                    setDateRangeEnd(new Date(selectedItems2[0]));
                } else {
                    setDateRangeStart(null);
                    setDateRangeEnd(null);
                    if (title === 'Created Date') setOpenDatePicker('created');
                    else if (title === 'Updated Date') setOpenDatePicker('updated');
                    else if (title === 'FollowUp Date') setOpenDatePicker('followUp');
                }
            }
        };

        const handleClear = (e: React.MouseEvent) => {
            e.stopPropagation();
            dispatch(setLeadProperty({ key: reduxKey1, value: [] }));

            // Clear child filters when parent is cleared
            if (reduxKey1 === 'leadSources') {
                dispatch(setLeadProperty({ key: 'leadSubSources', value: [] }));
            } else if (reduxKey1 === 'leadStages') {
                dispatch(setLeadProperty({ key: 'leadSubStages', value: [] }));
            } else if (reduxKey1 === 'leadAdmissionStatuses') {
                dispatch(setLeadProperty({ key: 'leadAdmissionSubStatus', value: [] }));
            } else if (reduxKey1 === 'leadStatuses') {
                dispatch(setLeadProperty({ key: 'leadSubStatuses', value: [] }));
            }

            if (reduxKey2) {
                dispatch(setLeadProperty({ key: reduxKey2, value: [] }));
            }
            setInputValue('');
            setLocalSearchQuery('');
            setTempDateValue('');
            if (type === 'date') {
                setDateRangeStart(null);
                setDateRangeEnd(null);
            }
            if (type === 'intake-month-year') {
                setSelectedMonth(null);
                setSelectedYear(null);
            }
            clearPaginationState(); // Reset current page in session storage
            savePaginationState(1, null);
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
            const filteredItems = filterItems(items, getDisplayText);

            if (!localDropdownOpen && (e.key === 'ArrowDown' || e.key === 'Enter')) {
                e.preventDefault();
                setLocalDropdownOpen(true);
                return;
            }

            if (!localDropdownOpen) return;

            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    setHighlightedIndex(prevIndex => {
                        if (prevIndex >= filteredItems.length - 1) {
                            return 0;
                        }
                        return prevIndex + 1;
                    });
                    break;

                case 'ArrowUp':
                    e.preventDefault();
                    setHighlightedIndex(prevIndex => {
                        if (prevIndex <= 0) {
                            return filteredItems.length - 1;
                        }
                        return prevIndex - 1;
                    });
                    break;

                case 'Enter':
                    e.preventDefault();
                    if (highlightedIndex >= 0 && highlightedIndex < filteredItems.length) {
                        handleSelection(filteredItems[highlightedIndex].id);
                    }
                    break;

                case 'Escape':
                    e.preventDefault();
                    setLocalDropdownOpen(false);
                    break;
            }
        };

        const scrollToHighlighted = (index: number) => {
            if (index < 0) return;

            setTimeout(() => {
                const highlightedElement = document.getElementById(`dropdown-item-${index}`);
                if (highlightedElement && dropdownRef.current) {
                    const dropdownList = dropdownRef.current.querySelector('.max-h-48');
                    if (dropdownList) {
                        const listRect = dropdownList.getBoundingClientRect();
                        const elementRect = highlightedElement.getBoundingClientRect();

                        if (elementRect.top < listRect.top) {
                            highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        } else if (elementRect.bottom > listRect.bottom) {
                            highlightedElement.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
                        }
                    }
                }
            }, 0);
        };

        // Determine if assignee filter should have red border
        const shouldShowRedBorder = () => {
            if (title === 'Assignee Options') {
                return shouldShowAssigneeRedIndicators();
            }
            return false;
        };

        const getBorderColor = () => {
            if (shouldShowRedBorder()) {
                return '#ef4444'; // red-500
            }
            return customColor || '';
        };

        useEffect(() => {
            scrollToHighlighted(highlightedIndex);
        }, [highlightedIndex]);

        return (
            <div ref={dropdownRef} className={`flex flex-col bg-white rounded-lg shadow-sm relative ${localDropdownOpen ? 'z-40' : 'z-0'}`}>
                <div className="px-2 py-1.5">
                    <div className="flex items-center justify-between mb-0.5">
                        <label className={`text-[14px] font-medium font-Montserrat ${shouldShowRedBorder() ? 'text-red-500' : 'text-gray-600'}`}>{title}</label>
                        {title === 'FollowUp Date' && (
                            <div className="flex gap-1">
                                <WaterEffectButton
                                    onClick={() => selectedFollowUpQuickFilter === 'upcoming' ? clearFollowUpQuickFilter() : handleFollowUpQuickFilter('upcoming')}
                                    isActive={selectedFollowUpQuickFilter === 'upcoming'}
                                    color="blue"
                                // disabled={selectedFollowUpQuickFilter === 'today' || selectedFollowUpQuickFilter === 'overdue'}
                                >
                                    Upcoming
                                </WaterEffectButton>
                                <WaterEffectButton
                                    onClick={() => selectedFollowUpQuickFilter === 'today' ? clearFollowUpQuickFilter() : handleFollowUpQuickFilter('today')}
                                    isActive={selectedFollowUpQuickFilter === 'today'}
                                    color="green"
                                // disabled={selectedFollowUpQuickFilter === 'upcoming' || selectedFollowUpQuickFilter === 'overdue'}
                                >
                                    Today
                                </WaterEffectButton>
                                <WaterEffectButton
                                    onClick={() => selectedFollowUpQuickFilter === 'overdue' ? clearFollowUpQuickFilter() : handleFollowUpQuickFilter('overdue')}
                                    isActive={selectedFollowUpQuickFilter === 'overdue'}
                                    color="red"
                                // disabled={selectedFollowUpQuickFilter === 'today' || selectedFollowUpQuickFilter === 'upcoming'}
                                >
                                    Overdue
                                </WaterEffectButton>
                            </div>
                        )}
                    </div>
                    <div className="relative">
                        <div className="relative flex items-center">
                            {type === 'intake-month-year' ? (
                                <div className="relative w-full">
                                    <input
                                        type="text"
                                        className={`w-full h-8 px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-Montserrat placeholder-gray-400  ${shouldShowRedBorder() ? 'border-red-500' : 'border-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                        style={{
                                            borderColor: getBorderColor(),
                                        }}
                                        title={title}
                                        value={
                                            (selectedItems && selectedItems.length > 0) || (selectedItems2 && selectedItems2.length > 0)
                                                ? `${selectedItems && selectedItems.length > 0 ? selectedItems[0] : ''} ${selectedItems2 && selectedItems2.length > 0 ? selectedItems2[0] : ''}`
                                                : "Select Intake Month & Year"
                                        }
                                        placeholder="Select..."
                                        readOnly
                                        onClick={() => setLocalDropdownOpen(true)}
                                    />
                                </div>
                            ) : type !== 'date' && type !== 'transferred-date' ? (
                                <input
                                    type={type}
                                    className={`w-full h-8 px-2 py-1 border rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-Montserrat placeholder-gray-400 ${type === 'date' && selectedItems.length > 0 ? '[&::-webkit-calendar-picker-indicator]:hidden' : ''
                                        } ${shouldShowRedBorder() ? 'border-red-500' : 'border-gray-200'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    // style border with customColor or red for assignee filter
                                    style={{
                                        borderColor: getBorderColor(),
                                    }}
                                    title={title}
                                    value={type === 'date' ? (tempDateValue || selectedItems[0] || '') : (localDropdownOpen ? localSearchQuery : getSelectedItemText())}
                                    placeholder="Select..."
                                    onClick={(e) => { handleInputClick(); e.stopPropagation(); }}
                                    onChange={(e) => {
                                        if (type === 'date') {
                                            handleSelection(e.target.value);
                                        } else {
                                            e.stopPropagation();
                                            setLocalSearchQuery(e.target.value);
                                            setInputValue(e.target.value);
                                            setLocalDropdownOpen(true);
                                        }
                                    }}
                                />
                            ) : (
                                <div className="relative w-full">
                                    <div className="relative">
                                        <input
                                            type="text"
                                            className={`w-full h-8 px-2 py-1 border border-gray-200 rounded-md text-xs focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent transition-all duration-200 font-Montserrat placeholder-gray-400  ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                                            title={title}
                                            value={selectedItems && selectedItems.length > 0 && selectedItems2 && selectedItems2.length > 0
                                                ? `${new Date(selectedItems[0]).toLocaleDateString()} - ${new Date(selectedItems2[0]).toLocaleDateString()}`
                                                : title === 'Created Date' ? "Select Create Date Range" : title === 'Updated Date' ? "Select Update Date Range" : title === 'FollowUp Date' ? "Select Follow Up Date Range" : "Select Transferred Date Range"}
                                            placeholder="Select..."
                                            readOnly
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (!disabled) {
                                                    setOpenDatePicker(title === 'Created Date' ? 'created' : title === 'Updated Date' ? 'updated' : title === 'FollowUp Date' ? 'followUp' : 'transferred');
                                                }
                                            }}
                                        />

                                        {/* Calendar icon for date inputs */}
                                        {type === 'date' && (
                                            <svg
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-700 pointer-events-none"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                                xmlns="http://www.w3.org/2000/svg"
                                            >
                                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                <line x1="16" y1="2" x2="16" y2="6"></line>
                                                <line x1="8" y1="2" x2="8" y2="6"></line>
                                                <line x1="3" y1="10" x2="21" y2="10"></line>
                                            </svg>
                                        )}

                                        {/* Close button for non-date inputs when items are selected */}
                                        {selectedItems.length > 0 && type !== 'date' && (
                                            <button
                                                type="button"
                                                className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    // Clear all selected items
                                                    if (title === 'Assignee Options') {
                                                        // For assignee options, clear all selections
                                                        dispatch(setLeadProperty({
                                                            key: reduxKey1,
                                                            value: []
                                                        }));
                                                        sessionStorage.setItem('currentPage', JSON.stringify(1));
                                                    } else {
                                                        handleSelection('');
                                                    }
                                                    setLocalSearchQuery('');
                                                    setInputValue('');
                                                }}
                                            >
                                                <svg
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-full h-full"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}

                                        {/* Close button for date inputs when a date is selected */}
                                        {type === 'date' && (tempDateValue || selectedItems[0]) && (
                                            <button
                                                type="button"
                                                className="absolute right-8 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 hover:text-gray-600 transition-colors duration-200"
                                                onClick={handleClear}
                                            >
                                                <svg
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="w-full h-full"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>

                                    {title === 'Created Date' && openDatePicker === 'created' && (
                                        <div
                                            ref={customDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select Create Date Range
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {selectedItems && selectedItems2 && selectedItems.length > 0 && selectedItems2.length > 0
                                                        ? `${new Date(selectedItems[0]).toLocaleDateString()} - ${new Date(selectedItems2[0]).toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => {
                                                        setDateRangeStart(new Date());
                                                        setDateRangeEnd(new Date());
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 7);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 30);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 90);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                                </div>
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DatePicker
                                                        selected={dateRangeStart}
                                                        startDate={dateRangeStart ?? undefined}
                                                        endDate={dateRangeEnd ?? undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setDateRangeStart(start);
                                                            setDateRangeEnd(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        clearDateRangeSelection('created');
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleApplyDateSelection}
                                                    disabled={!dateRangeStart || !dateRangeEnd}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {title === 'Updated Date' && openDatePicker === 'updated' && (
                                        <div
                                            ref={customDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select Update Date Range
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {selectedItems && selectedItems2 && selectedItems.length > 0 && selectedItems2.length > 0
                                                        ? `${new Date(selectedItems[0]).toLocaleDateString()} - ${new Date(selectedItems2[0]).toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => {
                                                        setDateRangeStart(new Date());
                                                        setDateRangeEnd(new Date());
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 7);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 30);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 90);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                                </div>
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DatePicker
                                                        selected={dateRangeStart}
                                                        startDate={dateRangeStart ?? undefined}
                                                        endDate={dateRangeEnd ?? undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setDateRangeStart(start);
                                                            setDateRangeEnd(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        clearDateRangeSelection('updated');
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleApplyDateSelection}
                                                    disabled={!dateRangeStart || !dateRangeEnd}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {title === 'FollowUp Date' && openDatePicker === 'followUp' && (
                                        <div
                                            ref={customDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select FollowUp Date Range
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {selectedItems && selectedItems2 && selectedItems.length > 0 && selectedItems2.length > 0
                                                        ? `${new Date(selectedItems[0]).toLocaleDateString()} - ${new Date(selectedItems2[0]).toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                {/* <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => {
                                                        setDateRangeStart(new Date());
                                                        setDateRangeEnd(new Date());
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 7);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} 
                                                    className="text-[12px] font-normal leading-[16px] text-left text-red-600">Last 7 Days (Overdue)</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        const end = new Date();
                                                        end.setDate(end.getDate() + 7);
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-green-600">Next 7 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 30);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-red-600">Last 30 Days (Overdue)</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        const end = new Date();
                                                        end.setDate(end.getDate() + 30);
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-green-600">Next 30 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 90);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-red-600">Last 90 Days (Overdue)</button>
                                                </div> */}
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DatePicker
                                                        selected={dateRangeStart}
                                                        startDate={dateRangeStart ?? undefined}
                                                        endDate={dateRangeEnd ?? undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setDateRangeStart(start);
                                                            setDateRangeEnd(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        clearDateRangeSelection('updated');
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleApplyDateSelection}
                                                    disabled={!dateRangeStart || !dateRangeEnd}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {title === 'Assignee Options' && (
                                        <div
                                            id="dropdown-item-all"
                                            className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedIndex === -2
                                                ? 'bg-blue-100 text-blue-800'
                                                : selectedItems?.includes('all')
                                                    ? 'bg-blue-50 text-blue-700'
                                                    : 'hover:bg-gray-50'
                                                }`}
                                            onClick={() => handleSelection('all')}
                                            onMouseEnter={() => setHighlightedIndex(-2)}
                                        >
                                            <span className="text-xs font-Montserrat truncate flex-1">
                                                All
                                            </span>
                                            {selectedItems?.includes('all') && (
                                                <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                </svg>
                                            )}
                                        </div>
                                    )}

                                    {title === 'Transferred Date' && openDatePicker === 'transferred' && (
                                        <div
                                            ref={customDatePopupRef}
                                            className="absolute top-full right-0 mt-2 w-auto h-auto bg-white border rounded-3xl shadow-lg p-4 z-[5]"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            <div className="flex justify-between items-center mb-2">
                                                <h3 className="text-[14px] font-normal leading-[20px] text-left">
                                                    Select Transfer Date Range
                                                </h3>
                                            </div>
                                            <div className="mb-1">
                                                <span className="text-[12px] font-normal leading-[16px] text-left">
                                                    <span className='font-medium'>Current: </span>
                                                    {selectedItems && selectedItems2 && selectedItems.length > 0 && selectedItems2.length > 0
                                                        ? `${new Date(selectedItems[0]).toLocaleDateString()} - ${new Date(selectedItems2[0]).toLocaleDateString()}`
                                                        : "Select Date Range"}
                                                </span>
                                                <div className="grid grid-cols-2 gap-2 mt-2">
                                                    <button onClick={() => {
                                                        setDateRangeStart(new Date());
                                                        setDateRangeEnd(new Date());
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Today</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 7);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 7 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 30);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 30 Days</button>
                                                    <button onClick={() => {
                                                        const start = new Date();
                                                        start.setDate(start.getDate() - 90);
                                                        const end = new Date();
                                                        setDateRangeStart(start);
                                                        setDateRangeEnd(end);
                                                    }} className="text-[12px] font-normal leading-[16px] text-left text-cyan-600">Last 90 Days</button>
                                                </div>
                                                <div
                                                    className="p-4 bg-white shadow-lg rounded-[12px]"
                                                    style={{ width: '100%', height: 'auto' }}
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <DatePicker
                                                        selected={dateRangeStart}
                                                        startDate={dateRangeStart ?? undefined}
                                                        endDate={dateRangeEnd ?? undefined}
                                                        selectsRange
                                                        onChange={(update) => {
                                                            const [start, end] = update;
                                                            setDateRangeStart(start);
                                                            setDateRangeEnd(end);
                                                        }}
                                                        inline
                                                        className="w-full h-full p-4 bg-gray-100 shadow-lg rounded-[12px] custom-calendar"
                                                        maxDate={new Date()}
                                                    />
                                                </div>
                                            </div>
                                            <div className="flex justify-between mt-2">
                                                <button
                                                    className="px-8 py-2 text-white bg-black rounded-full"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        clearDateRangeSelection('transferred');
                                                    }}
                                                >
                                                    Clear
                                                </button>
                                                <button
                                                    className="px-8 py-2 text-white bg-cyan-600 rounded-full"
                                                    onClick={handleApplyDateSelection}
                                                    disabled={!dateRangeStart || !dateRangeEnd}
                                                >
                                                    Apply
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                            <div className="absolute right-1.5 flex items-center space-x-0.5">
                                {(selectedItems.length > 0 || selectedItems2 && selectedItems2.length > 0 || inputValue) && (
                                    <button
                                        onClick={handleClear}
                                        className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                    >
                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                )}
                                {type !== 'date' && type !== 'transferred-date' && type !== 'intake-month-year' && (
                                    <button
                                        type="button"
                                        className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                        onClick={() => setLocalDropdownOpen(!localDropdownOpen)}
                                    >
                                        <svg
                                            className={`w-4 h-4 text-gray-400 transform transition-transform duration-200 ${localDropdownOpen ? 'rotate-180' : ''}`}
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                        </svg>
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {localDropdownOpen && type === 'intake-month-year' && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden z-40 min-w-[250px]">
                        <div className="p-3">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <h4 className="text-xs font-medium mb-1">Month</h4>
                                    <div className="max-h-48 overflow-y-auto">
                                        {months.map((month) => (
                                            <div
                                                key={month.value}
                                                className={`px-2 py-1 text-xs cursor-pointer rounded ${selectedMonth === month.name ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'}`}
                                                onClick={() => setSelectedMonth(month.name)}
                                            >
                                                {month.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <h4 className="text-xs font-medium mb-1">Year</h4>
                                    <div className="max-h-48 overflow-y-auto">
                                        {years.map((year) => (
                                            <div
                                                key={year}
                                                className={`px-2 py-1 text-xs cursor-pointer rounded ${selectedYear === year ? 'bg-blue-100 text-blue-800' : 'hover:bg-gray-50'}`}
                                                onClick={() => setSelectedYear(year)}
                                            >
                                                {year}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            <div className="flex justify-between mt-3 pt-2 border-t">
                                <button
                                    className="px-3 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded-md"
                                    onClick={() => {
                                        clearIntakeMonthYearSelection();
                                        setLocalDropdownOpen(false);
                                    }}
                                >
                                    Clear
                                </button>
                                <button
                                    className="px-3 py-1 text-xs bg-blue-500 hover:bg-blue-600 text-white rounded-md disabled:opacity-50"
                                    onClick={() => {
                                        handleIntakeMonthYearSelection();
                                    }}
                                    disabled={!selectedMonth && !selectedYear}
                                >
                                    Apply
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {localDropdownOpen && type !== 'date' && type !== 'transferred-date' && type !== 'intake-month-year' && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-48 overflow-y-auto z-40 min-w-[200px]">
                        <div className="p-1">
                            <div className="px-2 py-1 sticky top-0 bg-white z-10 border-b border-gray-100">
                                <input
                                    type="text"
                                    className="w-full px-2 py-1 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    placeholder={`Search ${title}...`}
                                    value={localSearchQuery}
                                    onChange={(e) => title === 'Destinations' && reduxKey1 === 'leadDestinations' ? handleLocalSearch(e) : setLocalSearchQuery(e.target.value)}
                                    onClick={(e) => e.stopPropagation()}
                                    onKeyDown={handleKeyDown}
                                    autoFocus
                                />
                            </div>

                            {/* "No Filter" option */}
                            <div
                                id="dropdown-item-no-filter"
                                className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedIndex === -1
                                    ? 'bg-blue-100 text-blue-800'
                                    : selectedItems?.includes('no-filter')
                                        ? 'bg-blue-50 text-blue-700'
                                        : 'hover:bg-gray-50'
                                    }`}
                                onClick={() => handleSelection('no-filter')}
                                onMouseEnter={() => setHighlightedIndex(-1)}
                            >
                                <span className="text-xs font-Montserrat truncate flex-1">
                                    No {title}
                                </span>
                                {selectedItems?.includes('no-filter') && (
                                    <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                )}
                            </div>

                            {isSearching && title === 'Destinations' && (
                                <div className="flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors">
                                    <span className="text-xs font-Montserrat truncate flex-1">Searching...</span>
                                </div>
                            )}
                            {/* {items.length === 0 && !isSearching && title !== 'Destinations' && (
                                <div
                                    className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors`}
                                >
                                    <span
                                        className="text-xs font-Montserrat truncate flex-1"
                                    >
                                        No {title} available
                                    </span>
                                </div>
                            )} */}
                            {title === 'Destinations' && (!localSearchQuery || localSearchQuery.length < 2) && (
                                <div className="flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors">
                                    <span className="text-xs font-Montserrat truncate flex-1">
                                        {localSearchQuery.length === 0
                                            ? "Type to search for a country"
                                            : "Type at least 2 characters to search"}
                                    </span>
                                </div>
                            )}
                            {title === 'Destinations' ? (
                                filteredItems.length > 0 ? filteredItems.map((item, index) => (
                                    <div
                                        id={`dropdown-item-${index}`}
                                        key={item?.id}
                                        className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedIndex === index
                                            ? 'bg-blue-100 text-blue-800'
                                            : selectedItems?.includes(item?.id)
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'hover:bg-gray-50'
                                            }`}
                                        onClick={() => handleSelection(item?.id)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        <span
                                            className="text-xs font-Montserrat truncate flex-1"
                                        >
                                            {getDisplayText(item?.data)}
                                        </span>
                                        {selectedItems?.includes(item?.id) && (
                                            <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                )) : (!isSearching && localSearchQuery.length >= 2) ? (
                                    <div className="flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors">
                                        <span className="text-xs font-Montserrat truncate flex-1">No countries found</span>
                                    </div>
                                ) : null
                            ) : filterItems(items, getDisplayText).length > 0 ? (
                                filterItems(items, getDisplayText).map((item, index) => (
                                    <div
                                        id={`dropdown-item-${index}`}
                                        key={item?.id}
                                        className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedIndex === index
                                            ? 'bg-blue-100 text-blue-800'
                                            : selectedItems?.includes(item?.id)
                                                ? 'bg-blue-50 text-blue-700'
                                                : 'hover:bg-gray-50'
                                            }`}
                                        onClick={() => handleSelection(item?.id)}
                                        onMouseEnter={() => setHighlightedIndex(index)}
                                    >
                                        <span
                                            className="text-xs font-Montserrat truncate flex-1"
                                            style={getColor ? { color: getColor(item?.data) } : undefined}
                                        >
                                            {getDisplayText(item?.data)}
                                        </span>
                                        {selectedItems?.includes(item?.id) && (
                                            <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                            </svg>
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div className="px-2 py-1.5 text-xs text-gray-500 text-center">
                                    No results found
                                </div>
                            )}
                            
                            {/* Add Done button for Assignee Options when multiple selections */}
                            {title === 'Assignee Options' && selectedItems && selectedItems.length > 0 && (
                                <div className="px-2 py-2 border-t border-gray-100">
                                    <button
                                        className="w-full px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors duration-200"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setLocalDropdownOpen(false);
                                            setLocalSearchQuery('');
                                            setInputValue('');
                                        }}
                                    >
                                        Done
                                    </button>
                                </div> 
                            )}
                        </div>
                    </div>
                )}
            </div>
        );
    };


    // Pipeline handling functions moved to AddUpdateLeadModal

    // Function to handle date range selection

    // Replace these state declarations:
    const [customDatePopupOpen, setCustomDatePopupOpen] = React.useState(false);
    // const [startDate, setStartDate] = React.useState<Date | null>(null);
    // const [endDate, setEndDate] = React.useState<Date | null>(null);

    // With these (if you're using the dates for "Updated At" filter):
    const customDatePopupRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        setSelectedUpdatedAtStartDate([]);
        setSelectedUpdatedAtEndDate([]);
    }, [userData?.companyId, currentScreen]);

    useEffect(() => {
        // Get all filterSettings from Firestore
        const q = query(collection(db, 'filterSettings'));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            const configs: FilterConfig[] = [];
            querySnapshot.forEach((doc) => {
                const data = doc.data() as FilterConfig;
                const { id, ...rest } = data as any;
                configs.push({ id: doc.id, ...rest });
            });

            // setFilterConfigs({ FilterConfig: configs });
            console.log('Filter Configs:', configs);

            // Check which filter IDs match the department's selectedFilterIds
            if (departmentData?.selectedFilterIds?.length) {
                const matchedFilters = configs.filter((config) =>
                    departmentData?.selectedFilterIds?.includes(config.id)
                );
                setSelectedFilters(matchedFilters);
                setFilterConfigs({
                    find: null,
                    filter: () => [],
                    FilterConfig: matchedFilters
                })
                console.log('Matched Filters:', matchedFilters);
            } else {
                setSelectedFilters([]); // clear selection if no filter ids
            }
        });

        return () => unsubscribe();
    }, [departmentData?.selectedFilterIds]); // re-run if departmentData changes



    // const SettingsBoxesLayout: React.FC = () => {
    //     return (
    //         <div className="grid grid-cols-4 gap-3 p-2">
    //             <div className={`col-span-4 md:col-span-1  ${leadSubSources.some(subSource => subSource?.data?.sourceId === selectedLeadSources[0]) ? 'border border-green-200 rounded-md p-2 ' : ''}`} >
    //                 {RenderSettingsBox(
    //                     'Lead Sources',
    //                     'text',
    //                     leadSources,
    //                     selectedLeadSources,
    //                     null,
    //                     'leadSources',
    //                     null,
    //                     null,
    //                     (item: LeadSource) => item?.source,
    //                 )}

    //                 {selectedLeadSources && leadSubSources.length > 0 && leadSubSources.some(subSource => subSource?.data?.sourceId === selectedLeadSources[0]) && (
    //                     <>
    //                         {RenderSettingsBox(
    //                             'Sub Sources',
    //                             'text',
    //                             leadSubSources.filter(subSource => selectedLeadSources.includes(subSource?.data?.sourceId)),
    //                             selectedLeadSubSources,
    //                             null,
    //                             'leadSubSources',
    //                             null,
    //                             '#F3C623',
    //                             (item: LeadSubSources) => item?.subSource,

    //                         )}
    //                     </>
    //                 )}
    //             </div>


    //             <div className={`col-span-4 md:col-span-1  ${leadSubStages.some(subStage => subStage?.data?.stageId === selectedLeadStages[0]) ? 'border border-green-200 rounded-md p-2 ' : ''}`} >
    //                 {RenderSettingsBox(
    //                     'Lead Stages',
    //                     'text',
    //                     leadStages,
    //                     selectedLeadStages,
    //                     null,
    //                     'leadStages',
    //                     null,
    //                     null,
    //                     (item: LeadStage) => item?.stage,
    //                     (item: LeadStage) => item?.color,
    //                 )}

    //                 {selectedLeadStages && leadSubStages.length > 0 && leadSubStages.some(subStage => subStage?.data?.stageId === selectedLeadStages[0]) && (
    //                     <>
    //                         {RenderSettingsBox(
    //                             'Sub Stages',
    //                             'text',
    //                             leadSubStages.filter(subStage => selectedLeadStages.includes(subStage?.data?.stageId)),
    //                             selectedLeadSubStages,
    //                             null,
    //                             'leadSubStages',
    //                             null,
    //                             '#F3C623',
    //                             (item: LeadSubStages) => item?.subStage,
    //                         )}
    //                     </>
    //                 )}
    //             </div>


    //             {RenderSettingsBox(
    //                 'Lead States',
    //                 'text',
    //                 leadStates,
    //                 selectedLeadStates,
    //                 null,
    //                 'leadStates',
    //                 null,
    //                 null,
    //                 (item: LeadState) => item?.state,
    //             )}
    //             <div className={`col-span-4 md:col-span-1  ${leadSubStatuses.some(subStatus => subStatus?.data?.statusId === selectedLeadStatuses[0]) ? 'border border-green-200 rounded-md p-2 ' : ''}`} >
    //                 {RenderSettingsBox(
    //                     'Lead Status',
    //                     'text',
    //                     leadStatuses,
    //                     selectedLeadStatuses,
    //                     null,
    //                     'leadStatuses',
    //                     null,
    //                     null,
    //                     (item: LeadStatus) => item?.status,
    //                     (item: LeadStatus) => item?.color,
    //                 )}
    //                 {selectedLeadStatuses && leadSubStatuses.length > 0 && leadSubStatuses.some(subStatus => subStatus?.data?.statusId === selectedLeadStatuses[0]) && (
    //                     <>
    //                         {RenderSettingsBox(
    //                             'Sub Statuses',
    //                             'text',
    //                             leadSubStatuses.filter(subStatus => selectedLeadStatuses.includes(subStatus?.data?.statusId)),
    //                             selectedLeadSubStatuses,
    //                             null,
    //                             'leadSubStatuses',
    //                             null,
    //                             '#F3C623',
    //                             (item: LeadSubStatuses) => item?.subStatus,
    //                         )}
    //                     </>
    //                 )}
    //             </div>

    //             <div className={`col-span-4 md:col-span-1  ${admissionSubStatus.some(subStatus => subStatus?.data?.admissionStatusId === selectedAdmissionStatuses[0]) ? 'border border-green-200 rounded-md p-2 ' : ''}`} >

    //                 {RenderSettingsBox(
    //                     'Admission Status',
    //                     'text',
    //                     admissionStatuses,
    //                     selectedAdmissionStatuses,
    //                     null,
    //                     'leadAdmissionStatuses',
    //                     null,
    //                     null,
    //                     (item: LeadAdmissionStatus) => item?.admissionStatus,
    //                 )}

    //                 {/* lead Admission SubStatus */}

    //                 {selectedAdmissionStatuses && admissionSubStatus.length > 0 && admissionSubStatus.some(subStatus => subStatus?.data?.admissionStatusId === selectedAdmissionStatuses[0]) && (
    //                     <>
    //                         {RenderSettingsBox(
    //                             'Admission Sub Status',
    //                             'text',
    //                             admissionSubStatus.filter(subStatus => selectedAdmissionStatuses.includes(subStatus?.data?.admissionStatusId)),
    //                             selectedAdmissionSubStatus,
    //                             null,
    //                             'leadAdmissionSubStatus',
    //                             null,
    //                             '#F3C623',
    //                             (item: AdmissionSubStatus) => item?.subAdmissionStatus,
    //                         )}
    //                     </>
    //                 )}
    //             </div>


    //             {RenderSettingsBox(
    //                 'Welcome Call Status',
    //                 'text',
    //                 welcomeCallByAdmissionTeamStatuses,
    //                 selectedWelcomeCallByAdmissionTeamStatuses,
    //                 null,
    //                 'leadWelcomeCallByAdmissionTeamStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadWelcomeCallByAdmissionTeamStatus) => item?.welcomeCallByAdmissionTeamStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Visa Processing',
    //                 'text',
    //                 visaProcessingStatuses,
    //                 selectedVisaProcessingStatuses,
    //                 null,
    //                 'leadVisaProcessingStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadVisaFileProcessingStatus) => item?.visaFileProcessingStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Application Status',
    //                 'text',
    //                 applicationMovedStatuses,
    //                 selectedApplicationMovedStatuses,
    //                 null,
    //                 'leadApplicationMovedStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadApplicationMovedStatus) => item?.applicationMovedStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Conditional Offer Letter Status',
    //                 'text',
    //                 conditionalofferLetterStatuses,
    //                 selectedConditionalOfferLetterStatuses,
    //                 null,
    //                 'leadConditionalOfferLetterStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadOfferLetterStatus) => item?.offerLetterStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Unconditional Offer Letter Status',
    //                 'text',
    //                 unConditionalofferLetterStatuses,
    //                 selectedUnConditionalOfferLetterStatuses,
    //                 null,
    //                 'leadUnConditionalOfferLetterStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadOfferLetterStatus) => item?.offerLetterStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Fee Payment Status',
    //                 'text',
    //                 feePaymentStatuses,
    //                 selectedFeePaymentStatuses,
    //                 null,
    //                 'leadFeePaymentStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadFeePaymentStatus) => item?.feePaymentStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'File Submission Status',
    //                 'text',
    //                 fileSubmissionStatuses,
    //                 selectedFileSubmissionStatuses,
    //                 null,
    //                 'leadFileSubmissionStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadFileSubmissionStatus) => item?.fileSubmissionStatus,
    //             )}
    //             {currentScreen !== "Staff" && RenderSettingsBox(
    //                 'Departments',
    //                 'text',
    //                 departments,
    //                 selectedDepartments,
    //                 null,
    //                 'departments',
    //                 null,
    //                 null,
    //                 (item: any) => item?.department,
    //             )}
    //             {currentScreen !== "Staff" && (
    //                 <>
    //                     {RenderSettingsBox(
    //                         'Assignee Options',
    //                         'text',
    //                         [{ id: 'all', data: { name: 'All' } }, ...assigneeOptions],
    //                         selectedAssigneeOptions,
    //                         null,
    //                         'leadAssigneeOptions',
    //                         null,
    //                         null,
    //                         (item: any) => item?.name,
    //                     )}
    //                 </>
    //             )}


    //             {RenderSettingsBox(
    //                 'Sub Agent Options',
    //                 'text',
    //                 subAgentOptions,
    //                 selectedSubAgentOptions,
    //                 null,
    //                 'leadSubAgentOptions',
    //                 null,
    //                 null,
    //                 (item: User) => item?.name,
    //             )}

    //             {RenderSettingsBox(
    //                 'Super Agents',
    //                 'text',
    //                 superAgents,
    //                 selectedSuperAgents,
    //                 null,
    //                 'leadSuperAgents',
    //                 null,
    //                 null,
    //                 (item: SuperAgents) => item?.superAgent,
    //             )}



    //             {RenderSettingsBox(
    //                 'Follow Up Status',
    //                 'text',
    //                 followUpStatuses,
    //                 selectedFollowUpStatuses,
    //                 null,
    //                 'leadFollowUpStatuses',
    //                 null,
    //                 null,
    //                 (item: LeadFollowUpStatus) => item?.followUpStatus,
    //             )}
    //             {RenderSettingsBox(
    //                 'Campaign Options',
    //                 'text',
    //                 campaignOptions,
    //                 selectedCampaignOptions,
    //                 null,
    //                 'leadCampaignOptions',
    //                 null,
    //                 null,
    //                 (item: LeadCampaign) => item?.campaign,
    //             )}
    //             {RenderSettingsBox(
    //                 'Preferred Stream',
    //                 'text',
    //                 prefferedStreams,
    //                 selectedPrefferedStreams,
    //                 null,
    //                 'leadPrefferedStreams',
    //                 null,
    //                 null,
    //                 (item: PreferredStream) => item?.preferredStream,
    //             )}


    //             {RenderSettingsBox(
    //                 'Destinations',
    //                 'text',
    //                 destinations,
    //                 selectedDestinations,
    //                 null,
    //                 'leadDestinations',
    //                 null,
    //                 null,
    //                 (item: Destination) => item?.name,
    //             )}

    //             {RenderSettingsBox(
    //                 'Intake Month & Year',
    //                 'intake-month-year',
    //                 [],
    //                 selectedIntakeMonth,
    //                 selectedIntakeYear,
    //                 'leadIntakeMonth',
    //                 'leadIntakeYear',
    //                 null,
    //                 (item: any) => item?.toString(),
    //             )}

    //             {/* {RenderSettingsBox(
    //                 'Transferred Date',
    //                 'transferred-date',
    //                 [],
    //                 selectedTransferredDates,
    //                 selectedTransferredEndDates,
    //                 'transferredAt',
    //                 'transferredEndAt',
    //                 () => '', // Empty function since we don't need display text for dates
    //                 undefined // No color function needed
    //             )} */}



    //             {RenderSettingsBox(
    //                 'Created Date',
    //                 'date',
    //                 [],
    //                 selectedStartDate,
    //                 selectedEndDate,
    //                 'createdAtStartDate',
    //                 'createdAtEndDate',
    //                 null,
    //                 (item: leadDateFilter) => item?.startDate,
    //                 undefined,
    //                 // isDateFilterDisabled('created'),
    //             )}
    //             {/* {RenderSettingsBox(
    //                 'Created To',
    //                 'date',
    //                 [],
    //                 selectedEndDate,
    //                 null,
    //                 'createdAtEndDate',
    //                 (item: leadDateFilter) => item?.endDate
    //             )} */}

    //             {/* {RenderSettingsBox(
    //                 'Updated Date',
    //                 'date',
    //                 [],
    //                 selectedUpdatedAtStartDate,
    //                 null,
    //                 'updatedAtStartDate',
    //                 (item: leadUpdatedFilter) => item?.startDate
    //             )} */}

    //             {RenderSettingsBox(
    //                 'Updated Date',
    //                 'date',
    //                 [],
    //                 selectedUpdatedAtStartDate,
    //                 selectedUpdatedAtEndDate,
    //                 'updatedAtStartDate',
    //                 'updatedAtEndDate',
    //                 null,
    //                 (item: leadUpdatedFilter) => item?.startDate,
    //                 undefined,
    //                 // isDateFilterDisabled('updated'),
    //             )}

    //             {RenderSettingsBox(
    //                 'FollowUp Date',
    //                 'date',
    //                 [],
    //                 selectedFollowUpAtStartDate,
    //                 selectedFollowUpAtEndDate,
    //                 'followUpAtStartDate',
    //                 'followUpAtEndDate',
    //                 null,
    //                 (item: leadFollowUpFilter) => item?.startDate,
    //                 undefined,
    //                 // isDateFilterDisabled('followUp'),
    //             )}

    //             {/* {RenderSettingsBox(
    //                 'Updated To',
    //                 'date',
    //                   [],
    //                 selectedUpdatedAtEndDate,
    //                 'updatedAtEndDate',
    //                 (item: leadUpdatedFilter) => item?.endDate
    //             )} */}
    //         </div>
    //     );
    // };



    // Filter button states

    const getDataByReduxKey = (reduxKey: string) => {
        const dataMap: { [key: string]: any } = {
            'leadSources': leadSources,
            'leadSubSources': leadSubSources,
            'leadStages': leadStages,
            'leadSubStages': leadSubStages,
            'leadStates': leadStates,
            'leadStatuses': leadStatuses,
            'leadSubStatuses': leadSubStatuses,
            'leadAdmissionStatuses': admissionStatuses,
            'leadAdmissionSubStatus': admissionSubStatus,
            'leadWelcomeCallByAdmissionTeamStatuses': welcomeCallByAdmissionTeamStatuses,
            'leadVisaProcessingStatuses': visaProcessingStatuses,
            'leadApplicationMovedStatuses': applicationMovedStatuses,
            'leadConditionalOfferLetterStatuses': conditionalofferLetterStatuses,
            'leadUnConditionalOfferLetterStatuses': unConditionalofferLetterStatuses,
            'leadFeePaymentStatuses': feePaymentStatuses,
            'leadFileSubmissionStatuses': fileSubmissionStatuses,
            'departments': departments,
            'leadAssigneeOptions': [{ id: 'all', data: { name: 'All' } }, ...assigneeOptions],
            'leadSubAgentOptions': subAgentOptions,
            'leadSuperAgents': superAgents,
            'leadFollowUpStatuses': followUpStatuses,
            'leadCampaignOptions': campaignOptions,
            'leadPrefferedStreams': prefferedStreams,
            'leadDestinations': destinations,
            'leadIntakeMonth': [],
            'createdAtStartDate': [],
            'updatedAtStartDate': [],
            'followUpAtStartDate': []
        };
        return dataMap[reduxKey] || [];
    };

    // Helper function to get selected values by redux key
    const getSelectedValuesByReduxKey = (reduxKey: string) => {
        const selectedMap: { [key: string]: any } = {
            'leadSources': selectedLeadSources,
            'leadSubSources': selectedLeadSubSources,
            'leadStages': selectedLeadStages,
            'leadSubStages': selectedLeadSubStages,
            'leadStates': selectedLeadStates,
            'leadStatuses': selectedLeadStatuses,
            'leadSubStatuses': selectedLeadSubStatuses,
            'leadAdmissionStatuses': selectedAdmissionStatuses,
            'leadAdmissionSubStatus': selectedAdmissionSubStatus,
            'leadWelcomeCallByAdmissionTeamStatuses': selectedWelcomeCallByAdmissionTeamStatuses,
            'leadVisaProcessingStatuses': selectedVisaProcessingStatuses,
            'leadApplicationMovedStatuses': selectedApplicationMovedStatuses,
            'leadConditionalOfferLetterStatuses': selectedConditionalOfferLetterStatuses,
            'leadUnConditionalOfferLetterStatuses': selectedUnConditionalOfferLetterStatuses,
            'leadFeePaymentStatuses': selectedFeePaymentStatuses,
            'leadFileSubmissionStatuses': selectedFileSubmissionStatuses,
            'departments': selectedDepartments,
            'leadAssigneeOptions': selectedAssigneeOptions,
            'leadSubAgentOptions': selectedSubAgentOptions,
            'leadSuperAgents': selectedSuperAgents,
            'leadFollowUpStatuses': selectedFollowUpStatuses,
            'leadCampaignOptions': selectedCampaignOptions,
            'leadPrefferedStreams': selectedPrefferedStreams,
            'leadDestinations': selectedDestinations,
            'leadIntakeMonth': selectedIntakeMonth,
            'leadIntakeYear': selectedIntakeYear,
            'createdAtStartDate': selectedStartDate,
            'createdAtEndDate': selectedEndDate,
            'updatedAtStartDate': selectedUpdatedAtStartDate,
            'updatedAtEndDate': selectedUpdatedAtEndDate,
            'followUpAtStartDate': selectedFollowUpAtStartDate,
            'followUpAtEndDate': selectedFollowUpAtEndDate
        };
        return selectedMap[reduxKey];
    };

    // Helper function to create display text function
    const createDisplayTextFunction = (path: string) => {
        return (item: any) => {
            if (path === 'toString') return item?.toString();
            return item?.[path];
        };
    };

    // Helper function to create color function
    const createColorFunction = (path?: string) => {
        if (!path) return undefined;
        return (item: any) => item?.[path];
    };

    // Helper function to check if sub-filter should be shown
    const shouldShowSubFilter = (config: any) => {
        if (!config.hasSubFilter) return false;

        const parentSelectedValues = getSelectedValuesByReduxKey(config.reduxKey1);
        const subFilterData = getDataByReduxKey(config.subFilterKey!);

        if (config.id === 'leadSources') {
            return selectedLeadSources && leadSubSources.length > 0 &&
                leadSubSources.some(subSource => subSource?.data?.sourceId === selectedLeadSources[0]);
        } else if (config.id === 'leadStages') {
            return selectedLeadStages && leadSubStages.length > 0 &&
                leadSubStages.some(subStage => subStage?.data?.stageId === selectedLeadStages[0]);
        } else if (config.id === 'leadStatuses') {
            return selectedLeadStatuses && leadSubStatuses.length > 0 &&
                leadSubStatuses.some(subStatus => subStatus?.data?.statusId === selectedLeadStatuses[0]);
        } else if (config.id === 'leadAdmissionStatuses') {
            return selectedAdmissionStatuses && admissionSubStatus.length > 0 &&
                admissionSubStatus.some(subStatus => subStatus?.data?.admissionStatusId === selectedAdmissionStatuses[0]);
        }

        return false;
    };

    // Helper function to filter sub-data
    const getFilteredSubData = (config: any) => {
        if (config.id === 'leadSubSources') {
            return leadSubSources.filter(subSource => selectedLeadSources.includes(subSource?.data?.sourceId));
        } else if (config.id === 'leadSubStages') {
            return leadSubStages.filter(subStage => selectedLeadStages.includes(subStage?.data?.stageId));
        } else if (config.id === 'leadSubStatuses') {
            return leadSubStatuses.filter(subStatus => selectedLeadStatuses.includes(subStatus?.data?.statusId));
        } else if (config.id === 'leadAdmissionSubStatus') {
            return admissionSubStatus.filter(subStatus => selectedAdmissionStatuses.includes(subStatus?.data?.admissionStatusId));
        }

        return getDataByReduxKey(config.reduxKey1);
    };

    // Helper function to check if container should have border
    const shouldHaveBorder = (config: any) => {
        if (config.id === 'leadSources') {
            return leadSubSources.some(subSource => subSource?.data?.sourceId === selectedLeadSources[0]);
        } else if (config.id === 'leadStages') {
            return leadSubStages.some(subStage => subStage?.data?.stageId === selectedLeadStages[0]);
        } else if (config.id === 'leadStatuses') {
            return leadSubStatuses.some(subStatus => subStatus?.data?.statusId === selectedLeadStatuses[0]);
        } else if (config.id === 'leadAdmissionStatuses') {
            return admissionSubStatus.some(subStatus => subStatus?.data?.admissionStatusId === selectedAdmissionStatuses[0]);
        }

        return false;
    };

    interface SettingsBoxesLayoutProps {
        selectedFilters: FilterConfig[];
        // Add your existing props here
        currentScreen?: string;
        // All your data props
        leadSources?: any[];
        selectedLeadSources?: any[];
        leadSubSources?: any[];
        selectedLeadSubSources?: any[];
        leadStages?: any[];
        selectedLeadStages?: any[];
        leadSubStages?: any[];
        selectedLeadSubStages?: any[];
        leadStates?: any[];
        selectedLeadStates?: any[];
        leadStatuses?: any[];
        selectedLeadStatuses?: any[];
        leadSubStatuses?: any[];
        selectedLeadSubStatuses?: any[];
        admissionStatuses?: any[];
        selectedAdmissionStatuses?: any[];
        admissionSubStatus?: any[];
        selectedAdmissionSubStatus?: any[];
        welcomeCallByAdmissionTeamStatuses?: any[];
        selectedWelcomeCallByAdmissionTeamStatuses?: any[];
        visaProcessingStatuses?: any[];
        selectedVisaProcessingStatuses?: any[];
        applicationMovedStatuses?: any[];
        selectedApplicationMovedStatuses?: any[];
        conditionalofferLetterStatuses?: any[];
        selectedConditionalOfferLetterStatuses?: any[];
        unConditionalofferLetterStatuses?: any[];
        selectedUnConditionalOfferLetterStatuses?: any[];
        feePaymentStatuses?: any[];
        selectedFeePaymentStatuses?: any[];
        fileSubmissionStatuses?: any[];
        selectedFileSubmissionStatuses?: any[];
        departments?: any[];
        selectedDepartments?: any[];
        assigneeOptions?: any[];
        selectedAssigneeOptions?: any[];
        subAgentOptions?: any[];
        selectedSubAgentOptions?: any[];
        superAgents?: any[];
        selectedSuperAgents?: any[];
        followUpStatuses?: any[];
        selectedFollowUpStatuses?: any[];
        campaignOptions?: any[];
        selectedCampaignOptions?: any[];
        prefferedStreams?: any[];
        selectedPrefferedStreams?: any[];
        destinations?: any[];
        selectedDestinations?: any[];
        selectedIntakeMonth?: any;
        selectedIntakeYear?: any;
        selectedStartDate?: any;
        selectedEndDate?: any;
        selectedUpdatedAtStartDate?: any;
        selectedUpdatedAtEndDate?: any;
        selectedFollowUpAtStartDate?: any;
        selectedFollowUpAtEndDate?: any;
        // Add your RenderSettingsBox function
        RenderSettingsBox: (
            title: string,
            type: string,
            items: DocumentWithId[],
            selectedItems: string[],
            selectedItems2: string[] | null,
            reduxKey1: string,
            reduxKey2: string | null,
            customColor: string | null,
            getDisplayText: (item: any) => string,
            getColor?: (item: any) => string | undefined,
            disabled?: boolean
        ) => React.ReactNode;
    }

    const SettingsBoxesLayout: React.FC<SettingsBoxesLayoutProps> = ({
        selectedFilters,
        currentScreen,
        leadSources,
        selectedLeadSources,
        leadSubSources,
        selectedLeadSubSources,
        leadStages,
        selectedLeadStages,
        leadSubStages,
        selectedLeadSubStages,
        leadStates,
        selectedLeadStates,
        leadStatuses,
        selectedLeadStatuses,
        leadSubStatuses,
        selectedLeadSubStatuses,
        admissionStatuses,
        selectedAdmissionStatuses,
        admissionSubStatus,
        selectedAdmissionSubStatus,
        welcomeCallByAdmissionTeamStatuses,
        selectedWelcomeCallByAdmissionTeamStatuses,
        visaProcessingStatuses,
        selectedVisaProcessingStatuses,
        applicationMovedStatuses,
        selectedApplicationMovedStatuses,
        conditionalofferLetterStatuses,
        selectedConditionalOfferLetterStatuses,
        unConditionalofferLetterStatuses,
        selectedUnConditionalOfferLetterStatuses,
        feePaymentStatuses,
        selectedFeePaymentStatuses,
        fileSubmissionStatuses,
        selectedFileSubmissionStatuses,
        departments,
        selectedDepartments,
        assigneeOptions,
        selectedAssigneeOptions,
        subAgentOptions,
        selectedSubAgentOptions,
        superAgents,
        selectedSuperAgents,
        followUpStatuses,
        selectedFollowUpStatuses,
        campaignOptions,
        selectedCampaignOptions,
        prefferedStreams,
        selectedPrefferedStreams,
        destinations,
        selectedDestinations,
        selectedIntakeMonth,
        selectedIntakeYear,
        selectedStartDate,
        selectedEndDate,
        selectedUpdatedAtStartDate,
        selectedUpdatedAtEndDate,
        selectedFollowUpAtStartDate,
        selectedFollowUpAtEndDate,
        RenderSettingsBox
    }) => {

        // Helper function to get data by redux key
        const getDataByReduxKey = (reduxKey: string) => {
            const dataMap: { [key: string]: any } = {
                'leadSources': leadSources || [],
                'leadSubSources': leadSubSources || [],
                'leadStages': leadStages || [],
                'leadSubStages': leadSubStages || [],
                'leadStates': leadStates || [],
                'leadStatuses': leadStatuses || [],
                'leadSubStatuses': leadSubStatuses || [],
                'leadAdmissionStatuses': admissionStatuses || [],
                'leadAdmissionSubStatus': admissionSubStatus || [],
                'leadWelcomeCallByAdmissionTeamStatuses': welcomeCallByAdmissionTeamStatuses || [],
                'leadVisaProcessingStatuses': visaProcessingStatuses || [],
                'leadApplicationMovedStatuses': applicationMovedStatuses || [],
                'leadConditionalOfferLetterStatuses': conditionalofferLetterStatuses || [],
                'leadUnConditionalOfferLetterStatuses': unConditionalofferLetterStatuses || [],
                'leadFeePaymentStatuses': feePaymentStatuses || [],
                'leadFileSubmissionStatuses': fileSubmissionStatuses || [],
                'departments': departments || [],
                'leadAssigneeOptions': assigneeOptions ?
                    (assigneeOptions.some((opt: any) => opt.id === 'all') ? assigneeOptions : [{ id: 'all', data: { name: 'All' } }, ...assigneeOptions]) : [],
                'leadSubAgentOptions': subAgentOptions || [],
                'leadSuperAgents': superAgents || [],
                'leadFollowUpStatuses': followUpStatuses || [],
                'leadCampaignOptions': campaignOptions || [],
                'leadPrefferedStreams': prefferedStreams || [],
                'leadDestinations': destinations || [],
                'leadIntakeMonth': [],
                'leadIntakeYear': [],
                'createdAtStartDate': [],
                'createdAtEndDate': [],
                'updatedAtStartDate': [],
                'updatedAtEndDate': [],
                'followUpAtStartDate': [],
                'followUpAtEndDate': []
            };
            return dataMap[reduxKey] || [];
        };

        // Helper function to get selected values by redux key
        const getSelectedValuesByReduxKey = (reduxKey: string) => {
            const selectedMap: { [key: string]: any } = {
                'leadSources': selectedLeadSources,
                'leadSubSources': selectedLeadSubSources,
                'leadStages': selectedLeadStages,
                'leadSubStages': selectedLeadSubStages,
                'leadStates': selectedLeadStates,
                'leadStatuses': selectedLeadStatuses,
                'leadSubStatuses': selectedLeadSubStatuses,
                'leadAdmissionStatuses': selectedAdmissionStatuses,
                'leadAdmissionSubStatus': selectedAdmissionSubStatus,
                'leadWelcomeCallByAdmissionTeamStatuses': selectedWelcomeCallByAdmissionTeamStatuses,
                'leadVisaProcessingStatuses': selectedVisaProcessingStatuses,
                'leadApplicationMovedStatuses': selectedApplicationMovedStatuses,
                'leadConditionalOfferLetterStatuses': selectedConditionalOfferLetterStatuses,
                'leadUnConditionalOfferLetterStatuses': selectedUnConditionalOfferLetterStatuses,
                'leadFeePaymentStatuses': selectedFeePaymentStatuses,
                'leadFileSubmissionStatuses': selectedFileSubmissionStatuses,
                'departments': selectedDepartments,
                'leadAssigneeOptions': selectedAssigneeOptions,
                'leadSubAgentOptions': selectedSubAgentOptions,
                'leadSuperAgents': selectedSuperAgents,
                'leadFollowUpStatuses': selectedFollowUpStatuses,
                'leadCampaignOptions': selectedCampaignOptions,
                'leadPrefferedStreams': selectedPrefferedStreams,
                'leadDestinations': selectedDestinations,
                'leadIntakeMonth': selectedIntakeMonth,
                'leadIntakeYear': selectedIntakeYear,
                'createdAtStartDate': selectedStartDate,
                'createdAtEndDate': selectedEndDate,
                'updatedAtStartDate': selectedUpdatedAtStartDate,
                'updatedAtEndDate': selectedUpdatedAtEndDate,
                'followUpAtStartDate': selectedFollowUpAtStartDate,
                'followUpAtEndDate': selectedFollowUpAtEndDate
            };
            return selectedMap[reduxKey];
        };

        // Helper function to create display text function
        const createDisplayTextFunction = (path: string) => {
            return (item: any) => {
                if (path === 'toString') return item?.toString();
                return item?.[path];
            };
        };

        // Helper function to create color function
        const createColorFunction = (path?: string) => {
            if (!path) return undefined;
            return (item: any) => item?.[path];
        };

        // Helper function to check if sub-filter should be shown
        const shouldShowSubFilter = (config: FilterConfig) => {
            if (!config.hasSubFilter) return false;

            // Use the exact logic from the original implementation
            if (config.id === 'leadSources' || config.reduxKey1 === 'leadSources') {
                return selectedLeadSources && leadSubSources && leadSubSources.length > 0 &&
                    leadSubSources.some(subSource => subSource?.data?.sourceId === selectedLeadSources[0]);
            } else if (config.id === 'leadStages' || config.reduxKey1 === 'leadStages') {
                return selectedLeadStages && leadSubStages && leadSubStages.length > 0 &&
                    leadSubStages.some(subStage => subStage?.data?.stageId === selectedLeadStages[0]);
            } else if (config.id === 'leadStatuses' || config.reduxKey1 === 'leadStatuses') {
                return selectedLeadStatuses && leadSubStatuses && leadSubStatuses.length > 0 &&
                    leadSubStatuses.some(subStatus => subStatus?.data?.statusId === selectedLeadStatuses[0]);
            } else if (config.id === 'leadAdmissionStatuses' || config.reduxKey1 === 'leadAdmissionStatuses') {
                return selectedAdmissionStatuses && admissionSubStatus && admissionSubStatus.length > 0 &&
                    admissionSubStatus.some(subStatus => subStatus?.data?.admissionStatusId === selectedAdmissionStatuses[0]);
            }

            return false;
        };

        // Helper function to filter sub-data
        const getFilteredSubData = (config: FilterConfig) => {
            if (config.reduxKey1 === 'leadSubSources') {
                return (leadSubSources || []).filter((subSource: any) =>
                    selectedLeadSources?.includes(subSource?.data?.sourceId));
            } else if (config.reduxKey1 === 'leadSubStages') {
                return (leadSubStages || []).filter((subStage: any) =>
                    selectedLeadStages?.includes(subStage?.data?.stageId));
            } else if (config.reduxKey1 === 'leadSubStatuses') {
                return (leadSubStatuses || []).filter((subStatus: any) =>
                    selectedLeadStatuses?.includes(subStatus?.data?.statusId));
            } else if (config.reduxKey1 === 'leadAdmissionSubStatus') {
                return (admissionSubStatus || []).filter((subStatus: any) =>
                    selectedAdmissionStatuses?.includes(subStatus?.data?.admissionStatusId));
            }

            return getDataByReduxKey(config.reduxKey1);
        };

        // Helper function to check if container should have border
        const shouldHaveBorder = (config: FilterConfig) => {
            return shouldShowSubFilter(config);
        };

        // Check if filter should be active based on conditions
        const isFilterActive = (config: FilterConfig) => {
            if (!config.isActive) return false;

            // Handle screen-specific conditions
            if (config.reduxKey1 === 'departments' && currentScreen === "Staff") return false;
            if (config.reduxKey1 === 'leadAssigneeOptions' && currentScreen === "Staff") return false;

            return true;
        };

        const renderFilterBox = (config: FilterConfig) => {
            // Skip sub-filters as they are handled within parent filters
            if (config.parentFilterId) return null;

            // Skip inactive filters
            if (!isFilterActive(config)) return null;

            const data = getDataByReduxKey(config.reduxKey1);
            const selectedValues1 = getSelectedValuesByReduxKey(config.reduxKey1);
            const selectedValues2 = config.reduxKey2 ? getSelectedValuesByReduxKey(config.reduxKey2) : null;
            const displayTextFn = createDisplayTextFunction(config.getDisplayTextPath);
            const colorFn = createColorFunction(config.getColorPath);

            const containerClass = `col-span-4 md:col-span-${config.gridSpan || 1} ${shouldHaveBorder(config) ? 'border border-green-200 rounded-md p-2 ' : ''
                }`;

            return (
                <div key={config.id} className={containerClass}>
                    {RenderSettingsBox(
                        config.title,
                        config.type,
                        data,
                        selectedValues1,
                        selectedValues2,
                        config.reduxKey1,
                        config.reduxKey2 || null,
                        config.customColor || null,
                        displayTextFn,
                        colorFn,
                    )}

                    {/* Render sub-filter if applicable */}
                    {config.hasSubFilter && shouldShowSubFilter(config) && (() => {
                        let subData: any[] = [];
                        let subSelectedValues: any[] = [];
                        let subTitle = '';
                        let subReduxKey = '';
                        let subDisplayTextFn = (item: any) => '';

                        // Define sub-filter based on parent filter
                        if (config.id === 'leadSources' || config.reduxKey1 === 'leadSources') {
                            subData = (leadSubSources || []).filter((subSource: any) =>
                                selectedLeadSources?.includes(subSource?.data?.sourceId)) || [];
                            subSelectedValues = selectedLeadSubSources || [];
                            subTitle = 'Sub Sources';
                            subReduxKey = 'leadSubSources';
                            subDisplayTextFn = (item: any) => item?.subSource;
                        } else if (config.id === 'leadStages' || config.reduxKey1 === 'leadStages') {
                            subData = (leadSubStages || []).filter((subStage: any) =>
                                selectedLeadStages?.includes(subStage?.data?.stageId)) || [];
                            subSelectedValues = selectedLeadSubStages || [];
                            subTitle = 'Sub Stages';
                            subReduxKey = 'leadSubStages';
                            subDisplayTextFn = (item: any) => item?.subStage;
                        } else if (config.id === 'leadStatuses' || config.reduxKey1 === 'leadStatuses') {
                            subData = (leadSubStatuses || []).filter((subStatus: any) =>
                                selectedLeadStatuses?.includes(subStatus?.data?.statusId)) || [];
                            subSelectedValues = selectedLeadSubStatuses || [];
                            subTitle = 'Sub Statuses';
                            subReduxKey = 'leadSubStatuses';
                            subDisplayTextFn = (item: any) => item?.subStatus;
                        } else if (config.id === 'leadAdmissionStatuses' || config.reduxKey1 === 'leadAdmissionStatuses') {
                            subData = (admissionSubStatus || []).filter((subStatus: any) =>
                                selectedAdmissionStatuses?.includes(subStatus?.data?.admissionStatusId)) || [];
                            subSelectedValues = selectedAdmissionSubStatus || [];
                            subTitle = 'Admission Sub Status';
                            subReduxKey = 'leadAdmissionSubStatus';
                            subDisplayTextFn = (item: any) => item?.subAdmissionStatus;
                        }

                        return (
                            <>
                                {RenderSettingsBox(
                                    subTitle,
                                    'text',
                                    subData,
                                    subSelectedValues,
                                    null,
                                    subReduxKey,
                                    null,
                                    '#F3C623',
                                    subDisplayTextFn,
                                    undefined,
                                )}
                            </>
                        );
                    })()}
                </div>
            );
        };

        // Filter and sort configs
        const activeConfigs = selectedFilters
            .filter(config => isFilterActive(config) && !config.parentFilterId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));

        return (
            <div className="grid grid-cols-4 gap-3 p-2">
                {activeConfigs.map(config => renderFilterBox(config))}
            </div>
        );
    };

    const [filterButtonActive, setFilterButtonActive] = useState(false);
    const [filterButtonKey, setFilterButtonKey] = useState<string | null>(null);

    // function to handle filter button 
    const handleFilterButtonClick = async (id: string, button: FilterButton) => {
        setFilterButtonActive(true);
        setFilterButtonKey(id);


        await dispatch(clearLeadManagementFilterProps());


        // Create mapping between filter button keys and redux state keys
        const keyMappings: Record<keyof FilterButton, string> = {
            sourceIds: 'leadSources',
            stageIds: 'leadStages',
            stateIds: 'leadStates',
            statusIds: 'leadStatuses',
            admissionStatusIds: 'leadAdmissionStatuses',
            welcomeCallStatusIds: 'leadWelcomeCallByAdmissionTeamStatuses',
            visaProcessingStatusIds: 'leadVisaProcessingStatuses',
            applicationStatusIds: 'leadApplicationMovedStatuses',
            conditionalOfferLetterStatusIds: 'leadConditionalOfferLetterStatuses',
            unconditionalOfferLetterStatusIds: 'leadUnConditionalOfferLetterStatuses',
            feePaymentStatusIds: 'leadFeePaymentStatuses',
            fileSubmissionStatusIds: 'leadFileSubmissionStatuses',
            preferredStreamIds: 'leadPrefferedStreams', // Note: keeping the typo from redux state
            assigneeIds: 'leadAssigneeOptions',
            subAgentIds: 'leadSubAgentOptions',
            superAgentIds: 'leadSuperAgents',
            followUpIds: 'leadFollowUpStatuses',
            campaignIds: 'leadCampaignOptions',
            destinationIds: 'leadDestinations',
            followUpStatus: 'leadFollowUpStatuses',
        };

        // Batch dispatch operations
        const dispatchActions = Object.entries(button)
            .filter(([_, value]) => Array.isArray(value) && value.length > 0)
            .map(([key, value]) => ({
                key: keyMappings[key as keyof FilterButton],
                value
            }))
            .filter(action => action.key); // Remove any unmapped keys

        // Dispatch all actions
        dispatchActions.forEach(action => {
            dispatch(setLeadProperty(action));
        });

        setCurrentPage(1);
        clearPaginationState();
        savePaginationState(1, null);
    };

    // clear button to current page 1
    const clearFillterButton = () => {
        dispatch(clearLeadManagementFilterProps());
        setFilterButtonActive(false);
        setFilterButtonKey('');
        setIsCollapsedFilter(false);
        setCurrentPage(1);
        clearPaginationState();
        savePaginationState(1, null);
    }

    useEffect(() => {
        const fetchData = async () => {
            const startTime = new Date().getTime();
            try {
                // Fetch admission statuses
                const admissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionStatus`));
                setAdmissionStatuses(admissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadAdmissionStatus
                }))
                    .sort((a, b) => {
                        return a.data.level - b.data.level;
                    })
                );

                // Fetch lead sources
                const leadSourcesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`));
                setLeadSources(leadSourcesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadSource
                }))
                    .sort((a, b) => a.data.source.localeCompare(b.data.source))
                );

                // Fetch lead stages
                const leadStagesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`));
                setLeadStages(leadStagesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadStage
                }))
                    .sort((a, b) => {
                        return a.data.priority - b.data.priority;
                    })
                );

                // Fetch lead states
                const leadStatesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStates`));
                setLeadStates(leadStatesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadState
                }))
                    .sort((a, b) => a.data.state.localeCompare(b.data.state))
                );

                // Fetch lead statuses
                const leadStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`));
                setLeadStatuses(leadStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadStatus
                }))
                    .sort((a, b) => {
                        return a.data.priority - b.data.priority;
                    })
                );

                // Fetch welcome call statuses
                const welcomeCallByAdmissionTeamStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadWelcomeCallByAdmissionTeamStatus`));
                setWelcomeCallByAdmissionTeamStatuses(welcomeCallByAdmissionTeamStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadWelcomeCallByAdmissionTeamStatus
                }))
                    .sort((a, b) => a.data.welcomeCallByAdmissionTeamStatus.localeCompare(b.data.welcomeCallByAdmissionTeamStatus))
                );

                // Fetch visa processing statuses
                const visaProcessingStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadVisaFileProcessingStatus`));
                setVisaProcessingStatuses(visaProcessingStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadVisaFileProcessingStatus
                }))
                    .sort((a, b) => a.data.visaFileProcessingStatus.localeCompare(b.data.visaFileProcessingStatus))
                );

                // Fetch application moved statuses
                const applicationMovedStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadApplicationMovedStatus`));
                setApplicationMovedStatuses(applicationMovedStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadApplicationMovedStatus
                }))
                    .sort((a, b) => a.data.applicationMovedStatus.localeCompare(b.data.applicationMovedStatus))
                );

                // Fetch offer letter statuses
                const offerLetterStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadOfferLetterStatus`));
                setConditionalOfferLetterStatuses(offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadOfferLetterStatus
                }))
                    .sort((a, b) => a.data.offerLetterStatus.localeCompare(b.data.offerLetterStatus))
                );

                setUnConditionalOfferLetterStatuses(offerLetterStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadOfferLetterStatus
                }))
                    .sort((a, b) => a.data.offerLetterStatus.localeCompare(b.data.offerLetterStatus))
                );

                // Fetch fee payment statuses
                const feePaymentStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadFeePaymentStatus`));
                setFeePaymentStatuses(feePaymentStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadFeePaymentStatus
                }))
                    .sort((a, b) => a.data.feePaymentStatus.localeCompare(b.data.feePaymentStatus))
                );

                // Fetch file submission statuses
                const fileSubmissionStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadFileSubmissionStatus`));
                setFileSubmissionStatuses(fileSubmissionStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadFileSubmissionStatus
                }))
                    .sort((a, b) => a.data.fileSubmissionStatus.localeCompare(b.data.fileSubmissionStatus))
                );

                // fetch campaign options where isActive is true
                const campaignOptionsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadCampaigns`));
                setCampaignOptions(campaignOptionsSnapshot.docs.filter(doc => doc.data().isActive).map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadCampaign
                }))
                    .sort((a, b) => a.data.campaign.localeCompare(b.data.campaign))
                );


                // fetch preffered stream
                const prefferedStreamsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/preferredStreams`));
                setPrefferedStreams(prefferedStreamsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as PreferredStream
                }))
                    .sort((a, b) => a.data.preferredStream.localeCompare(b.data.preferredStream))
                );

                // fetch Super Agent
                const superAgentSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/superAgents`));
                setSuperAgents(superAgentSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as SuperAgents
                })))

                // fetch Admission SubStatus
                const admissionSubStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadAdmissionSubStatus`));
                setAdmissionSubStatus(admissionSubStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as AdmissionSubStatus
                })));

                // fetch leadSubSources
                const leadSubSourcesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSubSources`));
                setLeadSubSources(leadSubSourcesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadSubSources
                })));

                // fetch leadSubStages
                const leadSubStagesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSubStages`));
                setLeadSubStages(leadSubStagesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadSubStages
                })));

                // fetch leadSubStatuses
                const leadSubStatusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSubStatuses`));
                setLeadSubStatuses(leadSubStatusesSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as LeadSubStatuses
                })));

                //  ftetch destination
                // In your fetchData function, update the destinations fetch:
                const destinationsSnapshot = await getDocs(collection(db, 'countries'));
                setDestinations(destinationsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as Destination
                })));

                // Fetch departments and filter out ones with no assignees
                const departmentsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/departments`));
                const departmentsWithUsers = [];

                for (const deptDoc of departmentsSnapshot.docs) {
                    // Query to check if there are any users in this department
                    const usersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", userData?.companyId),
                        where("departmentId", "==", deptDoc.id),
                        limit(1) // We only need to know if there's at least one user
                    );

                    const usersSnapshot = await getDocs(usersQuery);

                    // Only include department if it has at least one user
                    if (!usersSnapshot.empty) {
                        departmentsWithUsers.push({
                            id: deptDoc.id,
                            data: { department: deptDoc.data().name || deptDoc.data().department || 'Unknown Department' }
                        });
                    }
                }

                setDepartments(departmentsWithUsers);


                // fetch Filter Buttons
                if (userData?.filterButtons && userData.filterButtons.length > 0) {
                    // Create queries for each filter button ID
                    const filterButtonQueries = userData.filterButtons.map(filterId =>
                        doc(db, `companies/${userData?.companyId}/filterButtons`, filterId)
                    );

                    // Fetch all specified filter buttons
                    const filterButtonsPromises = filterButtonQueries.map(docRef => getDoc(docRef));
                    const filterButtonsSnapshots = await Promise.all(filterButtonsPromises);

                    // Process the results, filtering out non-existent documents
                    const filterButtonsData = filterButtonsSnapshots
                        .filter(snapshot => snapshot.exists())
                        .map(snapshot => ({
                            id: snapshot.id,
                            ...snapshot.data()
                        }) as filterButtons)
                        .sort((a, b) => a.name.localeCompare(b.name));

                    setFilterButtons(filterButtonsData);
                } else {
                    // If no filter button IDs specified, set empty array
                    setFilterButtons([]);
                }

                // We'll load destinations/countries on-demand when searching, not all at once
                // Initialize with an empty array instead of fetching all ~200 countries
                // setDestinations([]);

                // Set manager team options for manager role
                if (isManager && currentScreen === 'Manager') {
                    const managerUsersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", userData?.companyId),
                        where("managerIds", "array-contains", currentUser?.uid)
                    );

                    const users = await getDocs(managerUsersQuery);
                    const managerTeam = users.docs.map(doc => doc.id);
                    if (managerTeam.length === 0) {
                        // toast message
                        setToastMessage("No users found in your team");
                        setShowToast(true);
                    }
                    setManagerTeamOptions(managerTeam);
                } else if (isStaff && currentScreen === 'Staff') {
                    setSelectedAssigneeOptions([currentUser ? currentUser?.uid : '']);
                    // Also update Redux state for consistency
                    if (currentUser?.uid) {
                        dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: [currentUser.uid] }));
                    }
                }

                // Fetch sub agent options
                const subAgentOptionsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/subAgents`));
                const subAgentOptions = subAgentOptionsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    data: doc.data() as SubAgent
                }));
                // console.log('subAgentOptions', subAgentOptions);
                setSubAgentOptions(subAgentOptions);

                // Set first effect done after data is loaded
                console.log("first effect done");
                setFirstEffectDone(true);

                // console how much time taken to print the first effect done using a counter
                const endTime = new Date().getTime();
                const timeTaken = endTime - startTime;
                console.log("Time taken to print first effect done: ", timeTaken, "ms");
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, [userData?.companyId,]);

    // Get filtered actions for dropdown
    const getFilteredActions = () => {
        return Actions.filter(item =>
            item.name.toLowerCase().includes(actionSearchQuery.toLowerCase())
        );
    };

    // Actions array definition
    const Actions = Object.values(departmentData?.bulkActionPermission || {}).filter(item => item.active && item.view);

    const handleAction = async (actionId: number) => {
        if (selectedLeadsForAction.length === 0) {
            setToastMessage("Please select leads to perform this action");
            setShowToast(true);
            return;
        }

        setSelectedAction(actionId);
        setActionDropdownOpen(false);
        sessionStorage.setItem('currentPage', JSON.stringify(1));
    }

    // functon to get action permission
    const getActionPermission = (action: string) => {
        const actions = departmentData?.actionPermission || [];
        const actionPermission = Object.values(actions).find(item => item.name === action);
        return actionPermission ? actionPermission.allowed : false;
    }


    const handleSelectAll = () => {
        if (selectCurrentPage) {
            // Unselect all leads on the current page
            const currentPageLeadIds = leadsData.map(lead => lead.id);
            const remainingSelectedLeads = selectedLeadsForAction.filter(id => !currentPageLeadIds.includes(id));
            setSelectedLeadsForAction(remainingSelectedLeads);
            setSelectCurrentPage(false);
        } else {
            // Select all leads on the current page, but check maximum limit
            const currentPageLeadIds = leadsData.map(lead => lead.id);
            const existingSelections = selectedLeadsForAction.filter(id => !currentPageLeadIds.includes(id));
            const newSelections = [...existingSelections, ...currentPageLeadIds];

            if (newSelections.length > MAX_SELECTABLE_LEADS) {
                setToastMessage(`Failed: Selection limit exceeded. Maximum: ${MAX_SELECTABLE_LEADS}, attempting: ${newSelections.length}`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                return;
            }

            setSelectedLeadsForAction(newSelections);
            setSelectCurrentPage(true);
        }
    };
    console.log("selectedLeadsForAction", selectedLeadsForAction);


    const handleLeadSelection = (leadId: string) => {
        // Check if the lead is already selected
        if (selectedLeadsForAction.includes(leadId)) {
            // If it was selected, remove it
            setSelectedLeadsForAction(selectedLeadsForAction.filter(id => id !== leadId));

            // If we're removing a lead from the current page, update selectCurrentPage state
            if (leadsData.some(lead => lead.id === leadId)) {
                setSelectCurrentPage(false);
            }
        } else {
            // If it wasn't selected, check the maximum limit before adding
            if (selectedLeadsForAction.length >= MAX_SELECTABLE_LEADS) {
                setToastMessage(`Failed: Selection limit reached. Maximum of ${MAX_SELECTABLE_LEADS} leads allowed.`);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 3000);
                return;
            }

            // Add the lead to selected items
            setSelectedLeadsForAction([...selectedLeadsForAction, leadId]);

            // Check if all leads on the current page are now selected
            const allCurrentPageLeadsSelected = leadsData.every(lead =>
                lead.id === leadId || selectedLeadsForAction.includes(lead.id)
            );

            if (allCurrentPageLeadsSelected) {
                setSelectCurrentPage(true);
            }
        }
    }

    const handleOnClose = async (message: string) => {
        setSelectCurrentPage(false);
        setCurrentPage(1);
        setSelectedLeadsForAction([]);
        setSelectedAction(null);
        fetchPageData(currentPage);
        if (message !== "") {
            setToastMessage(message);
            setShowToast(true);
        }
    }

    useEffect(() => {
        const fetchCompanyId = async () => {
            const user = auth.currentUser;
            if (user) {
                const userDocRef = doc(db, "users", user.uid);
                const userDocSnap = await getDoc(userDocRef);
                if (userDocSnap.exists()) {
                    const userData = userDocSnap.data();
                    setCompanyId(userData.companyId);
                    setCurrentUserPipelines(userData.pipelineIds);

                    // fetch company data
                    const companyDocRef = doc(db, "companies", userData.companyId);
                    const companyDocSnap = await getDoc(companyDocRef);
                    if (companyDocSnap.exists()) {
                        const companyData = companyDocSnap.data();
                        setCompanyCloudTelephony(companyData.cloudTelephony);
                    } else {
                        console.error("Company document not found");
                    }
                } else {
                    console.error("User document not found");
                }
            }
        };
        fetchCompanyId();
    }, [auth]);

    useEffect(() => {
        const fetchDropdownData = async () => {
            try {
                const statesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStates`));
                const statusesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStatuses`));
                const stagesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadStages`));
                const sourcesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSources`));
                const subSourcesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadSubSources`));
                const campaignsSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadCampaigns`));
                const pipelinesSnapshot = await getDocs(collection(db, `companies/${userData?.companyId}/leadPipelines`));
                const fetchedStages = stagesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    stage: doc.data().stage,
                }));
                const fetchedStates = statesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    state: doc.data().state,
                }));
                const fetchedStatuses = statusesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    status: doc.data().status,
                }));
                const fetchedSources = sourcesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    source: doc.data().source,
                }));
                const fetchedSubSources = subSourcesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    subSource: doc.data().subSource,
                    sourceId: doc.data().sourceId,
                }));
                const fetchedCampaigns = campaignsSnapshot.docs.filter(doc => doc.data().isActive).map((doc) => ({
                    id: doc.id,
                    campaign: doc.data().campaign,
                }));
                const fetchedPipelines = pipelinesSnapshot.docs.map((doc) => ({
                    id: doc.id,
                    pipelineName: doc.data().pipelineName,
                }));

                setSources(fetchedSources);
                setSubSources(fetchedSubSources);
                setStates(fetchedStates);
                setStatuses(fetchedStatuses);
                setStages(fetchedStages);
                setCampaigns(fetchedCampaigns);
                setPipelines(fetchedPipelines);


            } catch (error) {
                console.error("Error fetching dropdown data:", error);
            }
        };
        fetchDropdownData();
    }, []);

    const fetchAssignedToOptions = useCallback(async () => {
        if (!companyId) return;
        console.log("fetchAssignedToOptions called with selectedDepartments:", selectedDepartments);
        try {
            let usersQuery = query(
                collection(db, "users"),
                where("companyId", "==", companyId)
            );

            // Add department filter if departments are selected
            if (selectedDepartments.length > 0) {
                console.log("Filtering users by departments:", selectedDepartments);
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where("departmentId", "in", selectedDepartments)
                );
            }

            if (isAdmin && currentScreen === 'Admin') {
                if (selectedDepartments.length > 0) {
                    usersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", companyId),
                        where("departmentId", "in", selectedDepartments)
                    );
                } else {
                    usersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", companyId),
                    );
                }
            } else if (isManager && currentScreen === 'Manager') {
                if (selectedDepartments.length > 0) {
                    usersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", companyId),
                        where("managerIds", "array-contains", currentUser?.uid),
                        where("departmentId", "in", selectedDepartments)
                    );
                } else {
                    usersQuery = query(
                        collection(db, "users"),
                        where("companyId", "==", companyId),
                        where("managerIds", "array-contains", currentUser?.uid)
                    );
                }
            } else if (isStaff && currentScreen === 'Staff') {
                usersQuery = query(
                    collection(db, "users"),
                    where("companyId", "==", companyId),
                    where(documentId(), "==", currentUser?.uid)
                );
                setNewLead({ ...newLead, assignedTo: currentUser?.uid });
            }

            const usersSnapshot = await getDocs(usersQuery);
            let users = usersSnapshot.docs.map((doc) => {
                const data = doc.data();
                return {
                    id: doc.id,
                    data: {
                        name: data.name,
                        lastName: data.lastName || '',
                        email: data.email || '',
                        distribution: data.distribution || 0,
                        isActive: data.isActive !== false, // default to true if undefined
                    }
                };
            });
            // Sort users alphabetically by name (case-insensitive)
            users = users.sort((a, b) => {
                const nameA = (a.data?.name || '').toLowerCase();
                const nameB = (b.data?.name || '').toLowerCase();
                if (nameA < nameB) return -1;
                if (nameA > nameB) return 1;
                return 0;
            });
            console.log(`Found ${users.length} users after department filtering:`, users);
            setAssigneeOptions(users);
            setAssignedToOptions(
                users
                    .filter(user => user.data && user.data.isActive === true) // Ensure user.data exists
                    .map(user => ({
                        id: user.id,
                        name: user.data.name,
                        lastName: user.data.lastName,
                        email: user.data.email,
                        distribution: user.data.distribution
                    }))
            );

            // For staff users, they should always have themselves selected
            if (isStaff && currentScreen === 'Staff' && currentUser?.uid) {
                setSelectedAssigneeOptions([currentUser.uid]);
            }
            // For other roles, don't automatically select assignees when departments change
            // This allows users to manually select assignees from the filtered department options
        }
        catch (error) {
            console.error("Error fetching users:", error);
        }
    }, [companyId, selectedDepartments, isAdmin, isManager, isStaff, currentScreen, currentUser?.uid]);

    useEffect(() => { fetchAssignedToOptions(); }, [currentScreen, companyId, selectedDepartments, fetchAssignedToOptions]);

    // Clear assignee options when departments change
    useEffect(() => {
        // Only clear if departments are selected (not when clearing departments)
        if (selectedDepartments.length > 0) {
            // For staff users, they should always have themselves selected
            if (isStaff && currentScreen === 'Staff' && currentUser?.uid) {
                setSelectedAssigneeOptions([currentUser.uid]);
                dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: [currentUser.uid] }));
            } else {
                // Clear for admin and manager users
                setSelectedAssigneeOptions([]);
                dispatch(setLeadProperty({ key: 'leadAssigneeOptions', value: [] }));
            }
        }
    }, [selectedDepartments, dispatch, isStaff, currentScreen, currentUser?.uid]);


    // Check if number already exists in the database
    // Define Lead interface (add this at the top of your file if not already defined)
    interface Lead {
        id: string;
        name: string;
        number: string;
        email?: string;
        assignedTo: string;
        // Add other properties as needed
        [key: string]: any; // Allow for additional properties
    }

    // Add effect to handle 'All' selection in assignee filter
    useEffect(() => {
        if (selectedAssigneeOptions.includes('all')) {
            // If 'All' is selected, set all assignee IDs except 'all'
            const allIds = assigneeOptions.map(option => option.id).filter(id => id !== 'all');
            setSelectedAssigneeOptions(allIds);
        }
    }, [selectedAssigneeOptions, assigneeOptions]);

    // Real-time phone number validation with debouncing
    useEffect(() => {
        if (!newLead.number || newLead.number.length < 10) {
            setPhoneValidationMessage("");
            setPhoneValidationType("");
            return;
        }

        // Debounce the validation to avoid too many API calls
        const timeoutId = setTimeout(() => {
            validatePhoneRealTime(newLead.number || "");
        }, 500); // 500ms delay

        return () => clearTimeout(timeoutId);
    }, [newLead.number, editingId, companyId]);

    // Check if number already exists in the database and return lead data
    const checkNumberExists = async (number: string, excludeLeadId?: string): Promise<{
        exists: boolean;
        leadData: Lead | null;
        assigneeName?: string;
    }> => {
        if (!companyId || !number) return { exists: false, leadData: null };

        try {
            const leadsRef = collection(db, `companies/${companyId}/leads`);
            let q = query(leadsRef, where("number", "==", number));

            const querySnapshot = await getDocs(q);

            // If we're editing a lead, exclude the current lead from duplicate check
            if (excludeLeadId) {
                const duplicateDocs = querySnapshot.docs.filter(doc => doc.id !== excludeLeadId);
                if (duplicateDocs.length > 0) {
                    const docData = duplicateDocs[0].data();
                    const leadData = { id: duplicateDocs[0].id, ...docData } as Lead;

                    // Fetch assignee name
                    let assigneeName = "Unknown";
                    if (leadData.assignedTo) {
                        try {
                            const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                            if (userDoc.exists()) {
                                assigneeName = userDoc.data().name || "Unknown";
                            }
                        } catch (error) {
                            console.error("Error fetching assignee name:", error);
                        }
                    }

                    return {
                        exists: true,
                        leadData: leadData,
                        assigneeName: assigneeName
                    };
                }
                return { exists: false, leadData: null };
            }

            if (!querySnapshot.empty) {
                const firstDoc = querySnapshot.docs[0];
                const docData = firstDoc.data();
                const leadData = { id: firstDoc.id, ...docData } as Lead;

                // Fetch assignee name
                let assigneeName = "Unknown";
                if (leadData.assignedTo) {
                    try {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            assigneeName = userDoc.data().name || "Unknown";
                        }
                    } catch (error) {
                        console.error("Error fetching assignee name:", error);
                    }
                }

                return {
                    exists: true,
                    leadData: leadData,
                    assigneeName: assigneeName
                };
            }

            return { exists: false, leadData: null };
        } catch (error) {
            console.error("Error checking number existence:", error);
            return { exists: false, leadData: null };
        }
    };

    // Real-time phone number validation function
    const validatePhoneRealTime = async (phoneNumber: string) => {
        // Clear previous validation state
        setPhoneValidationMessage("");
        setPhoneValidationType("");

        if (!phoneNumber || phoneNumber.length < 10) {
            return;
        }

        setIsPhoneValidating(true);

        try {
            // Format phone number for checking
            let formattedNumber = phoneNumber.replace(/\D/g, '');
            if (formattedNumber.length <= 10) {
                setPhoneValidationMessage("Please include country code. Example: +91XXXXXX1234");
                setPhoneValidationType("warning");
                setIsPhoneValidating(false);
                return;
            }

            formattedNumber = "+" + formattedNumber;

            // Check if number exists, excluding current editing lead
            const result = await checkNumberExists(formattedNumber, editingId || undefined);

            if (result.exists && result.leadData) {
                const existingLeadName = result.leadData.name || "Unknown";
                const assigneeName = result.assigneeName || "Unknown";
                setPhoneValidationMessage(`Lead already exists: ${existingLeadName} (Assigned to: ${assigneeName})`);
                setPhoneValidationType("error");
            } else {
                setPhoneValidationMessage("Phone number is available");
                setPhoneValidationType("success");
            }
        } catch (error) {
            console.error("Error validating phone number:", error);
            setPhoneValidationMessage("Error validating phone number");
            setPhoneValidationType("error");
        } finally {
            setIsPhoneValidating(false);
        }
    };

    // Simple validation function for required fields
    const validateRequiredFields = () => {
        // Check required fields
        if (!newLead.name || newLead.name.trim() === '') {
            return 'Name is required';
        }

        if (!newLead.number || newLead.number.trim() === '') {
            return 'Phone number is required';
        }

        if (!newLead.assignedTo || newLead.assignedTo.trim() === '') {
            return 'Assigned To is required';
        }

        if (!newLead.sourceId || newLead.sourceId.trim() === '') {
            return 'Source is required';
        }

        return null; // No errors
    };

    const isValidEmail = (email: string): boolean => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    // Add lead function moved to AddUpdateLeadModal



    const handleEditLead = async (id: string) => {
        const leadToEdit = leadsData.find((lead) => lead.id === id);

        // Find if the lead has any exsisting follow-up
        let followUpText = "";
        let followUpTime = Timestamp.fromDate(new Date());
        const followUpRef = leadToEdit?.activeFollowUpId ? doc(db, `companies/${userData?.companyId}/followUps`, leadToEdit.activeFollowUpId) : null;
        if (followUpRef) {
            const followUpDoc = await getDoc(followUpRef);
            if (followUpDoc.exists()) {
                const followUpData = followUpDoc.data() as FollowUp;
                followUpText = followUpData.followUp || "";
                followUpTime = followUpData.followUpTime || Timestamp.fromDate(new Date());
            } else {
                console.warn("No follow-up found for lead:", id);
            }
        } else {
            console.warn("No active follow-up ID for lead:", id);
        }

        if (leadToEdit) {
            setEditingId(id);
            // Clear phone validation state when editing
            setPhoneValidationMessage('');
            setIsPhoneValidating(false);
            setPhoneValidationType('');
            setNewLead({
                name: leadToEdit.name,
                email: leadToEdit.email,
                number: leadToEdit.number,
                alternateNumber1: leadToEdit.alternateNumber1,
                alternateNumber2: leadToEdit.alternateNumber2,
                assignedTo: leadToEdit.assignedTo,
                sourceId: leadToEdit.sourceId || "",
                subSourceId: leadToEdit.subSourceId || "",
                adminNotes: leadToEdit.adminNotes || "",
                followUp: leadToEdit.followUp,
                createdAt: leadToEdit.createdAt,
                stageId: leadToEdit.stageId,
                stateId: leadToEdit.stateId,
                statusId: leadToEdit.statusId,
                campaignId: leadToEdit.campaignId,
                hasActiveFollowUp: leadToEdit.hasActiveFollowUp || false,
                activeFollowUpId: leadToEdit.activeFollowUpId || "",
                pipelines: leadToEdit.pipelines,
                followUpText: followUpText,
                followUpTime: followUpTime,
            });
            setPopupOpen(true);
        }
    };

    function convertToDate(value: Date | Timestamp): Date {
        if (value instanceof Timestamp) {
            return value.toDate(); // Convert Timestamp to Date
        }
        return new Date(value); // Handle number or other Date-compatible types
    }

    // Update lead function moved to AddUpdateLeadModal

    const handleToggleActive = async (id: string, isActive: boolean) => {
        // If deactivating (changing from active to inactive), show confirmation modal
        if (isActive) {
            // Find the lead to get its name for the confirmation modal
            const lead = leadsData.find(l => l.id === id);
            setLeadToDeactivate({
                id,
                isActive,
                name: lead?.name || 'Unknown Lead'
            });
            setShowDeactivateModal(true);
            return;
        }

        // If reactivating (changing from inactive to active), proceed directly
        await toggleLeadStatusConfirmed(id, isActive);
    };

    const toggleLeadStatusConfirmed = async (id: string, isActive: boolean) => {
        setIsDeactivating(true);
        const leadRef = doc(db, `companies/${companyId}/leads`, id);
        try {
            await updateDoc(leadRef, { isActive: !isActive, updatedAt: Timestamp.fromDate(new Date()), updatedBy: currentUser?.uid });
            setLeadsData(
                leadsData.map((lead) =>
                    lead.id === id ? { ...lead, isActive: !isActive } : lead
                )
            );
            setToastMessage(isActive ? "Lead deactivated successfully" : "Lead reactivated successfully");
            setShowToast(true);

            // If deactivating, refresh the page after a short delay
            if (isActive) {
                setTimeout(() => {
                    window.location.reload();
                }, 1500);
            } else {
                setTimeout(() => {
                    setShowToast(false);
                }, 3000);
            }
        } catch (error) {
            console.error("Error toggling lead active state:", error);
            setToastMessage("Error changing lead status");
            setIsError(true);
            setShowToast(true);

            setTimeout(() => {
                setShowToast(false);
                setIsError(false);
            }, 3000);
        } finally {
            setIsDeactivating(false);
            setShowDeactivateModal(false);
            setLeadToDeactivate(null);
        }
    };

    const getStatusCircleColor = (status: string) => {
        switch (status) {
            case 'Low Priority':
                return 'bg-red-300';
            case 'Meduim Priority':
                return 'bg-blue-200';
            case 'High Priority':
                return 'bg-black';
            default:
                return 'bg-transperant';
        }
    };

    const distributeEqually = (users: any[]) => {
        const equalShare = Math.floor(100 / users.length);
        const remainder = 100 % users.length;
        return users.map((user, index) => ({
            ...user,
            distribution: index < remainder ? equalShare + 1 : equalShare,
        }));
    };

    const handleSearchUsers = async (value: string) => {
        if (!userData?.companyId || value.length < 3) {
            setSearchResults([]);
            return;
        }

        setIsLoadingUsers(true);
        try {
            const upperValue = value.toUpperCase();
            const usersRef = collection(db, 'users');
            const q = query(
                usersRef,
                where('companyId', '==', userData.companyId),
                where('name', '>=', upperValue),
                where('name', '<=', upperValue + '\uf8ff'),
                limit(10)
            );

            const querySnapshot = await getDocs(q);
            const usersData = querySnapshot.docs
                .filter(doc => doc.data().isActive !== false) // Filter out deactivated users
                .map(doc => ({
                    id: doc.id,
                    name: doc.data().name
                }));

            setSearchResults(usersData);
        } catch (error) {
            console.error('Error searching users:', error);
        } finally {
            setIsLoadingUsers(false);
        }
    };

    const handleStudentClick = (studentId: string) => {
        // Capture current state before navigation
        const currentState = {
            from: '/leadManagement',
            scrollPosition: window.scrollY,
            pageSize: pageSize,
            totalLeadsCount: totalLeadsCount,
            newLeadsCount: newLeadsCount,
            isLoadingNewCount: isLoadingNewCount,
            // Capture all filter states
            filters: {
                selectedLeadStates,
                selectedLeadSources,
                selectedLeadStages,
                selectedLeadStatuses,
                selectedAdmissionStatuses,
                selectedWelcomeCallByAdmissionTeamStatuses,
                selectedVisaProcessingStatuses,
                selectedApplicationMovedStatuses,
                selectedConditionalOfferLetterStatuses,
                selectedUnConditionalOfferLetterStatuses,
                selectedFeePaymentStatuses,
                selectedFileSubmissionStatuses,
                selectedFollowUpStatuses,
                selectedAssigneeOptions,
                selectedSubAgentOptions,
                selectedCampaignOptions,
                selectedPrefferedStreams,
                selectedSuperAgents,
                selectedAdmissionSubStatus,
                selectedLeadSubSources,
                selectedLeadSubStages,
                selectedLeadSubStatuses,
                selectedDestinations,
                selectedDepartments,
                selectedStartDate,
                selectedEndDate,
                selectedTransferredDates,
                selectedTransferredEndDates,
                selectedUpdatedAtStartDate,
                selectedUpdatedAtEndDate,
                selectedFollowUpAtStartDate,
                selectedFollowUpAtEndDate,
                selectedIntakeYear,
                selectedIntakeMonth
            },
            searchQuery,
            phoneSearchQuery,
            orderByField,
            selectedFollowUpQuickFilter
        };

        navigate(`/studentdetails/${studentId}`, { state: currentState });
    };

    const handleCollaborateClick = async (student: LeadData) => {
        setSelectedStudent(student);
        if (userData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');
                }
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowCollaborateModal(true);
    };

    const handleTransferClick = async (student: LeadData) => {
        setSelectedStudent(student);
        setCurrentAssignedUser(null);

        if (userData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    setSelectedStudentPhone(leadData.number || 'No number available');

                    // Fetch current assigned user's name
                    if (leadData.assignedTo) {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            setCurrentAssignedUser({
                                id: userDoc.id,
                                name: userDoc.data().name
                            });
                        }
                    }
                }
            } catch (error) {
                console.error('Error fetching student data:', error);
                setSelectedStudentPhone('No number available');
            }
        }
        setShowTransferModal(true);
    };

    const handleFollowUpClick = async (student: LeadData) => {
        setSelectedStudent(student);
        setSelectedStudentFollowUp({
            id: student.id,
            name: student.name,
            phone: student.number || '',
        })
        setCurrentAssignedUser(null);

        if (userData?.companyId) {
            try {
                const leadRef = doc(db, `companies/${userData.companyId}/leads`, student.id);
                const leadDoc = await getDoc(leadRef);
                if (leadDoc.exists()) {
                    const leadData = leadDoc.data();
                    // Fetch current assigned user's name
                    if (leadData.assignedTo) {
                        const userDoc = await getDoc(doc(db, 'users', leadData.assignedTo));
                        if (userDoc.exists()) {
                            setCurrentAssignedUser({
                                id: userDoc.id,
                                name: userDoc.data().name
                            });
                        }
                    }

                    // check if hasActiveFollowUp is true and activeFollowUpId is not null
                    if (leadData.hasActiveFollowUp && leadData.activeFollowUpId) {
                        const followUpRef = doc(db, `companies/${userData.companyId}/followUps`, leadData.activeFollowUpId);
                        const followUpDoc = await getDoc(followUpRef);
                        if (followUpDoc.exists()) {
                            const followUpData = followUpDoc.data();
                            setSelectedFollowUp({
                                id: followUpDoc.id,
                                userId: followUpData.userId,
                                leadId: followUpData.leadId,
                                followUp: followUpData.followUp,
                                followUpTime: followUpData.followUpTime,
                                isDone: followUpData.isDone,
                                isEdited: followUpData.isEdited || false,
                                studentName: followUpData.studentName || '',
                                studentNumber: followUpData.studentNumber || '',
                                assignedUserName: followUpData.assignedUserName || ''
                            });
                        }
                    } else {
                        setSelectedFollowUp(null);
                    }
                }
            } catch (error) {
                console.error('Error fetching student data:', error);
            }
        }
        setShowFollowUpModal(true);
    }

    const [leadsData, setLeadsData] = useState<LeadData[]>([]);
    const [assigneesWithLeads, setAssigneesWithLeads] = useState<Set<string>>(new Set());
    console.log("leaddata", leadsData);

    const [hasMore, setHasMore] = useState(true);
    const [lastDocument, setLastDocument] = useState<{
        id: string;
        leadDate: Timestamp;
    } | null>(null);

    const [currentPage, setCurrentPage] = useState<number>(() => {
        const storedPage = sessionStorage.getItem('leadManagementCurrentPage');
        return storedPage ? parseInt(storedPage, 10) : 1;
    });
    // We're using server-side pagination, so we don't need to slice the data


    const handlePageChange = (pageNumber: number) => {
        if (pageNumber < 1 || pageNumber > totalPages || (pageNumber > currentPage && !hasMore)) {
            return;
        }

        setCurrentPage(pageNumber);
        fetchPageData(pageNumber);

        // Save pagination state to sessionStorage
        savePaginationState(pageNumber, lastDocument?.id || null);
    };

    // Helper function to fetch data for a specific page
    const fetchPageData = (pageNum: number) => {

        console.log("createdAtStartDate:", selectedStartDate);

        // Always fetch new data when changing pages
        // This ensures we're getting the correct data for the page
        fetchLeadsdata(false, pageNum, {
            selectedLeadSources: selectedLeadSources,
            selectedLeadStates: selectedLeadStates,
            selectedLeadStages: selectedLeadStages,
            selectedLeadStatuses: selectedLeadStatuses,
            selectedAdmissionStatuses: selectedAdmissionStatuses,
            selectedWelcomeCallByAdmissionTeamStatuses: selectedWelcomeCallByAdmissionTeamStatuses,
            selectedVisaProcessingStatuses: selectedVisaProcessingStatuses,
            selectedApplicationMovedStatuses: selectedApplicationMovedStatuses,
            selectedConditionalOfferLetterStatuses: selectedConditionalOfferLetterStatuses,
            selectedUnConditionalOfferLetterStatuses: selectedUnConditionalOfferLetterStatuses,
            selectedFeePaymentStatuses: selectedFeePaymentStatuses,
            selectedFileSubmissionStatuses: selectedFileSubmissionStatuses,
            selectedFollowUpStatuses: selectedFollowUpStatuses,
            selectedSubAgentOptions: selectedSubAgentOptions,
            selectedCampaignOptions: selectedCampaignOptions,
            selectedPrefferedStreams: selectedPrefferedStreams,
            selectedSuperAgents: selectedSuperAgents,
            selectedAdmissionSubStatus: selectedAdmissionSubStatus,
            selectedLeadSubSources: selectedLeadSubSources,
            selectedLeadSubStages: selectedLeadSubStages,
            selectedLeadSubStatuses: selectedLeadSubStatuses,
            selectedDestinations: selectedDestinations,
            searchQuery: searchQuery,
            phoneSearchQuery: phoneSearchQuery,
            selectedStartDate: selectedStartDate,
            selectedEndDate: selectedEndDate,
            selectedTransferredDates: selectedTransferredDates,
            selectedTransferredEndDates: selectedTransferredEndDates,
            selectedUpdatedAtStartDate: selectedUpdatedAtStartDate,
            selectedUpdatedAtEndDate: selectedUpdatedAtEndDate,
            selectedFollowUpAtStartDate: selectedFollowUpAtStartDate,
            selectedFollowUpAtEndDate: selectedFollowUpAtEndDate,
            selectedIntakeYear: selectedIntakeYear,
            selectedIntakeMonth: selectedIntakeMonth
        });
    };

    const getAssignedToValue = async () => {
        console.log("currentScreen:", currentScreen);

        // Return early if there are already selected assignees
        if (selectedAssigneeOptions.length > 0) {
            return selectedAssigneeOptions;
        }

        // Handle Staff screen case
        if (currentScreen === "Staff" && currentUser?.uid) {
            return [currentUser.uid];
        }

        // Handle Manager screen case
        if (currentScreen === "Manager" && managerTeamOptions?.length > 0) {
            console.log("Manager team options:", managerTeamOptions);

            return managerTeamOptions;
        }

        // Default case
        return [];
    };


    // async function fetchLeadsdata(isLoadingMore = false, pageNum?: number) {
    //     // Only prevent duplicate load-more requests
    //     if (isLoadingMore && loading) return;

    //     setLoading(true);
    //     const pageToFetch = pageNum !== undefined ? pageNum : currentPage;
    //     try {
    //         const startTime = new Date();
    //         const assignedTo = await getAssignedToValue();
    //         const auth = getAuth();

    //         if (!auth.currentUser) {
    //             console.error('User not logged in');
    //             return;
    //         }

    //         // Cursor-based pagination logic
    //         let direction: 'first' | 'next' | 'prev' = 'first';
    //         let cursorId: string | undefined = undefined;
    //         if (pageToFetch === 1) {
    //             direction = 'first';
    //         } else if (pageToFetch > currentPage) {
    //             direction = 'next';
    //             cursorId = lastDocument?.id;
    //         } else if (pageToFetch < currentPage) {
    //             direction = 'prev';
    //             cursorId = leadsData[0]?.id; // Use first doc id for prev
    //         }

    //         if (!cursorId && pageToFetch > 1) {
    //             cursorId = sessionStorage.getItem('cursorId') || '';
    //         }

    //         console.log("Fetching leads data for page:", cursorId);

    //         const result = await fetchLeads(db, auth.currentUser, {
    //             pageSize: pageSize,
    //             direction,
    //             cursorId,
    //             isActive: isStaff && currentScreen === "Staff" ? true : undefined,
    //             sourceIds: selectedLeadSources,
    //             stateIds: selectedLeadStates,
    //             stageIds: selectedLeadStages,
    //             statusIds: selectedLeadStatuses,
    //             admissionStatusIds: selectedAdmissionStatuses,
    //             welcomeCallStatusIds: selectedWelcomeCallByAdmissionTeamStatuses,
    //             visaProcessStatusIds: selectedVisaProcessingStatuses,
    //             applicationStatusIds: selectedApplicationMovedStatuses,
    //             conditionalOfferLetterStatusIds: selectedConditionalOfferLetterStatuses,
    //             unconditionalOfferLetterStatusIds: selectedUnConditionalOfferLetterStatuses,
    //             feePaymentStatusIds: selectedFeePaymentStatuses,
    //             fileSubmissionStatusIds: selectedFileSubmissionStatuses,
    //             activeFollowUpIds: selectedFollowUpStatuses,
    //             assignedTo: assignedTo,
    //             subAgent: selectedSubAgentOptions,
    //             campaignIds: selectedCampaignOptions,
    //             preferredStreamIds: selectedPrefferedStreams,
    //             destinationIds: selectedDestinations,
    //             searchValue: searchQuery.length > 2 ? searchQuery : '',
    //             phoneValue: phoneSearchQuery.length > 2 ? phoneSearchQuery : '',
    //             startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
    //             endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
    //             updatedAtStartDate: selectedUpdatedAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtStartDate[0], true) : null,
    //             updatedAtEndDate: selectedUpdatedAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtEndDate[0], false) : null,
    //         });

    //         const response = result.data;
    //         setLeadsData(response?.leads || []);
    //         setHasMore(response?.hasMore);
    //         setLastDocument(response?.lastDocument);
    //         setTotalLeadsCount(response?.count);

    //         // Check if we should update the selectCurrentPage state based on current selections
    //         const currentPageLeadIds = response?.leads.map(lead => lead.id) || [];
    //         if (currentPageLeadIds.length > 0) {
    //             const allCurrentPageLeadsSelected = currentPageLeadIds.every(id =>
    //                 selectedLeadsForAction.includes(id)
    //             );
    //             setSelectCurrentPage(allCurrentPageLeadsSelected);
    //         }

    //         if (response?.leads.length === 0) {
    //             setToastMessage("No leads found for the selected filters");
    //             setShowToast(true);
    //             setTimeout(() => {
    //                 setShowToast(false);
    //             }, 3000);
    //         }

    //         const endTime = new Date();
    //         const timeDiff = endTime.getTime() - startTime.getTime();
    //         console.log("Time taken to fetch leads data:", timeDiff, "ms");
    //     } catch (error) {
    //         console.error('Failed to fetch leads:', error);
    //     } finally {
    //         setLoading(false);
    //     }
    // }

    async function fetchLeadsdata(
        isLoadingMore = false,
        pageNum?: number,
        overrideFilters?: {
            selectedLeadSources?: any[];
            selectedLeadStates?: any[];
            selectedLeadStages?: any[];
            selectedLeadStatuses?: any[];
            selectedAdmissionStatuses?: any[];
            selectedWelcomeCallByAdmissionTeamStatuses?: any[];
            selectedVisaProcessingStatuses?: any[];
            selectedApplicationMovedStatuses?: any[];
            selectedConditionalOfferLetterStatuses?: any[];
            selectedUnConditionalOfferLetterStatuses?: any[];
            selectedFeePaymentStatuses?: any[];
            selectedFileSubmissionStatuses?: any[];
            selectedFollowUpStatuses?: any[];
            selectedSubAgentOptions?: any[];
            selectedCampaignOptions?: any[];
            selectedPrefferedStreams?: any[];
            selectedSuperAgents?: any[];
            selectedAdmissionSubStatus?: any[];
            selectedLeadSubSources?: any[];
            selectedLeadSubStages?: any[];
            selectedLeadSubStatuses?: any[];
            selectedDestinations?: any[];
            searchQuery?: string;
            phoneSearchQuery?: string;
            selectedStartDate?: any[];
            selectedEndDate?: any[];
            selectedTransferredDates?: any[];
            selectedTransferredEndDates?: any[];
            selectedUpdatedAtStartDate?: any[];
            selectedUpdatedAtEndDate?: any[];
            selectedFollowUpAtStartDate?: any[];
            selectedFollowUpAtEndDate?: any[];
            selectedIntakeYear?: any[];
            selectedIntakeMonth?: any[];
        }
    ) {
        // Only prevent duplicate load-more requests
        if (isLoadingMore && loading) return;

        setLoading(true);
        const pageToFetch = pageNum !== undefined ? pageNum : currentPage;

        try {
            const startTime = new Date();
            const assignedTo = await getAssignedToValue();
            const auth = getAuth();

            if (!auth.currentUser) {
                console.error('User not logged in');
                return;
            }

            // Cursor-based pagination logic
            let direction: 'first' | 'next' | 'prev' = 'first';
            let cursorId: string | undefined = undefined;

            if (pageToFetch === 1) {
                direction = 'first';
            } else if (pageToFetch > currentPage) {
                direction = 'next';
                cursorId = lastDocument?.id;
            } else if (pageToFetch < currentPage) {
                direction = 'prev';
                cursorId = leadsData[0]?.id;
            }

            // Special case: If we want to go to a page > 1 but don't have cursor data
            // (e.g., after page refresh), we need to build up the cursor chain
            if (!cursorId && pageToFetch > 1 && !lastDocument) {
                // Try to get stored cursor first
                const storedCursorId = sessionStorage.getItem('leadManagementCursorId') || sessionStorage.getItem('cursorId');
                if (storedCursorId) {
                    cursorId = storedCursorId;
                    direction = 'next';
                } else {
                    // If no stored cursor, we need to build up to the target page
                    // This will trigger the restoration function
                    setLoading(false);
                    restorePaginationStateAndFetch(pageToFetch);
                    return;
                }
            } else if (!cursorId && pageToFetch > 1) {
                cursorId = sessionStorage.getItem('cursorId') || '';
            }

            console.log("Fetching leads data for page:", cursorId);

            // Use override values if provided, otherwise use current state
            const filters = overrideFilters || {};

            const result = await fetchLeads(db, auth.currentUser, {
                orderByField: orderByField,
                sortDirection: sortDirection,
                pageSize: pageSize,
                direction,
                cursorId,
                // Filter inactive leads for staff on Staff screen
                isActive: isStaff && currentScreen === "Staff" ? true : undefined,
                sourceIds: filters.selectedLeadSources ?? selectedLeadSources,
                leadSubSources: filters.selectedLeadSubSources ?? selectedLeadSubSources,
                stateIds: filters.selectedLeadStates ?? selectedLeadStates,
                stageIds: filters.selectedLeadStages ?? selectedLeadStages,
                leadSubStages: filters.selectedLeadSubStages ?? selectedLeadSubStages,
                leadSubStatuses: filters.selectedLeadSubStatuses ?? selectedLeadSubStatuses,
                statusIds: filters.selectedLeadStatuses ?? selectedLeadStatuses,
                admissionStatusIds: filters.selectedAdmissionStatuses ?? selectedAdmissionStatuses,
                admissionSubStatus: filters.selectedAdmissionSubStatus ?? selectedAdmissionSubStatus,
                welcomeCallStatusIds: filters.selectedWelcomeCallByAdmissionTeamStatuses ?? selectedWelcomeCallByAdmissionTeamStatuses,
                visaProcessStatusIds: filters.selectedVisaProcessingStatuses ?? selectedVisaProcessingStatuses,
                applicationStatusIds: filters.selectedApplicationMovedStatuses ?? selectedApplicationMovedStatuses,
                conditionalOfferLetterStatusIds: filters.selectedConditionalOfferLetterStatuses ?? selectedConditionalOfferLetterStatuses,
                unconditionalOfferLetterStatusIds: filters.selectedUnConditionalOfferLetterStatuses ?? selectedUnConditionalOfferLetterStatuses,
                feePaymentStatusIds: filters.selectedFeePaymentStatuses ?? selectedFeePaymentStatuses,
                fileSubmissionStatusIds: filters.selectedFileSubmissionStatuses ?? selectedFileSubmissionStatuses,
                activeFollowUpIds: filters.selectedFollowUpStatuses ?? selectedFollowUpStatuses,
                // Handle Staff assignment differently
                assignedTo: isStaff && currentScreen === "Staff" ? [currentUser?.uid].filter((id): id is string => !!id) : assignedTo,
                subAgent: filters.selectedSubAgentOptions ?? selectedSubAgentOptions,
                campaignIds: filters.selectedCampaignOptions ?? selectedCampaignOptions,
                preferredStreamIds: filters.selectedPrefferedStreams ?? selectedPrefferedStreams,
                superAgentId: filters.selectedSuperAgents ?? selectedSuperAgents,
                destinationIds: filters.selectedDestinations ?? selectedDestinations,
                searchValue: (filters.searchQuery ?? searchQuery).length > 2 ? (filters.searchQuery ?? searchQuery) : '',
                phoneValue: (filters.phoneSearchQuery ?? phoneSearchQuery).length > 2 ? (filters.phoneSearchQuery ?? phoneSearchQuery) : '',
                startDate: (filters.selectedStartDate ?? selectedStartDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedStartDate ?? selectedStartDate)[0], true) : null,
                endDate: (filters.selectedEndDate ?? selectedEndDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedEndDate ?? selectedEndDate)[0], false) : null,
                tranffertAtStartDate: (filters.selectedTransferredDates ?? selectedTransferredDates).length > 0 ? convertToFirebaseTimestamp((filters.selectedTransferredDates ?? selectedTransferredDates)[0], true) : null,
                tranffertAtEndDate: (filters.selectedTransferredEndDates ?? selectedTransferredEndDates).length > 0 ? convertToFirebaseTimestamp((filters.selectedTransferredEndDates ?? selectedTransferredEndDates)[0], false) : null,
                updatedAtStartDate: (filters.selectedUpdatedAtStartDate ?? selectedUpdatedAtStartDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedUpdatedAtStartDate ?? selectedUpdatedAtStartDate)[0], true) : null,
                updatedAtEndDate: (filters.selectedUpdatedAtEndDate ?? selectedUpdatedAtEndDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedUpdatedAtEndDate ?? selectedUpdatedAtEndDate)[0], false) : null,
                followUpAtStartDate: (filters.selectedFollowUpAtStartDate ?? selectedFollowUpAtStartDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedFollowUpAtStartDate ?? selectedFollowUpAtStartDate)[0], true) : null,
                followUpAtEndDate: (filters.selectedFollowUpAtEndDate ?? selectedFollowUpAtEndDate).length > 0 ? convertToFirebaseTimestamp((filters.selectedFollowUpAtEndDate ?? selectedFollowUpAtEndDate)[0], false) : null,
                intakeYear: filters.selectedIntakeYear ?? selectedIntakeYear,
                intakeMonth: filters.selectedIntakeMonth ?? selectedIntakeMonth
            });

            const response = result.data;
            const leadsFromResponse = response?.leads || [];
            setLeadsData(leadsFromResponse);
            setHasMore(response?.hasMore);
            setLastDocument(response?.lastDocument);
            setTotalLeadsCount(response?.count);

            // Save pagination state after successful data fetch
            savePaginationState(pageToFetch, response?.lastDocument?.id || null);
            
            // Calculate and set total new leads count from all leads (not just current page)
            // Only fetch new count if we're on the first page or if filters have changed
            if (pageToFetch === 1 || !newLeadsCount) {
                setIsLoadingNewCount(true);
                try {
                    const totalNewCount = await fetchTotalNewLeadsCount(filters);
                    setNewLeadsCount(totalNewCount);
                } catch (error) {
                    console.error('Error fetching new leads count:', error);
                } finally {
                    setIsLoadingNewCount(false);
                }
            }

            // Track which assignees have leads
            const assigneesWithLeadsSet = new Set<string>();
            response?.leads.forEach(lead => {
                if (lead.assignedTo) {
                    assigneesWithLeadsSet.add(lead.assignedTo);
                }
            });
            setAssigneesWithLeads(assigneesWithLeadsSet);

            // Check if we should update the selectCurrentPage state based on current selections
            const currentPageLeadIds = response?.leads.map(lead => lead.id) || [];
            if (currentPageLeadIds.length > 0) {
                const allCurrentPageLeadsSelected = currentPageLeadIds.every(id =>
                    selectedLeadsForAction.includes(id)
                );
                setSelectCurrentPage(allCurrentPageLeadsSelected);
            }

            // Show "No leads found" message when no leads are returned
            // Don't show any message when "All" is selected in assignee filter
            if (response?.leads.length === 0 && !(selectedAssigneeOptionsValue && selectedAssigneeOptionsValue.includes('all'))) {
                let message = "No leads found for the selected filters";

                // Add assignee names to the message for assignees without leads
                if (selectedAssigneeOptionsValue && selectedAssigneeOptionsValue.length > 0) {
                    let assigneesWithoutLeads: string[] = [];

                    // When specific assignees are selected, show only selected assignees without leads
                    assigneesWithoutLeads = selectedAssigneeOptionsValue
                        .filter(assigneeId => !assigneesWithLeads.has(assigneeId))
                        .map(assigneeId => {
                            const assignee = assigneeOptions.find(option => option.id === assigneeId);
                            return assignee ? assignee.data.name : null;
                        })
                        .filter(name => name !== null) as string[];

                    if (assigneesWithoutLeads.length > 0) {
                        if (assigneesWithoutLeads.length === 1) {
                            message = `No leads found for ${assigneesWithoutLeads[0]}`;
                        } else if (assigneesWithoutLeads.length === 2) {
                            message = `No leads found for ${assigneesWithoutLeads[0]} and ${assigneesWithoutLeads[1]}`;
                        } else {
                            const lastAssignee = assigneesWithoutLeads.pop();
                            message = `No leads found for ${assigneesWithoutLeads.join(', ')}, and ${lastAssignee}`;
                        }
                    } else if (selectedAssigneeOptionsValue.length === 1) {
                        // Handle case where single assignee is selected but they do have leads (shouldn't normally happen with 0 leads)
                        // This is a fallback to show the assignee name even if the logic above doesn't catch it
                        const selectedAssignee = assigneeOptions.find(option => option.id === selectedAssigneeOptionsValue[0]);
                        if (selectedAssignee) {
                            message = `No leads found for ${selectedAssignee.data.name}`;
                        }
                    }
                }

                setToastMessage(message);
                setShowToast(true);
                setTimeout(() => {
                    setShowToast(false);
                }, 3000);
            }

            const endTime = new Date();
            const timeDiff = endTime.getTime() - startTime.getTime();
            console.log("Time taken to fetch leads data:", timeDiff, "ms");
        } catch (error) {
            console.error('Failed to fetch leads:', error);
            setToastMessage('Failed to fetch leads. Please try again.');
            setShowToast(true);
        } finally {
            setLoading(false);
        }
    }

    const handleCopy = async () => {
        if (!password) return;

        try {
            await navigator.clipboard.writeText(password);
            setIsCopied(true);

            // Reset the "Copied" state after 2 seconds
            setTimeout(() => {
                setIsCopied(false);
            }, 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const handleGeneratePassword = () => {
        // Add your password generation logic here

    };

    const handleSearchString = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
        dispatch(setSearchString({ key: 'nameSearchString', value: [e.target.value] }));
    }

    const handleSearchPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        // remove all spaces
        let value = e.target.value.replace(/\s/g, '');

        // update state
        setPhoneSearchQuery(value);

        // dispatch update
        dispatch(setSearchString({ key: 'phoneSearchString', value: [value] }));
    };

    // Phone validation functions for modal moved to AddUpdateLeadModal

    const handlePhoneFocusSearch = () => {
        // if field is empty when focused, add +91
        if (phoneSearchQuery.trim() === "") {
            setPhoneSearchQuery("+91");
        }
    };

    const handlePhoneBlurSearch = () => {
        // if user clears everything and leaves field, reset to +91
        if (phoneSearchQuery.trim() === "") {
            setPhoneSearchQuery("+91");
        }
    };

    const handleSearch = (
        e?: React.KeyboardEvent<HTMLInputElement> | React.MouseEvent<HTMLImageElement, MouseEvent> | React.MouseEvent<Element, MouseEvent>
    ) => {
        // For any search action, reset to page 1
        setCurrentPage(1);

        // If called manually (without an event), directly fetch data
        if (!e) {
            return;
        }

        // Handle keyboard event (Enter key)
        if ("key" in e && e.key === "Enter") {
            e.preventDefault(); // Prevent form submission
            fetchPageData(1);
            return;
        }

        // Handle mouse click event
        if (e.type === "click") {
            fetchPageData(1);
        }

        if (selectedAssigneeOptions.length !== 0) {
            setTempAssignee(selectedAssigneeOptions);
            // set all the filters to empty
            dispatch(clearLeadManagementAssigneeOptions());
        }

    };

    // Initial load
    useEffect(() => {
        if (firstEffectDone === true && (currentScreen !== null || currentScreen !== undefined || currentScreen !== "")) {
            console.log("First effect", firstEffectDone);
            console.log("currentScreen", currentScreen);

            console.log("selectedCampaignOptions in useEffect:", selectedCampaignOptions);

            // Reset new leads count when filters change
            setNewLeadsCount(0);

            // Try to get stored page from new key first, then fall back to old key
            const storedPage = sessionStorage.getItem('leadManagementCurrentPage') || 
                              sessionStorage.getItem('currentPage') || '1';
            const pageToLoad = parseInt(storedPage, 10);
            
            if (pageToLoad > 1) {
                // If we have a stored page > 1, restore it using the comprehensive restoration function
                restorePaginationStateAndFetch(pageToLoad);
            } else {
                // If page is 1 or invalid, fetch first page normally
                setCurrentPage(1);
                fetchPageData(1);
            }
        }
    }, [firstEffectDone, currentScreen, managerTeamOptions, selectedLeadSources, selectedLeadStates, selectedLeadStages, selectedLeadStatuses,
        selectedAdmissionStatuses, selectedWelcomeCallByAdmissionTeamStatuses, selectedVisaProcessingStatuses,
        selectedApplicationMovedStatuses, selectedConditionalOfferLetterStatuses, selectedUnConditionalOfferLetterStatuses,
        selectedFeePaymentStatuses, selectedFileSubmissionStatuses, selectedFollowUpStatuses, selectedAssigneeOptions, selectedSubAgentOptions, selectedCampaignOptions,
        selectedPrefferedStreams, selectedSuperAgents, selectedAdmissionSubStatus, selectedLeadSubStages, selectedLeadSubStatuses, selectedLeadSubSources, selectedDestinations, selectedStartDate, selectedEndDate, selectedTransferredDates, selectedTransferredEndDates, selectedUpdatedAtStartDate, selectedUpdatedAtEndDate, selectedFollowUpAtStartDate, selectedFollowUpAtEndDate, selectedIntakeMonth,
        selectedIntakeYear, pageSize, orderByField, sortDirection]);



    const showToastMessage = (message: string, isError: boolean = false) => {
        setToastMessage(message);
        setShowToast(true);
        setTimeout(() => setShowToast(false), 3000);
    };

    const getTotalFilters = () => {
        // For staff users, exclude assignee filter from count since it's automatically applied
        const assigneeFilterCountForDisplay = (isStaff && currentScreen === 'Staff') ? 0 : selectedAssigneeOptions.length;
        
        const filterCount =
            selectedLeadSources.length +
            selectedLeadStates.length +
            selectedLeadStages.length +
            selectedLeadStatuses.length +
            selectedAdmissionStatuses.length +
            selectedWelcomeCallByAdmissionTeamStatuses.length +
            selectedVisaProcessingStatuses.length +
            selectedApplicationMovedStatuses.length +
            selectedConditionalOfferLetterStatuses.length +
            selectedUnConditionalOfferLetterStatuses.length +
            selectedFeePaymentStatuses.length +
            selectedFileSubmissionStatuses.length +
            selectedFollowUpStatuses.length +
            assigneeFilterCountForDisplay + // Use filtered assignee count
            selectedSubAgentOptions.length +
            selectedCampaignOptions.length +
            selectedPrefferedStreams.length +
            selectedSuperAgents.length +
            selectedAdmissionSubStatus.length +
            selectedLeadSubStages.length +
            selectedLeadSubStatuses.length +
            selectedLeadSubSources.length +
            selectedDestinations.length +
            selectedStartDate.length +
            selectedEndDate.length +
            selectedTransferredDates.length +
            selectedTransferredEndDates.length +
            selectedUpdatedAtEndDate.length +
            selectedUpdatedAtStartDate.length +
            selectedFollowUpAtStartDate.length +
            selectedFollowUpAtEndDate.length +
            selectedIntakeMonth.length +
            selectedIntakeYear.length

        return filterCount > 0 ? filterCount : 0;
    };

    // function to calculate new leads count from current page
    const calculateNewLeadsCount = (leads: LeadData[]): number => {
        return leads.filter(lead => {
            if (!lead.createdAt || !lead.updatedAt) return false;

            const createdDate = lead.createdAt.toDate();
            const updatedDate = lead.updatedAt.toDate();
            const transferredDate = lead.transferredAt ? lead.transferredAt.toDate() : null;

            const createdTime = createdDate.getTime();
            const updatedTime = updatedDate.getTime();
            const transferredTime = transferredDate ? transferredDate.getTime() : 0;

            const timeDiffCreatedUpdated = Math.abs(updatedTime - createdTime);
            const timeDiffCreatedTransferred = transferredDate ? Math.abs(transferredTime - createdTime) : 0;
            const timeDiffUpdatedTransferred = transferredDate ? Math.abs(transferredTime - updatedTime) : 0;

            return timeDiffCreatedUpdated < 2000 &&
                (!transferredDate || (timeDiffCreatedTransferred < 2000 && timeDiffUpdatedTransferred < 2000));
        }).length;
    };

    // function to fetch total new leads count - with multiple batches for accuracy
    const fetchTotalNewLeadsCount = async (filters?: {
        selectedLeadSources?: any[];
        selectedLeadStates?: any[];
        selectedLeadStages?: any[];
        selectedLeadStatuses?: any[];
        selectedAdmissionStatuses?: any[];
        selectedWelcomeCallByAdmissionTeamStatuses?: any[];
        selectedVisaProcessingStatuses?: any[];
        selectedApplicationMovedStatuses?: any[];
        selectedConditionalOfferLetterStatuses?: any[];
        selectedUnConditionalOfferLetterStatuses?: any[];
        selectedFeePaymentStatuses?: any[];
        selectedFileSubmissionStatuses?: any[];
        selectedFollowUpStatuses?: any[];
        selectedSubAgentOptions?: any[];
        selectedCampaignOptions?: any[];
        selectedPrefferedStreams?: any[];
        selectedSuperAgents?: any[];
        selectedAdmissionSubStatus?: any[];
        selectedLeadSubSources?: any[];
        selectedLeadSubStages?: any[];
        selectedLeadSubStatuses?: any[];
        selectedDestinations?: any[];
        searchQuery?: string;
        phoneSearchQuery?: string;
        selectedStartDate?: any[];
        selectedEndDate?: any[];
        selectedTransferredDates?: any[];
        selectedTransferredEndDates?: any[];
        selectedUpdatedAtStartDate?: any[];
        selectedUpdatedAtEndDate?: any[];
        selectedFollowUpAtStartDate?: any[];
        selectedFollowUpAtEndDate?: any[];
        selectedIntakeYear?: any[];
        selectedIntakeMonth?: any[];
    }) => {
        try {
            const assignedTo = await getAssignedToValue();
            const auth = getAuth();
            
            if (!auth.currentUser || !companyId) {
                return 0;
            }

            let totalNewLeads = 0;
            let fetchedLeads = 0;
            let actualTotalCount = 0;
            let hasMore = true;
            let cursorId: string | undefined = undefined;
            
            // Fetch leads in batches to get an accurate count
            while (hasMore && fetchedLeads < 2000) { // Limit to 2000 leads max for performance
                const result: { data: { leads: LeadData[]; hasMore: boolean; count: number; lastDocument?: { id: string; leadDate: any } | null } } = await fetchLeads(db, auth.currentUser, {
                    orderByField: orderByField,
                    sortDirection: sortDirection,
                    pageSize: 500, // Fetch 500 leads per batch
                    direction: cursorId ? 'next' : 'first',
                    cursorId: cursorId,
                    isActive: isStaff && currentScreen === "Staff" ? true : undefined,
                    sourceIds: filters?.selectedLeadSources ?? selectedLeadSources,
                    leadSubSources: filters?.selectedLeadSubSources ?? selectedLeadSubSources,
                    stateIds: filters?.selectedLeadStates ?? selectedLeadStates,
                    stageIds: filters?.selectedLeadStages ?? selectedLeadStages,
                    leadSubStages: filters?.selectedLeadSubStages ?? selectedLeadSubStages,
                    leadSubStatuses: filters?.selectedLeadSubStatuses ?? selectedLeadSubStatuses,
                    statusIds: filters?.selectedLeadStatuses ?? selectedLeadStatuses,
                    admissionStatusIds: filters?.selectedAdmissionStatuses ?? selectedAdmissionStatuses,
                    admissionSubStatus: filters?.selectedAdmissionSubStatus ?? selectedAdmissionSubStatus,
                    welcomeCallStatusIds: filters?.selectedWelcomeCallByAdmissionTeamStatuses ?? selectedWelcomeCallByAdmissionTeamStatuses,
                    visaProcessStatusIds: filters?.selectedVisaProcessingStatuses ?? selectedVisaProcessingStatuses,
                    applicationStatusIds: filters?.selectedApplicationMovedStatuses ?? selectedApplicationMovedStatuses,
                    conditionalOfferLetterStatusIds: filters?.selectedConditionalOfferLetterStatuses ?? selectedConditionalOfferLetterStatuses,
                    unconditionalOfferLetterStatusIds: filters?.selectedUnConditionalOfferLetterStatuses ?? selectedUnConditionalOfferLetterStatuses,
                    feePaymentStatusIds: filters?.selectedFeePaymentStatuses ?? selectedFeePaymentStatuses,
                    fileSubmissionStatusIds: filters?.selectedFileSubmissionStatuses ?? selectedFileSubmissionStatuses,
                    activeFollowUpIds: filters?.selectedFollowUpStatuses ?? selectedFollowUpStatuses,
                    assignedTo: isStaff && currentScreen === "Staff" ? [currentUser?.uid].filter((id): id is string => !!id) : assignedTo,
                    subAgent: filters?.selectedSubAgentOptions ?? selectedSubAgentOptions,
                    campaignIds: filters?.selectedCampaignOptions ?? selectedCampaignOptions,
                    preferredStreamIds: filters?.selectedPrefferedStreams ?? selectedPrefferedStreams,
                    superAgentId: filters?.selectedSuperAgents ?? selectedSuperAgents,
                    destinationIds: filters?.selectedDestinations ?? selectedDestinations,
                    searchValue: (filters?.searchQuery ?? searchQuery).length > 2 ? (filters?.searchQuery ?? searchQuery) : '',
                    phoneValue: (filters?.phoneSearchQuery ?? phoneSearchQuery).length > 2 ? (filters?.phoneSearchQuery ?? phoneSearchQuery) : '',
                    startDate: (filters?.selectedStartDate ?? selectedStartDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedStartDate ?? selectedStartDate)[0], true) : null,
                    endDate: (filters?.selectedEndDate ?? selectedEndDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedEndDate ?? selectedEndDate)[0], false) : null,
                    tranffertAtStartDate: (filters?.selectedTransferredDates ?? selectedTransferredDates).length > 0 ? convertToFirebaseTimestamp((filters?.selectedTransferredDates ?? selectedTransferredDates)[0], true) : null,
                    tranffertAtEndDate: (filters?.selectedTransferredEndDates ?? selectedTransferredEndDates).length > 0 ? convertToFirebaseTimestamp((filters?.selectedTransferredEndDates ?? selectedTransferredEndDates)[0], false) : null,
                    updatedAtStartDate: (filters?.selectedUpdatedAtStartDate ?? selectedUpdatedAtStartDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedUpdatedAtStartDate ?? selectedUpdatedAtStartDate)[0], true) : null,
                    updatedAtEndDate: (filters?.selectedUpdatedAtEndDate ?? selectedUpdatedAtEndDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedUpdatedAtEndDate ?? selectedUpdatedAtEndDate)[0], false) : null,
                    followUpAtStartDate: (filters?.selectedFollowUpAtStartDate ?? selectedFollowUpAtStartDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedFollowUpAtStartDate ?? selectedFollowUpAtStartDate)[0], true) : null,
                    followUpAtEndDate: (filters?.selectedFollowUpAtEndDate ?? selectedFollowUpAtEndDate).length > 0 ? convertToFirebaseTimestamp((filters?.selectedFollowUpAtEndDate ?? selectedFollowUpAtEndDate)[0], false) : null,
                    intakeYear: filters?.selectedIntakeYear ?? selectedIntakeYear,
                    intakeMonth: filters?.selectedIntakeMonth ?? selectedIntakeMonth
                });

                const batchLeads = result.data?.leads || [];
                if (batchLeads.length === 0) {
                    break;
                }

                // Count new leads in this batch
                totalNewLeads += calculateNewLeadsCount(batchLeads);
                fetchedLeads += batchLeads.length;
                actualTotalCount = result.data?.count || 0;
                hasMore = result.data?.hasMore || false;
                
                // Update cursor for next batch
                if (hasMore && result.data?.lastDocument?.id) {
                    cursorId = result.data.lastDocument.id;
                } else {
                    hasMore = false;
                }
            }
            
            // If we fetched all leads, return exact count
            if (fetchedLeads >= actualTotalCount || !hasMore) {
                return totalNewLeads;
            }
            
            // If we only fetched a portion, estimate the total
            if (fetchedLeads > 0 && actualTotalCount > 0) {
                const sampleRatio = totalNewLeads / fetchedLeads;
                const estimatedNewCount = Math.round(actualTotalCount * sampleRatio);
                return estimatedNewCount;
            }
            
            return 0;
        } catch (error) {
            console.error('Error fetching total new leads count:', error);
            return 0;
        }
    };

    // function to check the ceratedAt and updatedAt has atleast a difference of 2 seconds
    const getStatusLabel = (
        createdAt: Timestamp,
        updatedAt: Timestamp,
        transferredAt: Timestamp
    ): string => {
        // First, ensure required timestamps exist
        if (!createdAt || !updatedAt) return "";

        const createdDate = createdAt.toDate();
        const updatedDate = updatedAt.toDate();
        const transferredDate = transferredAt ? transferredAt.toDate() : null;

        // Convert to timestamps for comparison
        const createdTime = createdDate.getTime();
        const updatedTime = updatedDate.getTime();
        const transferredTime = transferredDate ? transferredDate.getTime() : 0;

        // Case 1: If updatedAt is greater than both transferredAt and createdAt, show no tag
        if (updatedTime > transferredTime && updatedTime > createdTime) {
            return "";
        }

        // Case 2: If all three timestamps are the same (within 2 seconds), show "New"
        const timeDiffCreatedUpdated = Math.abs(updatedTime - createdTime);
        const timeDiffCreatedTransferred = transferredDate ? Math.abs(transferredTime - createdTime) : 0;
        const timeDiffUpdatedTransferred = transferredDate ? Math.abs(transferredTime - updatedTime) : 0;

        if (timeDiffCreatedUpdated < 2000 &&
            (!transferredDate || (timeDiffCreatedTransferred < 2000 && timeDiffUpdatedTransferred < 2000))) {
            return "New";
        }

        // Case 3: If transferredAt is greater than both updatedAt and createdAt, show "Transferred"
        if (transferredDate && transferredTime >= updatedTime && transferredTime > createdTime) {
            return "Transferred";
        }

        // Case 4: If createdAt is greater than transferredAt and the difference between createdAt and updatedAt is 1 day, show "Re-Enquiry"
        if (createdTime > transferredTime) {
            const dayDiffCreatedUpdated = Math.abs(createdTime - updatedTime) / (1000 * 60 * 60 * 24);
            if (dayDiffCreatedUpdated >= 1) {
                return "Re-Enquiry";
            }
        }

        // Default case 
        return "";
    };

    // Function to handle cloud telephony
    const handleCall = async (lead: LeadData) => {
        try {
            // Set calling state for this specific lead
            setCallingLeads(prev => ({ ...prev, [lead.id]: true }));

            // Make sure we have a phone number to call
            if (!lead.number) {
                setToastMessage('Cannot initiate call: Missing phone number');
                setShowToast(true);
                // Clear calling state
                setCallingLeads(prev => ({ ...prev, [lead.id]: false }));
                return;
            }

            // Clean the phone number to ensure it's in the correct format
            const cleanNumber = lead.number.trim();

            // Call the Exotel cloud function
            console.log("Initiating call to:", cleanNumber);

            try {
                // collect company data
                const company = await getDoc(doc(db, `companies/${userData?.companyId}`));
                if (!company.exists()) {
                    console.error('Company data not found');
                    setToastMessage('Company data not found. Please try again later.');
                    setShowToast(true);
                }

                const cloudTelephonyProvider = company.data()?.cloudTelephonyProvider;
                console.log('Cloud Telephony Provider:', cloudTelephonyProvider);


                // When using Firebase Functions v2, we need to be explicit with the region
                // and ensure we're using the correct configuration
                let initiateCallFn;

                if (cloudTelephonyProvider === 'exotel') {
                    // Use the Exotel cloud function
                    initiateCallFn = httpsCallable(functions, 'initiateExotelCall');
                } else if (cloudTelephonyProvider === 'airtel') {
                    // Use the Twilio cloud function
                    initiateCallFn = httpsCallable(functions, 'initiateAirtelCall');
                } else {
                    console.error('Unsupported cloud telephony provider:', cloudTelephonyProvider);
                    setToastMessage('Unsupported cloud telephony provider. Please contact support.');
                    setShowToast(true);
                    // Clear calling state
                    setCallingLeads(prev => ({ ...prev, [lead.id]: false }));
                    return;
                }

                // Note: We no longer need to pass the uid parameter
                const result = await initiateCallFn({
                    to: cleanNumber,
                    leadId: lead.id
                });

                console.log('Call initiation result:', result.data);

                // Show success message
                setToastMessage('Call initiated successfully!');
                setShowToast(true);

                // Hide toast after 3 seconds
                setTimeout(() => {
                    setShowToast(false);
                }, 3000);

                // Clear calling state
                setCallingLeads(prev => ({ ...prev, [lead.id]: false }));
            } catch (functionError: any) {
                console.error('Detailed function error:', functionError);
                // Handle specific error codes from the function
                if (functionError.code === 'functions/cancelled') {
                    setToastMessage('Call was cancelled: ' + (functionError.message || 'Unknown error'));
                } else if (functionError.code === 'functions/unavailable') {
                    setToastMessage('Service is currently unavailable. Please try again later.');
                } else {
                    console.error(`Call failed: ${functionError.message || 'Unknown error'}`);
                    setToastMessage(`Failed to initiate call with ${lead.name}`);
                }
                setShowToast(true);

                // Clear calling state
                setCallingLeads(prev => ({ ...prev, [lead.id]: false }));
            }
        } catch (error) {
            console.error('Error initiating call:', error);
            setToastMessage(`Failed to initiate call with ${error instanceof Error ? error.message : 'Unknown error'}`);
            setShowToast(true);

            // Clear calling state
            setCallingLeads(prev => ({ ...prev, [lead.id]: false }));
        }
    };

    const getAssignedToExcel = () => {
        if (currentScreen === "staff" && currentUser?.uid) {
            return [currentUser.uid];
        }

        if (selectedAssigneeOptions.length > 0) {
            return selectedAssigneeOptions;
        }

        return undefined; // or return undefined if you want to skip the query constraint
    };

    const handleExcelExport = async (selectedColumns: string[]) => {
        setDownloadLoading(true);
        try {
            const assignedTo = getAssignedToExcel();
            const auth = getAuth();
            const excel = await exportLeadsToExcel(db, auth.currentUser, {
                pageSize: 10,
                sourceIds: selectedLeadSources,
                leadSubSources: selectedLeadSubSources,
                stateIds: selectedLeadStates,
                stageIds: selectedLeadStages,
                leadSubStages: selectedLeadSubStages,
                statusIds: selectedLeadStatuses,
                leadSubStatuses: selectedLeadSubStatuses,
                admissionStatusIds: selectedAdmissionStatuses,
                admissionSubStatus: selectedAdmissionSubStatus,
                welcomeCallStatusIds: selectedWelcomeCallByAdmissionTeamStatuses,
                visaProcessStatusIds: selectedVisaProcessingStatuses,
                applicationStatusIds: selectedApplicationMovedStatuses,
                conditionalOfferLetterStatusIds: selectedConditionalOfferLetterStatuses,
                unconditionalOfferLetterStatusIds: selectedUnConditionalOfferLetterStatuses,
                feePaymentStatusIds: selectedFeePaymentStatuses,
                fileSubmissionStatusIds: selectedFileSubmissionStatuses,
                subAgent: selectedSubAgentOptions,
                activeFollowUpIds: selectedFollowUpStatuses,
                assignedTo: assignedTo,
                campaignIds: selectedCampaignOptions,
                preferredStreamIds: selectedPrefferedStreams,
                superAgentId: selectedSuperAgents,
                destinationIds: selectedDestinations,
                updatedAtStartDate: selectedUpdatedAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtStartDate[0], true) : null,
                updatedAtEndDate: selectedUpdatedAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedUpdatedAtEndDate[0], false) : null,
                followUpAtStartDate: selectedFollowUpAtStartDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtStartDate[0], true) : null,
                followUpAtEndDate: selectedFollowUpAtEndDate.length > 0 ? convertToFirebaseTimestamp(selectedFollowUpAtEndDate[0], false) : null,
                startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
                endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
                intakeYear: selectedIntakeYear.length > 0 ? selectedIntakeYear : undefined,
                intakeMonth: selectedIntakeMonth.length > 0 ? selectedIntakeMonth : undefined,
                // if i select the sort by field in the export modal, it should override the current orderByField
                // and sortDirection
                orderByField: orderByField,
                sortDirection: sortDirection,
                // always order by updatedAt desc as the second criteria
                // orderByField: "updatedAt",
            }, selectedColumns);

            const url = window.URL.createObjectURL(new Blob([new Uint8Array(excel.buffer as ArrayBuffer)]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `Leads_${new Date().toISOString().slice(0, 10)}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            if (error instanceof Error && error.message === 'NO_LEADS_FOUND') {
                // This is expected behavior when no leads match the criteria
                setToastMessage("No leads found matching your criteria. Please adjust your filters and try again.");
                setIsError(true);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 4000);
            } else {
                // This is an actual error that should be logged
                console.error('Failed to export leads:', error);
                setToastMessage("Failed to export leads. Please try again.");
                setIsError(true);
                setShowToast(true);
                setTimeout(() => setShowToast(false), 4000);
            }
        } finally {
            setDownloadLoading(false);
            setShowExportModal(false);
        }
    };


    return (
        <div className="min-h-screen bg-gray-200">
            <div className="fixed top-16 left-0 h-full z-10">
                <SideNavbar />
            </div>

            <div className={`${isCollapsed ? 'ml-[70px]' : 'ml-[290px]'} mt-[109px] p-8 transition-all duration-300 w-[calc(100%-${isCollapsed ? '70px' : '290px'})]`}>
                <div className="bg-white p-6 rounded-3xl shadow-md">
                    <div className="flex items-center gap-6 mb-8">
                        <div className="flex items-center justify-center w-14 h-14 rounded-full" style={{ background: 'linear-gradient(180deg, #00A6D9 0%, #3483BE 100%)' }}>
                            <img src={`${process.env.PUBLIC_URL}/images/desktop/ph_student.svg`} alt="Lead Icon" />
                        </div>
                        <h2 className="text-2xl font-medium text-black"> Lead Management </h2>
                        <div className="ml-auto flex items-center gap-4">
                            <div ref={dropdownRef} className={`flex flex-col bg-white rounded-lg shadow-sm relative ${actionDropdownOpen ? '' : 'z-0'} w-[200px]`}>
                                <div className="relative">
                                    <div className="relative flex items-center text-black font-semibold rounded-full gap-2">
                                        <div
                                            className="w-full px-4 py-3 border border-gray-200 rounded-full flex items-center gap-2 text-xs 
                                            focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent  
                                            transition-all duration-200 font-Montserrat placeholder-gray-400 cursor-pointer"
                                            onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                                            onKeyDown={handleActionDropdownKeyDown}
                                            tabIndex={0}
                                            role="combobox"
                                            aria-expanded={actionDropdownOpen}
                                            aria-haspopup="listbox"
                                            aria-controls="action-listbox"
                                        >
                                            {selectedAction ? Actions.find((item) => item.id === selectedAction)?.name : "Select Bulk Action.."}
                                        </div>
                                        <div className="absolute right-1.5 flex items-center space-x-0.5 ">
                                            {selectedAction && (
                                                <button
                                                    onClick={handleClear}
                                                    className="p-0.5 hover:bg-gray-100 rounded-full transition-colors"
                                                    aria-label="Clear selection"
                                                >
                                                    <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                    </svg>
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className={`p-0.5 hover:bg-gray-100 rounded-full transition-colors ${actionDropdownOpen ? 'rotate-180' : ''}`}
                                                onClick={() => setActionDropdownOpen(!actionDropdownOpen)}
                                                aria-label={actionDropdownOpen ? "Close dropdown" : "Open dropdown"}
                                            >
                                                <svg
                                                    className={`w-4 h-4 text-gray-400 transform transition-transform duration-200`}
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                                {actionDropdownOpen && (
                                    <div
                                        className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-xl overflow-hidden max-h-48 min-w-[200px] w-full z-[9]"
                                        id="action-listbox"
                                        role="listbox"
                                    >
                                        <div className="p-2 sticky top-0 bg-white border-b border-gray-100 z-10">
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    placeholder="Search actions..."
                                                    className="w-full px-3 py-2 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
                                                    value={actionSearchQuery}
                                                    onChange={handleActionSearchInputChange}
                                                    onKeyDown={handleActionSearchInputKeyDown}
                                                    autoFocus
                                                    autoComplete="off"
                                                    spellCheck="false"
                                                    aria-label="Search actions"
                                                />
                                                {actionSearchQuery && (
                                                    <button
                                                        onClick={() => setActionSearchQuery('')}
                                                        className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded-full transition-colors"
                                                        aria-label="Clear search"
                                                    >
                                                        <svg className="w-3 h-3 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                                        </svg>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="overflow-y-auto max-h-36 p-1">
                                            {getFilteredActions().length > 0 ? (
                                                getFilteredActions()
                                                    .filter((item) => item?.view === true)
                                                    .map((item, index) => (
                                                        <div
                                                            id={`action-option-${index}`}
                                                            key={item?.id}
                                                            className={`flex items-center px-2 py-1.5 cursor-pointer rounded-md transition-colors ${highlightedActionIndex === index ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                                                            onClick={() => item?.active ? handleAction(item?.id) : null}
                                                            onMouseEnter={() => setHighlightedActionIndex(index)}
                                                            role="option"
                                                            aria-selected={selectedAction === item.id}
                                                            tabIndex={-1}
                                                        >
                                                            <span
                                                                className={`text-xs font-Montserrat truncate flex-1 ${!item?.active ? 'pointer-events-none opacity-50' : ''} ${highlightedActionIndex === index ? 'text-blue-600' : ''}`}
                                                                aria-disabled={!item?.active}
                                                            >
                                                                {item?.name}
                                                            </span>
                                                            {selectedAction === item.id && (
                                                                <svg className="w-4 h-4 text-blue-600 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                                                </svg>
                                                            )}
                                                        </div>
                                                    ))
                                            ) : (
                                                <div className="px-2 py-4 text-xs text-gray-500 text-center">
                                                    No results found
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {getActionPermission('Add') && (
                                <button onClick={openPopup} className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2"
                                    style={{ background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)', }}  >
                                    <span style={{ fontFamily: 'Montserrat', fontSize: '20px', fontWeight: '500', lineHeight: '24.38px', }}>    Add Lead    </span>
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/staffadd.svg`} alt="Add Staff Icon" className="w-4 h-4 ml-2" />
                                </button>
                            )}
                            {currentScreen === "Admin" && currentUser?.uid && (
                                <div className="ml-auto flex items-center gap-4">
                                    <button
                                        onClick={() => setShowExportModal(true)}
                                        disabled={downloadLoading}
                                        className="px-4 py-2 text-white font-semibold rounded-full flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                        style={{
                                            background: 'linear-gradient(180deg, #FFA782 0%, #FC583D 100%)',
                                            fontFamily: 'Montserrat',
                                            fontSize: '20px',
                                            fontWeight: 500,
                                            lineHeight: '24.38px',
                                        }}
                                    >
                                        Export
                                        <img
                                            src={`${process.env.PUBLIC_URL}/images/desktop/sidebaricon/report-svgrepo-com-white.svg`}
                                            alt="Export Icon"
                                            className="w-4 h-4"
                                        />
                                    </button>
                                </div>
                            )}

                            {showExportModal && (
                                <ExportModal
                                    onClose={() => setShowExportModal(false)}
                                    onExport={handleExcelExport}
                                    isLoading={downloadLoading}
                                />
                            )}
                        </div>
                    </div>

                    {/* Search inputs */}
                    <div className="flex items-center gap-4 mt-4">
                        <div className={`flex flex-col ${filterButtonActive ? 'pointer-events-none opacity-40' : ''}`}>
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <input
                                    type="text"
                                    placeholder="Search for Student..."
                                    className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                    value={searchQuery}
                                    onChange={(e) => handleSearchString(e)}
                                    onKeyPress={handleSearch}
                                />
                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="search-lens-icon"
                                    className="w-6 h-6 mr-2 cursor-pointer"
                                    onClick={(e) => handleSearch(e)}
                                />
                            </div>
                            {searchQuery && searchQuery.length < 3 && (
                                <span id='warning' className="text-xs text-red-500 font-normal mt-1 ml-2">Need atleast 3 characters</span>
                            )}
                        </div>
                        <div className="flex flex-col">
                            <div className="flex items-center w-80 rounded-3xl bg-zinc-100">
                                <input
                                    type="text"
                                    placeholder="Search by Number..."
                                    className="w-full px-4 py-2 text-gray-700 bg-zinc-100 focus:outline-none rounded-3xl"
                                    value={phoneSearchQuery}
                                    onChange={(e) => handleSearchPhone(e)}
                                    onFocus={handlePhoneFocusSearch}
                                    onBlur={handlePhoneBlurSearch}   // blur handle
                                    onKeyPress={handleSearch}
                                />

                                <img
                                    src={`${process.env.PUBLIC_URL}/images/desktop/searchbg.svg`}
                                    alt="search-lens-icon"
                                    className="w-6 h-6 mr-2 cursor-pointer"
                                    onClick={(e) => handleSearch(e)}
                                />
                            </div>
                            {phoneSearchQuery && phoneSearchQuery.length < 3 && (
                                <span id='warning' className="text-xs text-red-500 font-normal mt-1 ml-2">Need atleast 3 characters</span>
                            )}
                        </div>
                        {(searchQuery || phoneSearchQuery) && (
                            <a
                                href="#"
                                className="text-xs text-blue-500 font-normal"
                                onClick={(e) => {
                                    e.preventDefault();
                                    clearSearch(e);
                                }}
                            >
                                Clear Search
                            </a>
                        )}
                    </div>

                    {/* collapsible filter settings */}
                    <div className={`mt-4 mb-4 border border-gray-200 rounded-lg relative z-[5] p-4 ${(searchQuery || phoneSearchQuery) && 'pointer-events-none opacity-40'}`}>
                        <div
                            className="flex items-center cursor-pointer justify-between"
                            onClick={() => setIsCollapsedFilter(!isCollapsedFilter)}
                        >
                            <span className={`flex flex-col mr-2 font-semibold ${shouldShowAssigneeRedIndicators() ? 'text-red-500' : ''}`}>Filters</span>

                            {searchQuery || phoneSearchQuery ? (
                                <h3>Filter not allowed while search</h3>
                            ) : null}
                            <span className="flex items-center gap-2">
                                {getTotalFilters() > 0 && (
                                    <a
                                        href="#"
                                        className="text-xs text-blue-500 font-normal mr-2"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            clearAllFilters(e);
                                        }}
                                    >
                                        Clear All Filters
                                    </a>
                                )}

                                <span className={`text-xs font-normal mr-6 ${shouldShowAssigneeRedIndicators() ? 'text-red-500' : 'text-gray-500'}`}>
                                    {getTotalFilters()} Filters Applied and {filterCombinationCount} Filters Combinations Applied
                                </span>

                                {showFilterWarning && (
                                    <span className="text-xs font-normal mr-6 text-orange-600 bg-orange-50 px-2 py-1 rounded border border-orange-200">
                                        ⚠️ {filterCombinationCount} filter combinations (exceeds recommended limit of 30)
                                    </span>
                                )}

                                <span
                                    className="text-sm transition-transform duration-300 ease-in-out"
                                    style={{ transform: isCollapsedFilter ? 'rotate(0deg)' : 'rotate(180deg)' }}
                                >
                                    ▲
                                </span>
                            </span>
                        </div>

                        <div
                            className={`transition-all duration-300 ease-in-out transform origin-top ${isCollapsedFilter
                                ? 'max-h-[1000px] opacity-100 scale-y-100'
                                : 'overflow-hidden max-h-0 opacity-0 scale-y-95'
                                }`}
                        >
                            <div className="pt-4">
                                <SettingsBoxesLayout
                                    selectedFilters={selectedFilters}
                                    currentScreen={currentScreen}
                                    leadSources={leadSources}
                                    selectedLeadSources={selectedLeadSources}
                                    leadSubSources={leadSubSources}
                                    selectedLeadSubSources={selectedLeadSubSources}
                                    leadStages={leadStages}
                                    selectedLeadStages={selectedLeadStages}
                                    leadSubStages={leadSubStages}
                                    selectedLeadSubStages={selectedLeadSubStages}
                                    leadStates={leadStates}
                                    selectedLeadStates={selectedLeadStates}
                                    leadStatuses={leadStatuses}
                                    selectedLeadStatuses={selectedLeadStatuses}
                                    leadSubStatuses={leadSubStatuses}
                                    selectedLeadSubStatuses={selectedLeadSubStatuses}
                                    admissionStatuses={admissionStatuses}
                                    selectedAdmissionStatuses={selectedAdmissionStatuses}
                                    admissionSubStatus={admissionSubStatus}
                                    selectedAdmissionSubStatus={selectedAdmissionSubStatus}
                                    welcomeCallByAdmissionTeamStatuses={welcomeCallByAdmissionTeamStatuses}
                                    selectedWelcomeCallByAdmissionTeamStatuses={selectedWelcomeCallByAdmissionTeamStatuses}
                                    visaProcessingStatuses={visaProcessingStatuses}
                                    selectedVisaProcessingStatuses={selectedVisaProcessingStatuses}
                                    applicationMovedStatuses={applicationMovedStatuses}
                                    selectedApplicationMovedStatuses={selectedApplicationMovedStatuses}
                                    conditionalofferLetterStatuses={conditionalofferLetterStatuses}
                                    selectedConditionalOfferLetterStatuses={selectedConditionalOfferLetterStatuses}
                                    unConditionalofferLetterStatuses={unConditionalofferLetterStatuses}
                                    selectedUnConditionalOfferLetterStatuses={selectedUnConditionalOfferLetterStatuses}
                                    feePaymentStatuses={feePaymentStatuses}
                                    selectedFeePaymentStatuses={selectedFeePaymentStatuses}
                                    fileSubmissionStatuses={fileSubmissionStatuses}
                                    selectedFileSubmissionStatuses={selectedFileSubmissionStatuses}
                                    departments={departments}
                                    selectedDepartments={selectedDepartments}
                                    assigneeOptions={assigneeOptions}
                                    selectedAssigneeOptions={selectedAssigneeOptions}
                                    subAgentOptions={subAgentOptions}
                                    selectedSubAgentOptions={selectedSubAgentOptions}
                                    superAgents={superAgents}
                                    selectedSuperAgents={selectedSuperAgents}
                                    followUpStatuses={followUpStatuses}
                                    selectedFollowUpStatuses={selectedFollowUpStatuses}
                                    campaignOptions={campaignOptions}
                                    selectedCampaignOptions={selectedCampaignOptions}
                                    prefferedStreams={prefferedStreams}
                                    selectedPrefferedStreams={selectedPrefferedStreams}
                                    destinations={destinations}
                                    selectedDestinations={selectedDestinations}
                                    selectedIntakeMonth={selectedIntakeMonth}
                                    selectedIntakeYear={selectedIntakeYear}
                                    selectedStartDate={selectedStartDate}
                                    selectedEndDate={selectedEndDate}
                                    selectedUpdatedAtStartDate={selectedUpdatedAtStartDate}
                                    selectedUpdatedAtEndDate={selectedUpdatedAtEndDate}
                                    selectedFollowUpAtStartDate={selectedFollowUpAtStartDate}
                                    selectedFollowUpAtEndDate={selectedFollowUpAtEndDate}
                                    RenderSettingsBox={RenderSettingsBox}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Filter Combination Warning Banner */}
                    {showFilterWarning && (
                        <div className="mt-4 mb-4 border border-orange-300 bg-orange-50 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <span className="text-orange-600 text-lg">⚠️</span>
                                <div className="flex-1">
                                    <h4 className="font-semibold text-orange-800 mb-1">High Filter Complexity Detected</h4>
                                    <p className="text-sm text-orange-700 mb-2">
                                        Your current filter selection creates <strong>{filterCombinationCount} combinations</strong>, which exceeds the recommended limit of 30.
                                    </p>
                                </div>
                                <button
                                    onClick={() => setShowFilterWarning(false)}
                                    className="text-orange-500 hover:text-orange-700 text-lg font-bold leading-none"
                                >
                                    ×
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filter Buttons */}
                    {filterButtons.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-2 mb-2">
                            {filterButtons.map((button) => (
                                <button
                                    key={button.id}
                                    className={`w-[10%] h-10 px-3 py-1 text-xs font-semibold rounded transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-md active:shadow-sm ${filterButtonKey === button.id ? 'ring-2 ring-blue-400 ring-opacity-75' : ''
                                        }`}
                                    style={button.color ? { backgroundColor: button.color } : { backgroundColor: '#E0E7FF' }}
                                    onClick={() => handleFilterButtonClick(button.id, button.filterButton)}
                                >
                                    <span className="flex items-center justify-center gap-1">
                                        {filterButtonKey === button.id && (
                                            <span className="w-2 h-2 bg-white rounded-full opacity-80"></span>
                                        )}
                                        {button.name}
                                    </span>
                                </button>
                            ))}
                            {filterButtonKey && (
                                <button
                                    className={`w-[10%] h-10 px-3 py-1 text-xs font-semibold rounded transition-all duration-200 transform hover:scale-105 active:scale-95 hover:shadow-md active:shadow-sm hover:bg-red-200`}
                                    style={{ backgroundColor: '#E0E7FF' }}
                                    onClick={() => clearFillterButton()}
                                >
                                    <span className="flex items-center justify-center gap-1">
                                        <span className="text-red-500">✕</span>
                                        Clear Filters
                                    </span>
                                </button>
                            )}
                        </div>
                    )}

                    {/* Pagination Control - Top */}
                    <div className="flex items-center justify-between mt-6 mb-4">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <label className="text-xs text-gray-500 mr-2" >Rows per page:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                            <div className="ml-2 relative">
                                <button
                                    className="text-white text-xs font-semibold flex items-center gap-2 bg-[#3483BE] p-1 rounded"
                                    onClick={() => setCustomizeOpen(!customizeOpen)}
                                >
                                    <img src={`${process.env.PUBLIC_URL}/images/desktop/customize.svg`} alt="Add Staff Icon" className="w-5 h-5" />
                                </button>
                                {customizeOpen && (
                                    <div className="absolute top-full mt-1 right-0 bg-white shadow-lg rounded-lg p-4 z-50">
                                        <div className="flex flex-col gap-2">
                                            {Object.keys(customTableProps).map((key) => (
                                                <div key={key} className="flex items-center justify-between">
                                                    <label className="text-xs font-semibold text-gray-500 ml-2 whitespace-nowrap">{key}</label>
                                                    <button
                                                        className={`ml-2 text-xs p-1 rounded ${leadManagementDashboardProps[customTableProps[key as keyof typeof customTableProps]] ? 'bg-green-100' : 'bg-pink-100'}`}
                                                        onClick={() => {
                                                            dispatch(setDashboardProps({ key: customTableProps[key as keyof typeof customTableProps], value: !leadManagementDashboardProps[customTableProps[key as keyof typeof customTableProps]] }));
                                                        }}
                                                    >
                                                        {leadManagementDashboardProps[customTableProps[key as keyof typeof customTableProps]] ? 'Hide' : 'Show'}
                                                    </button>
                                                </div>
                                            ))}
                                            <button className="text-xs text-red-500 font-semibold mt-2"
                                                onClick={() => {
                                                    dispatch(reset());
                                                    setCustomizeOpen(false);
                                                }}
                                            >
                                                Reset to Default
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Add the sort dropdown */}
                            <div className="flex flex-col">
                                <select
                                    value={orderByField}
                                    onChange={(e) => {
                                        const newOrderBy = e.target.value as 'createdAt' | 'updatedAt' | 'transferredAt';
                                        // Use a callback to ensure both state updates happen together
                                        setOrderByField(newOrderBy);
                                        setCurrentPage(1);
                                        // Store the orderByField in sessionStorage if needed
                                        sessionStorage.setItem('orderByField', newOrderBy);
                                    }}
                                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="createdAt">Created Date</option>
                                    <option value="updatedAt">Updated Date</option>
                                    <option value="transferredAt">Transferred Date</option>
                                </select>
                            </div>
                            {/* add the select color from the lead stage, lead status, lead state */}
                            <div className="flex flex-col">
                                <select
                                    value={leadColorScheme || ''}
                                    onChange={(e) => {
                                        const newColorScheme = e.target.value as 'stage' | 'status' | 'state' | '';
                                        setLeadColorScheme(newColorScheme || null);
                                    }}
                                    className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value="">None</option>
                                    <option value="stage">Lead Stage</option>
                                    <option value="status">Lead Status</option>
                                    <option value="state">Lead State</option>
                                </select>
                            </div>
                        </div>
                        {/* Navigation buttons (centered) */}
                        <div className="flex-1 flex items-right justify-end gap-2 pr-96">
                            {/* first */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;&lt;
                            </button>

                            {/* prev */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;
                            </button>

                            {/* page number */}
                            <div className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff]">
                                {currentPage}
                            </div>

                            {/* next */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={loading || !hasMore || (currentPage === Math.ceil(totalLeadsCount / pageSize))}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &gt;
                            </button>
                        </div>

                        {/* Page count (right-aligned) */}
                        <div className="flex-1 text-right">
                            <div className="text-xs text-gray-500">
                                {loading ? 'Loading...' : `(Total: ${totalLeadsCount} Leads | New: ${isLoadingNewCount ? '...' : newLeadsCount} Leads) ${currentPage} / ${totalPages} pages`}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-xl overflow-hidden shadow-lg">
                        <div 
                            className={`overflow-x-auto relative ${pageSize > 10 ? 'overflow-y-auto lead-table-container' : ''}`}
                            style={pageSize > 10 ? {
                                scrollbarWidth: 'thin',
                                scrollbarColor: '#3483BE #f1f5f9',
                                height: '600px', // Height to show header + 10 full rows
                                minHeight: '600px'
                            } : {}}
                        >
                            {loading ? (
                                <div className={pageSize > 10 ? 'h-[600px] overflow-hidden' : ''}>
                                    <TableSkeleton columns={10} rows={pageSize > 10 ? 10 : pageSize} />
                                </div>
                            ) : (
                                <table className="min-w-full table-auto border-collapse">
                                    <thead className={pageSize > 10 ? 'sticky top-0' : ''}>
                                        <tr className="bg-gradient-to-r from-[#02385E] to-[#02385E]/90">
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                <input
                                                    type="checkbox"
                                                    className="form-checkbox h-3.5 w-3.5 accent-cyan-600 rounded"
                                                    checked={selectCurrentPage}
                                                    onChange={() => handleSelectAll()}
                                                />
                                            </th>
                                            <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                Selected ({selectedLeadsForAction.length})
                                            </th>
                                            {leadManagementDashboardProps.name && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Name
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.phone && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Phone
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.createdAt && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Created At
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.updatedAt && (
                                                <th
                                                    className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat cursor-pointer hover:bg-white/10 select-none transition-colors duration-200"
                                                    onClick={handleUpdatedAtSort}
                                                    title="Click to sort by Updated At"
                                                >
                                                    <div className="flex items-center justify-between">
                                                        <span>Updated At</span>
                                                        {renderSortIcon()}
                                                    </div>
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.source && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Source
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.campaign && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Campaign
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.assignedTo && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Assigned To
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.destinations && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Destinations
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.subAgent && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Sub Agent
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.admissionStatus && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Admission Status
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.intakeMonth && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Intake Month
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.intakeYear && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Intake Year
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.stage && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Stage
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.priority && (
                                                <th className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] font-Montserrat">
                                                    Status
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.followUp && (
                                                <th className="px-4 py-3 text-sm font-semibold text-white tracking-wider whitespace-nowrap border-r border-[#ffffff20]">
                                                    Follow Up
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.lastRemark && (
                                                <th className="px-4 py-3 text-sm font-semibold text-white tracking-wider whitespace-nowrap border-r border-[#ffffff20]">
                                                    Last Remark
                                                </th>
                                            )}
                                            {leadManagementDashboardProps.actions && (
                                                <th className="px-4 py-3 text-sm font-medium text-white uppercase tracking-wider border-r border-[#ffffff20] min-w-[200px]">
                                                    Actions
                                                </th>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {loading ? (
                                            Array(10).fill(0).map((_, index) => (
                                                <tr key={index} className={`transition-colors duration-150 ease-in-out ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50'}`}>
                                                    {Array(9).fill(0).map((_, colIndex) => (
                                                        <td key={colIndex} className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100">
                                                            <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            leadsData.map((lead, index) => (
                                                <tr key={lead.id} style={{ color: lead.isNewEntry ? 'black' : 'inherit' }}
                                                    className={`
                                            transition-colors duration-150 ease-in-out
                                            ${index % 2 === 0 ? 'bg-white hover:bg-blue-50' : 'bg-slate-50 hover:bg-blue-50'}
                                          `}
                                                >
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                        <div className="flex items-center">
                                                            <input
                                                                type="checkbox"
                                                                checked={selectedLeadsForAction.includes(lead.id)}
                                                                onChange={() => { handleLeadSelection(lead.id) }}
                                                                className="form-checkbox h-3.5 w-3.5 accent-cyan-600 rounded"
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">{(currentPage - 1) * pageSize + index + 1}</td>
                                                    {leadManagementDashboardProps.name && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat cursor-pointer"
                                                            onClick={() => handleStudentClick(lead.id)}
                                                        >
                                                            <span 
                                                                className={`inline-block px-2 py-1 rounded-full ${
                                                                    getLeadNameColor(lead) 
                                                                        ? 'border-2' 
                                                                        : ''
                                                                }`}
                                                                style={{
                                                                    borderColor: getLeadNameColor(lead) || 'transparent',
                                                                    backgroundColor: getLeadNameColor(lead) ? `${getLeadNameColor(lead)}20` : 'transparent'
                                                                }}
                                                            >
                                                                {lead.name}
                                                            </span>
                                                            <span className={`text-xs ml-2 py-1 px-2 rounded 
                                                            ${getStatusLabel(lead.createdAt, lead.updatedAt, lead.transferredAt) === "New" ? 'bg-[#E6F7FF] text-[#54be34]'
                                                                    : getStatusLabel(lead.createdAt, lead.updatedAt, lead.transferredAt) === "Re Enquiry" ? 'bg-[#FFF8E6] text-[#777873]'
                                                                        : getStatusLabel(lead.createdAt, lead.updatedAt, lead.transferredAt) === "Transferred" ? 'bg-[#FDE6E6] text-[#aa3727]'
                                                                            : 'text-gray-500'}`}>
                                                                {getStatusLabel(lead.createdAt, lead.updatedAt, lead.transferredAt)}
                                                            </span>
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.phone && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {lead.number}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.createdAt && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {lead.createdAt
                                                                ? new Date(convertToDate(lead.createdAt)).toLocaleDateString("en-US", {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                    year: "numeric",
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })
                                                                : ""}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.updatedAt && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {lead.createdAt
                                                                ? new Date(convertToDate(lead.updatedAt)).toLocaleDateString("en-US", {
                                                                    month: "short",
                                                                    day: "numeric",
                                                                    year: "numeric",
                                                                    hour: "2-digit",
                                                                    minute: "2-digit",
                                                                })
                                                                : ""}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.source && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {sources.find((src) => src.id === lead.sourceId)?.source ||
                                                                (lead.isNewEntry ? "--" :
                                                                    (lead.sourceId ? "--" : "--"))}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.campaign && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {campaignOptions.find((camp) => camp.id === lead.campaignId)?.data?.campaign || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.assignedTo && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {assigneeOptions.find((assigneeOption) => assigneeOption.id === lead.assignedTo)?.data?.name || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.destinations && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {destinations.find((destination) => destination.id === lead.destinationId)?.data?.name || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.subAgent && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {subAgentOptions.find((subAgentOptionsOption) => subAgentOptionsOption.id === lead.subAgentId)?.data?.name || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.admissionStatus && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {admissionStatuses.find((admissionStatus) => admissionStatus.id === lead.admissionStatusId)?.data.admissionStatus || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.intakeMonth && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {lead.intakeMonth || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.intakeYear && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {lead.intakeYear || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.stage && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            {stages.find((stage) => stage.id === lead.stageId)?.stage || "--"}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.priority && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100 text-neutral-600 text-left font-Montserrat">
                                                            <div className="flex items-center gap-2">
                                                                <span className={`w-3 h-3 rounded-full ${getStatusCircleColor(statuses.find((status) => status.id === lead.statusId)?.status || "--")}`}></span>
                                                                {statuses.find((status) => status.id === lead.statusId)?.status || "--"}
                                                            </div>
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.followUp && (
                                                        <td className="p-2 text-center whitespace-nowrap border-r border-gray-100">
                                                            {lead.hasActiveFollowUp ? (
                                                                <span
                                                                    className="text-xs font-semibold text-[#3483BE] cursor-pointer"
                                                                    onClick={() => handleFetchFollowUp(lead.id, lead.activeFollowUpId)}
                                                                >
                                                                    {currentViewingFollowUpId === lead.id && followUpDetails[lead.id] ? (
                                                                        new Date(convertToDate(followUpDetails[lead.id].followUpTime)).toLocaleDateString("en-US", {
                                                                            month: "short",
                                                                            day: "numeric",
                                                                            year: "numeric",
                                                                            hour: "2-digit",
                                                                            minute: "2-digit",
                                                                        })
                                                                    ) : "Yes"}
                                                                </span>
                                                            ) : (
                                                                <span className="text-xs font-bold text-gray-500">No</span>
                                                            )}
                                                        </td>
                                                    )}
                                                    {leadManagementDashboardProps.lastRemark && (
                                                        <td className="px-4 py-2.5 border-r border-gray-100 text-neutral-600 text-left font-Montserrat max-w-[200px]">
                                                            <div className="truncate" title={lead.lastRemark || ""}>
                                                                {lead.lastRemark ? (
                                                                    <span className="text-sm text-gray-700">
                                                                        {lead.lastRemark}
                                                                    </span>
                                                                ) : (
                                                                    <span className="text-xs text-gray-400 italic">No remarks</span>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}

                                                    {leadManagementDashboardProps.actions && (
                                                        <td className="px-4 py-2.5 whitespace-nowrap border-r border-gray-100">
                                                            <div className="flex items-center justify-center gap-3 min-w-[200px]">
                                                                {!getActionPermission('Delete') && !getActionPermission('Edit') && !getActionPermission('Details') && !getActionPermission('Collaborate') && !getActionPermission('Transfer') && !getActionPermission('Follow Up') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-3 left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            You are not authorized to perform any <br /> actions. Contact admin for details.
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/info-circle.svg`}
                                                                            alt="More Icon"
                                                                            className="w-5 h-5 cursor-pointer"
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Delete') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            {lead.isActive ? "Mark Lead as Closed" : "Reactivate Lead"}
                                                                        </span>
                                                                        <img
                                                                            src={lead.isActive ? `${process.env.PUBLIC_URL}/images/desktop/deleteicon.svg` : `${process.env.PUBLIC_URL}/images/desktop/reseticon.svg`}
                                                                            alt={lead.isActive ? "Delete Icon" : "Reset Icon"}
                                                                            className="w-5 h-5 cursor-pointer"
                                                                            onClick={() => handleToggleActive(lead.id, lead.isActive)}
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Edit') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            Edit Lead
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/editicon.svg`}
                                                                            alt="Edit Icon"
                                                                            onClick={() => handleEditLead(lead.id)}
                                                                            className="w-5 h-5 cursor-pointer"
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Details') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            View Details
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/print-preview.svg`}
                                                                            alt="preview-icon"
                                                                            className="w-5 h-5 cursor-pointer"
                                                                            onClick={() => handleStudentClick(lead.id)}
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Collaborate') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            Collaborate
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/collaborate.svg`}
                                                                            alt="collaborate-icon"
                                                                            className="w-5 h-5 cursor-pointer"
                                                                            onClick={() => handleCollaborateClick(lead)}
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Transfer') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            Transfer Lead
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/TransferLead.svg`}
                                                                            alt="transfer-lead-icon"
                                                                            className="w-5 h-5 cursor-pointer"
                                                                            onClick={() => handleTransferClick(lead)}
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Follow Up') && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            Follow Up
                                                                        </span>
                                                                        <img
                                                                            src={`${process.env.PUBLIC_URL}/images/desktop/follow-up-leadMngt.svg`}
                                                                            alt="transfer-lead-icon"
                                                                            className="w-5 h-5 cursor-pointer"
                                                                            onClick={() => handleFollowUpClick(lead)}
                                                                        />
                                                                    </button>
                                                                )}
                                                                {getActionPermission('Cloud Telephony') && companyCloudTelephony && (
                                                                    <button className="p-1 hover:bg-gray-100 rounded relative group">
                                                                        <span className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black bg-opacity-80 text-white text-xs rounded px-2 py-1 mb-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200 whitespace-nowrap">
                                                                            Call
                                                                        </span>
                                                                        {callingLeads[lead.id] ? (
                                                                            <div className="w-5 h-5 flex items-center justify-center">
                                                                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                                                            </div>
                                                                        ) : (
                                                                            <img
                                                                                src={`${process.env.PUBLIC_URL}/images/desktop/cloudTelephony.svg`}
                                                                                alt="transfer-lead-icon"
                                                                                className="w-5 h-5 cursor-pointer"
                                                                                onClick={() => handleCall(lead)}
                                                                            />
                                                                        )}
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    )}
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                    {/*  */}
                    {/* Pagination Control - Bottom */}
                    <div className="flex items-center justify-between mt-6">
                        <div className="flex items-center gap-4">
                            <div className="flex items-center">
                                <label className="text-xs text-gray-500 mr-2">Rows per page:</label>
                                <select
                                    value={pageSize}
                                    onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                                    className="border border-gray-200 rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-blue-500"
                                >
                                    <option value={10}>10</option>
                                    <option value={25}>25</option>
                                    <option value={50}>50</option>
                                    <option value={100}>100</option>
                                    <option value={500}>500</option>
                                </select>
                            </div>
                            <div className="mr-8">

                            </div>
                        </div>
                        {/* Navigation buttons (centered) */}
                        <div className="flex-1 flex items-right justify-end gap-2">
                            {/* first */}
                            <button
                                onClick={() => handlePageChange(1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;&lt;
                            </button>

                            {/* prev */}
                            <button
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={loading || currentPage === 1}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &lt;
                            </button>

                            {/* page number */}
                            <div className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff]">
                                {currentPage}
                            </div>

                            {/* next */}
                            <button
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={loading || !hasMore || (currentPage === Math.ceil(totalLeadsCount / pageSize))}
                                className="px-2 py-1 border border-[#609ac7] rounded-md text-sm bg-white text-[#32a6ff] hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                &gt;
                            </button>
                        </div>

                        {/* Page count (right-aligned) */}
                        <div className="flex-1 text-right">
                            <div className="text-xs text-gray-500">
                                {loading ? 'Loading...' : `(Total: ${totalLeadsCount} Leads | New: ${isLoadingNewCount ? '...' : newLeadsCount} Leads) ${currentPage} / ${totalPages} pages`}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Add and Update Modal */}
            <AddUpdateLeadModal
                isOpen={isPopupOpen}
                onClose={() => {
                    setPopupOpen(false);
                    setNewLead({});
                    setEditingId(null);
                    setPhoneValidationMessage('');
                    setIsPhoneValidating(false);
                    setPhoneValidationType('');
                }}
                editingId={editingId}
                leadData={newLead}
                assignedToOptions={assignedToOptions}
                sources={sources}
                subSources={subSources}
                campaigns={campaigns}
                pipelines={pipelines}
                currentUserPipelines={currentUserPipelines}
                isAdmin={isAdmin}
                isManager={isManager}
                isStaff={isStaff}
                onSuccess={(message) => {
                    setToastMessage(message);
                    setShowToast(true);
                    if (message.includes('Error') || message.includes('Already Exists') || message.includes('required') || message.includes('valid')) {
                        setIsError(true);
                    } else {
                        setIsError(false);
                    }
                    setTimeout(() => {
                        setShowToast(false);
                    }, 3000);
                }}
                onLeadAdded={async (lead) => {
                    setLeadsData((prevLeads) => [lead, ...prevLeads]);
                    setTotalLeadsCount((prevCount) => prevCount + 1);
                    
                    // Check if the new lead is a "New" lead and update count accordingly
                    if (lead.createdAt && lead.updatedAt) {
                        const createdTime = lead.createdAt.toDate().getTime();
                        const updatedTime = lead.updatedAt.toDate().getTime();
                        const transferredTime = lead.transferredAt ? lead.transferredAt.toDate().getTime() : 0;
                        const timeDiffCreatedUpdated = Math.abs(updatedTime - createdTime);
                        const timeDiffCreatedTransferred = lead.transferredAt ? Math.abs(transferredTime - createdTime) : 0;
                        const timeDiffUpdatedTransferred = lead.transferredAt ? Math.abs(transferredTime - updatedTime) : 0;
                        
                        if (timeDiffCreatedUpdated < 2000 &&
                            (!lead.transferredAt || (timeDiffCreatedTransferred < 2000 && timeDiffUpdatedTransferred < 2000))) {
                            setNewLeadsCount((prevCount) => prevCount + 1);
                        }
                    }
                }}
                onLeadUpdated={async (lead) => {
                    const updatedLeads = leadsData.map((prevLead) =>
                        prevLead.id === editingId ? {
                            ...prevLead,
                            ...lead
                        } : prevLead
                    );
                    setLeadsData(updatedLeads);
                    
                    // For lead updates, we should refresh the new count since the update might change a lead's status
                    // But only do this if we're editing a lead that could potentially be "new"
                    setIsLoadingNewCount(true);
                    try {
                        const totalNewCount = await fetchTotalNewLeadsCount();
                        setNewLeadsCount(totalNewCount);
                    } catch (error) {
                        console.error('Error fetching new leads count after update:', error);
                    } finally {
                        setIsLoadingNewCount(false);
                    }
                }}
            />

            {/* Bulk Action Modal */}
            {
                selectedAction && (
                    <ActionModals
                        action={selectedAction ? Actions.find((item) => item.id === selectedAction)?.name ?? "'" : "'"}
                        // selectAll={false} /* Not using global select all anymore */
                        count={selectedLeadsForAction.length}
                        selectedIds={selectedLeadsForAction}
                        // neglectedIds={[]}
                        // filterQuery={{
                        //     sourceId: selectedLeadSources[0],
                        //     stateId: selectedLeadStates[0],
                        //     stageId: selectedLeadStages[0],
                        //     statusId: selectedLeadStatuses[0],
                        //     admissionStatusId: selectedAdmissionStatuses[0],
                        //     welcomeCallByAdmissionTeamStatusId: selectedWelcomeCallByAdmissionTeamStatuses[0],
                        //     visaFileProcessingStatusId: selectedVisaProcessingStatuses[0],
                        //     applicationMovedStatusId: selectedApplicationMovedStatuses[0],
                        //     conditionalOfferLetterStatusId: selectedConditionalOfferLetterStatuses[0],
                        //     unconditionalOfferLetterStatusId: selectedUnConditionalOfferLetterStatuses[0],
                        //     feePaymentStatusId: selectedFeePaymentStatuses[0],
                        //     fileSubmissionStatusId: selectedFileSubmissionStatuses[0],
                        //     hasActiveFollowUp: selectedFollowUpStatuses[0] === 'true' ? true : selectedFollowUpStatuses[0] === 'false' ? false : null,
                        //     assignedTo: getAssignedToValue(),
                        //     campaignId: selectedCampaignOptions[0],
                        //     startDate: selectedStartDate.length > 0 ? convertToFirebaseTimestamp(selectedStartDate[0], true) : null,
                        //     endDate: selectedEndDate.length > 0 ? convertToFirebaseTimestamp(selectedEndDate[0], false) : null,
                        //     nameSearchQuery: searchQuery !== '' ? searchQuery : null,
                        //     phoneSearchQuery: phoneSearchQuery !== '' ? phoneSearchQuery : null,
                        // }}
                        onClose={(message) => handleOnClose(message)}
                    />
                )
            }

            {/* Collaborator Modal */}
            <CollaborateModal
                isOpen={showCollaborateModal}
                onClose={() => {
                    setShowCollaborateModal(false);
                    setSelectedStudent(null);
                    setSelectedStudentPhone('');
                }}
                selectedStudent={selectedStudent}
                selectedStudentPhone={selectedStudentPhone}
                onSuccess={(message) => {
                    setToastMessage(message);
                    setShowToast(true);
                    setIsError(false);
                }}
            />

            {/* Transfer Modal */}
            <TransferModal
                isOpen={showTransferModal}
                onClose={() => {
                    setShowTransferModal(false);
                    setSelectedStudent(null);
                    setSelectedStudentPhone('');
                    setCurrentAssignedUser(null);
                }}
                selectedStudent={selectedStudent}
                selectedStudentPhone={selectedStudentPhone}
                currentAssignedUser={currentAssignedUser}
                onSuccess={(message) => {
                    setToastMessage(message);
                    setShowToast(true);
                    setIsError(false);
                }}
            />

            {/* Deactivate Confirmation Modal */}
            {showDeactivateModal && leadToDeactivate && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 shadow-xl">
                        <div className="flex items-center mb-4">
                            <div className="flex-shrink-0 w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                                <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                            <div className="ml-4">
                                <h3 className="text-lg font-medium text-gray-900">
                                    Mark Lead as Closed
                                </h3>
                            </div>
                        </div>

                        <div className="mb-6">
                            <p className="text-sm text-gray-600">
                                Are you sure you want to mark this lead as closed for <strong>{leadToDeactivate.name}</strong>?
                            </p>
                        </div>

                        <div className="flex items-center justify-end space-x-3">
                            <button
                                type="button"
                                onClick={() => {
                                    setShowDeactivateModal(false);
                                    setLeadToDeactivate(null);
                                }}
                                disabled={isDeactivating}
                                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => toggleLeadStatusConfirmed(leadToDeactivate.id, leadToDeactivate.isActive)}
                                disabled={isDeactivating}
                                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                            >
                                {isDeactivating ? (
                                    <>
                                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                                        Marking as Closed...
                                    </>
                                ) : (
                                    'Deactivate'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Follow up modal */}
            {showFollowUpModal && (
                <FollowUpModal
                    isOpen={showFollowUpModal}
                    onClose={() => {
                        setShowFollowUpModal(false);
                        setSelectedStudent(null);
                        setSelectedStudentFollowUp({ id: '', name: '', phone: '' });
                        setSelectedFollowUp(null);
                        // Refresh data when modal closes
                        fetchPageData(currentPage);
                    }}
                    selectedStudent={selectedStudentFollowUp}
                />
            )}

            {showToast && (
                <Toast
                    message={toastMessage}
                    isError={
                        toastMessage.includes('Error') ||
                        toastMessage.includes('Cannot') ||
                        toastMessage.includes('Failed')
                    }
                    show={showToast}
                    onClose={() => setShowToast(false)}
                />
            )}
        </div >
    );
};

// (Removed duplicate convertToFirebaseTimestamp utility as it has been moved to the top of the file)

const convertToFirebaseTimestamp = (
    date: string | Date,
    isStartDate: boolean = true
): Timestamp => {
    const dateObj = new Date(date);

    if (isStartDate) {
        // Set to start of day (00:00:00) in UTC+5:30
        dateObj.setHours(0, 0, 0, 0);
    } else {
        // Set to end of day (23:59:59.999) in UTC+5:30
        dateObj.setHours(23, 59, 59, 999);
    }

    console.log('dateObj', Timestamp.fromDate(dateObj));


    // Convert to Firebase Timestamp
    return Timestamp.fromDate(dateObj);
};

// function to convert Timestamp to Date
const convertToDateString = (timestamp: Timestamp | Date): string => {
    if (timestamp instanceof Timestamp) {
        const date = timestamp.toDate();
        date.setMinutes(date.getMinutes() + 330); // Add 5 hours and 30 minutes (330 minutes)
        return date.toISOString().slice(0, 16); // Returns in 'YYYY-MM-DDTHH:mm' format
    } else {
        throw new Error("Invalid timestamp type");
    }
}

export default LeadManagement;



function isValidEmail(email: string) {
    throw new Error("Function not implemented.");
}
// function convertToFirebaseTimestamp(followUpTime: string, p0: boolean): any {
//     throw new Error("Function not implemented.");
// }




